import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import enMessages from '../../locales/en.json';
import teMessages from '../../locales/te.json';
import hiMessages from '../../locales/hi.json';
import enCommon from '../../locales/en/common.json';
import enHome from '../../locales/en/home.json';
import enProducts from '../../locales/en/products.json';
import teCommon from '../../locales/te/common.json';
import teHome from '../../locales/te/home.json';
import teProducts from '../../locales/te/products.json';
import hiCommon from '../../locales/hi/common.json';
import hiHome from '../../locales/hi/home.json';
import hiProducts from '../../locales/hi/products.json';
import productCatalogTranslations from '../i18n/productCatalogTranslations';

const STORAGE_KEY = 'Agro_language';

export const languages = [
  { code: 'en', shortLabel: 'ENG', label: 'English', nativeLabel: 'English' },
  { code: 'te', shortLabel: 'TEL', label: 'Telugu', nativeLabel: 'తెలుగు' },
  { code: 'hi', shortLabel: 'HIN', label: 'Hindi', nativeLabel: 'हिन्दी' },
];

const i18nMessages = {
  en: { ...enMessages, ...enCommon, ...enHome, ...enProducts },
  te: { ...teMessages, ...teCommon, ...teHome, ...teProducts },
  hi: { ...hiMessages, ...hiCommon, ...hiHome, ...hiProducts },
};

const RTL_LANGUAGES = new Set(['ar']);

const interpolate = (template, values = {}) =>
  String(template || '').replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] ?? '');

const formatReadableKey = (label) =>
  String(label || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const CATEGORY_ID_BY_ENGLISH_NAME = {
  // Old categories (for safety)
  'Heavy Machinery': 'heavy-machinery',
  'Agriculture Tools': 'agriculture-tools',
  'Irrigation & Water': 'irrigation-water',
  'Workshop & MRO': 'workshop-mro',
  'Sprayer Units': 'sprayers-units',
  'Food Processing': 'food-processing',

  // Database categories
  'Farm & garden': 'farm-and-garden',
  'Farm & Garden': 'farm-and-garden',
  'Sprayers': 'sprayers',
  'Landscaping': 'landscaping',
  'Pumps & Motors': 'pumps-and-motors',
  'commercial kitchen equipment': 'commercial-kitchen-equipment',
  'Food packaging Tools': 'food-packaging-tools',
  'Workshop, DIY & MRO': 'workshop,-diy-and-mro',
  'Power Tools': 'power-tools',
  'FERTILIZERS': 'fertilizers',
  'Neutrilizer': 'neutrilizer',
};

const translations = {
  en: {
    freeShipping: 'FREE SHIPPING ON ALL ORDERS OVER ₹5000!',
    searchProducts: 'Search products...',
    search: 'Search',
    searchEverything: 'Search products by name',
    searchResults: 'Search Results',
    allResults: 'results for',
    viewAllResults: 'View all results',
    noSearchResults: 'No matching results found',
    startSearching: 'Start typing a product name',
    matchingProducts: 'Matching Products',
    matchingCategories: 'Matching Categories',
    matchingSubcategories: 'Matching Sub-Categories',
    matchingPages: 'Matching Pages',
    pages: 'Pages',
    offers: 'Offers',
    shoppingCart: 'Shopping Cart',
    accountInfo: 'Account info',
    myOrders: 'My Orders',
    supportChat_trackTickets: 'TRACK SUBMITTED TICKETS',
    supportChat_submittedTickets: 'Submitted Tickets',
    supportChat_noTicketsFallback: 'No tickets submitted yet. Your latest ticket details will appear here after submission.',
    wallet: 'Wallet',
    signOut: 'Sign Out',
    emptyCart: 'There are no items in your cart.',
    subtotal: 'Subtotal:',
    viewCart: 'View Cart',
    checkout: 'Checkout',
    home: 'HOME',
    categories: 'CATEGORIES',
    featured: 'FEATURED',
    menu: 'MENU',
    language: 'Language',
    myAccount: 'MY ACCOUNT',
    signIn: 'SIGN IN',
    addToCart: 'Add to Cart',
    youSave: 'You Save',
    industrialGrade: 'Industrial Grade',
    shyamAgroOriginal: 'Shyam Agro Original',
    quantity: 'Quantity',
    buyItNow: 'Buy It Now',
    freeDelivery: 'Free Delivery',
    warranty: '1 Year Warranty',
    easyReturns: 'Easy Returns',
    addToWishlist: 'Add to Wishlist',
    shareProduct: 'Share Product',
    description: 'Description',
    specifications: 'Specifications',
    shortDescription: 'Short Description',
    reviews: 'Reviews',
    technicalDetails: 'Technical details and usage',
    keyFeatures: 'Key Features',
    packageIncludes: 'Package Includes',
    cartItem: 'item',
    cartItems: 'items',
    remove: 'Remove',
    orderSummary: 'Order Summary',
    store: 'Store',
    aboutProduct: 'About Product',
    workItDoes: 'Work It Does',
    usageInstructions: 'Usage Instructions',
    suitableFor: 'Suitable For',
    noProductVideo: 'Product video not available',
    mediaInfo: 'Info Flyer',
    mediaFront: 'Front View',
    mediaSide: 'Side View',
    mediaBack: 'Back View',
    mediaCloseup: 'Close-Up View',
    mediaTop: 'Top View',
    mediaTopView: 'Top View',
    mediaLeft: 'Left View',
    mediaRight: 'Right View',
    mediaVideo: 'Video',
    previousMedia: 'Previous media',
    nextMedia: 'Next media',
    viewDetails: 'View Details',
    addedToCart: 'added to Cart',
    addedToWishlist: 'Added to Wishlist',
    removedFromWishlist: 'Removed from Wishlist',
    unableWishlist: 'Unable to update wishlist',
    productLinkCopied: 'Product link copied successfully',
    unableShare: 'Unable to share product',
    shoppingSession: 'Shopping Session',
    yourCart: 'Your Cart',
    cartEmptyTitle: 'Your cart is currently empty',
    cartEmptyMessage: "Looks like you haven't added any agriculture tools yet. Start exploring our collections.",
    returnToShop: 'Return to Shop',
    productDetails: 'Product Details',
    noProductsFound: 'No products found',
    price: 'Price',
    sku: 'SKU',
    total: 'Total',
    freeDeliveryMessage: 'Spend ₹5,000 more to qualify for free shipping nationwide.',
    securePayment: 'Secure Payment',
    securePaymentMessage: 'Your data is protected with 256-bit SSL encryption.',
    itemSubtotal: 'Item Subtotal',
    estimatedTax: 'Estimated Tax (18%)',
    deliveryCharges: 'Delivery Charges',
    free: 'Free',
    coinsDiscount: 'Coins Discount',
    couponCode: 'Coupon Code',
    apply: 'Apply',
    totalAmount: 'Total Amount',
    proceedToCheckout: 'Proceed to Checkout',
    securePayments: 'Payments are 100% Secure',
    secureCheckout: 'Secure Checkout',
    checkoutSubtitle: 'Complete your purchase by providing your shipping and billing information.',
    shippingInformation: 'Shipping Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    emailAddress: 'Email Address',
    phoneNumber: 'Phone Number',
    fullAddress: 'Full Address',
    city: 'City',
    state: 'State',
    pincode: 'Pincode',
    paymentMethod: 'Payment Method',
    cashOnDelivery: 'Cash on Delivery',
    payOnDelivery: 'Pay when you receive the tools',
    netBankingUpi: 'Net Banking / UPI',
    secureOnlinePayment: 'Secure online payment',
    confirmPlaceOrder: 'Confirm & Place Order',
    grandTotal: 'Grand Total',
    ourCollections: 'Our Collections',
    allCategories: 'All Categories',
    productPreview: 'Product Preview',
    items: 'Items',
    more: 'More',
    exploreCollection: 'Explore Collection',
    savedProducts: 'Saved Products',
    yourWishlist: 'Your Wishlist',
    wishlistEmptyTitle: 'Your wishlist is empty',
    wishlistEmptyMessage: 'Save your favorite agriculture tools and come back to them whenever you are ready.',
    actions: 'Actions',
    quickLinks: 'Quick Links',
    agricultureStore: 'Agriculture Store',
    becomeSeller: 'Become a Supplier',
    orderTracking: 'Order Tracking',
    agricultureBlog: 'Agriculture Blog',
    customerService: 'Customer Service',
    helpCenter: 'Help Center',
    contactUs: 'Contact Us',
    termsConditions: 'Terms & Conditions',
    privacyPolicy: 'Privacy Policy',
    refundPolicy: 'Refund Policy',
    getInTouch: 'Get in Touch',
    footerAbout: 'Leading marketplace for professional agricultural machinery and industrial equipment. We provide heavy-duty tools to farmers and enterprises across the country.',
    newsletter: 'Subscribe',
    yourEmail: 'Your Email',
    allRightsReserved: 'All Rights Reserved',
    madeInIndia: 'Made in India',
    codAvailable: 'COD Available',
    estimatedDelivery: 'Estimated Delivery',
    deliveryReturn: 'Delivery & Return',
    countryOfOrigin: 'Country of Origin',
    brand: 'Brand',
    mrp: 'MRP',
    stockAvailability: 'Stock Availability',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    reset: 'Reset',
    first: 'First',
    previous: 'Previous',
    next: 'Next',
    last: 'Last',
    reviewsRatings: 'Reviews & Ratings',
    writeReview: 'Write a Review',
    verifiedPurchase: 'Verified purchase',
    viewAllReviews: 'View all reviews',
    showLess: 'Show less',
    reviewName: 'Name',
    reviewNamePlaceholder: 'Enter your name',
    reviewRating: 'Rating',
    reviewMessage: 'Review message',
    reviewMessagePlaceholder: 'Share your experience with this product',
    submitReview: 'Submit Review',
    reviewSubmitted: 'Review submitted successfully',
    reviewNameRequired: 'Name is required',
    reviewRatingRequired: 'Please select a rating',
    reviewMessageRequired: 'Review message is required',
    cancel: 'Cancel',
    totalReviews: 'Total reviews',
    marketplaceTitle: "India's Leading E-Commerce Marketplace",
    customers: 'Customers',
    brandStore: 'Brand Store',
    products: 'Products',
    qualityAssurance: 'Quality Assurance',
    trustedDelivery: 'Trusted Delivery',
    afterSalesAssistance: 'After Sales Assistance',
    relatedProducts: 'Related Products',
    ourTrustedBrands: 'OUR TRUSTED BRANDS',
    noSubcategoriesAvailable: 'No Subcategories Available',
    specialOffer: 'Special Offer',
    premiumFarmingTools: 'PREMIUM FARMING\nTOOLS 40% OFF & ABOVE',
    premiumFarmingToolsDesc: 'Equip your farm with the best industrial tools at unbeatable prices this season.',
    shopCollection: 'SHOP COLLECTION',
    powerTillers: 'Power Tillers',
    powerfulPowerTillers: 'POWERFUL\nPOWER TILLERS',
    powerfulPowerTillersDesc: 'Discover our newly launched range of high-performance industrial power tillers.',
    exploreNow: 'EXPLORE NOW',
    rememberMe: 'REMEMBER ME',
  },
  te: {
    freeShipping: '₹5000 పైబడిన అన్ని ఆర్డర్లకు ఉచిత షిప్పింగ్!',
    searchProducts: 'ఉత్పత్తులను వెతకండి...',
    viewAllResults: 'అన్ని ఫలితాలను చూడండి',
    shoppingCart: 'షాపింగ్ కార్ట్',
    accountInfo: 'ఖాతా సమాచారం',
    myOrders: 'నా ఆర్డర్లు',
    supportChat_trackTickets: 'సమర్పించిన టిక్కెట్‌లను ట్రాక్ చేయండి',
    supportChat_submittedTickets: 'సమర్పించిన టిక్కెట్లు',
    supportChat_noTicketsFallback: 'ఇంకా టిక్కెట్లు సమర్పించబడలేదు. సమర్పించిన తర్వాత మీ తాజా టిక్కెట్ వివరాలు ఇక్కడ కనిపిస్తాయి.',
    wallet: 'వాలెట్',
    signOut: 'సైన్ అవుట్',
    emptyCart: 'మీ కార్ట్‌లో ఉత్పత్తులు లేవు.',
    subtotal: 'ఉప మొత్తం:',
    viewCart: 'కార్ట్ చూడండి',
    checkout: 'చెకౌట్',
    home: 'హోమ్',
    categories: 'వర్గాలు',
    featured: 'ఫీచర్డ్',
    menu: 'మెను',
    language: 'భాష',
    myAccount: 'నా ఖాతా',
    signIn: 'సైన్ ఇన్',
    addToCart: 'కార్ట్‌కు జోడించండి',
    youSave: 'మీ సేవింగ్',
    industrialGrade: 'ఇండస్ట్రియల్ గ్రేడ్',
    shyamAgroOriginal: 'శ్యామ్ ఆగ్రో ఒరిజినల్',
    quantity: 'పరిమాణం',
    buyItNow: 'ఇప్పుడే కొనండి',
    freeDelivery: 'ఉచిత డెలివరీ',
    warranty: '1 సంవత్సరం వారంటీ',
    easyReturns: 'సులభమైన రిటర్న్స్',
    addToWishlist: 'విష్‌లిస్ట్‌కు జోడించండి',
    shareProduct: 'ఉత్పత్తిని షేర్ చేయండి',
    description: 'వివరణ',
    specifications: 'స్పెసిఫికేషన్లు',
    shortDescription: 'చిన్న వివరణ',
    reviews: 'రివ్యూలు',
    technicalDetails: 'సాంకేతిక వివరాలు మరియు వినియోగం',
    keyFeatures: 'ప్రధాన లక్షణాలు',
    packageIncludes: 'ప్యాకేజీలో ఉంటాయి',
    cartItem: 'ఉత్పత్తి',
    cartItems: 'ఉత్పత్తులు',
    remove: 'తొలగించండి',
    orderSummary: 'ఆర్డర్ సారాంశం',
    store: 'స్టోర్',
    aboutProduct: 'ఉత్పత్తి గురించి',
    workItDoes: 'ఇది చేసే పని',
    usageInstructions: 'వినియోగ సూచనలు',
    suitableFor: 'అనుకూలం',
    noProductVideo: 'ఉత్పత్తి వీడియో అందుబాటులో లేదు',
    mediaInfo: 'సమాచారం',
    mediaFront: 'ముందు',
    mediaSide: 'పక్క',
    mediaBack: 'వెనుక',
    mediaCloseup: 'క్లోజ్-అప్',
    mediaTop: 'పై',
    mediaTopView: 'పై భాగం',
    mediaLeft: 'ఎడమ వైపు భాగం',
    mediaRight: 'కుడి వైపు భాగం',
    mediaVideo: 'వీడియో',
    previousMedia: 'మునుపటి మీడియా',
    nextMedia: 'తదుపరి మీడియా',
    viewDetails: 'వివరాలు చూడండి',
    addedToCart: 'కార్ట్‌కు జోడించబడింది',
    addedToWishlist: 'విష్‌లిస్ట్‌కు జోడించబడింది',
    removedFromWishlist: 'విష్‌లిస్ట్ నుండి తొలగించబడింది',
    unableWishlist: 'విష్‌లిస్ట్‌ను నవీకరించలేకపోయాం',
    productLinkCopied: 'ఉత్పత్తి లింక్ కాపీ అయింది',
    unableShare: 'ఉత్పత్తిని షేర్ చేయలేకపోయాం',
    shoppingSession: 'షాపింగ్ సెషన్',
    yourCart: 'మీ కార్ట్',
    cartEmptyTitle: 'మీ కార్ట్ ప్రస్తుతం ఖాళీగా ఉంది',
    cartEmptyMessage: 'మీరు ఇంకా వ్యవసాయ పరికరాలు జోడించలేదు. మా కలెక్షన్లను చూడండి.',
    returnToShop: 'షాప్‌కు తిరిగి వెళ్లండి',
    productDetails: 'ఉత్పత్తి వివరాలు',
    noProductsFound: 'ఉత్పత్తులు కనబడలేదు',
    price: 'ధర',
    sku: 'SKU',
    total: 'మొత్తం',
    freeDeliveryMessage: 'దేశవ్యాప్తంగా ఉచిత డెలివరీకి ₹5,000 పైగా కొనుగోలు చేయండి.',
    securePayment: 'సురక్షిత చెల్లింపు',
    securePaymentMessage: 'మీ డేటా 256-bit SSL ఎన్క్రిప్షన్‌తో రక్షించబడుతుంది.',
    itemSubtotal: 'వస్తువుల ఉపమొత్తం',
    estimatedTax: 'అంచనా పన్ను (18%)',
    deliveryCharges: 'డెలివరీ ఛార్జీలు',
    free: 'ఉచితం',
    coinsDiscount: 'కాయిన్స్ డిస్కౌంట్',
    couponCode: 'కూపన్ కోడ్',
    apply: 'అప్లై',
    totalAmount: 'మొత్తం మొత్తం',
    proceedToCheckout: 'చెకౌట్‌కు కొనసాగండి',
    securePayments: 'చెల్లింపులు 100% సురక్షితం',
    secureCheckout: 'సురక్షిత చెకౌట్',
    checkoutSubtitle: 'షిప్పింగ్ మరియు బిల్లింగ్ వివరాలు ఇచ్చి కొనుగోలును పూర్తి చేయండి.',
    shippingInformation: 'షిప్పింగ్ సమాచారం',
    firstName: 'మొదటి పేరు',
    lastName: 'చివరి పేరు',
    emailAddress: 'ఈమెయిల్ చిరునామా',
    phoneNumber: 'ఫోన్ నంబర్',
    fullAddress: 'పూర్తి చిరునామా',
    city: 'నగరం',
    state: 'రాష్ట్రం',
    pincode: 'పిన్‌కోడ్',
    paymentMethod: 'చెల్లింపు విధానం',
    cashOnDelivery: 'క్యాష్ ఆన్ డెలివరీ',
    payOnDelivery: 'పరికరాలు అందుకున్నప్పుడు చెల్లించండి',
    netBankingUpi: 'నెట్ బ్యాంకింగ్ / యూపీఐ',
    secureOnlinePayment: 'సురక్షిత ఆన్‌లైన్ చెల్లింపు',
    confirmPlaceOrder: 'ఆర్డర్ నిర్ధారించండి',
    grandTotal: 'గ్రాండ్ టోటల్',
    ourCollections: 'మా కలెక్షన్లు',
    allCategories: 'అన్ని వర్గాలు',
    productPreview: 'ఉత్పత్తి ప్రివ్యూ',
    items: 'వస్తువులు',
    more: 'మరిన్ని',
    exploreCollection: 'కలెక్షన్ చూడండి',
    savedProducts: 'సేవ్ చేసిన ఉత్పత్తులు',
    yourWishlist: 'మీ విష్‌లిస్ట్',
    wishlistEmptyTitle: 'మీ విష్‌లిస్ట్ ఖాళీగా ఉంది',
    wishlistEmptyMessage: 'మీకు ఇష్టమైన వ్యవసాయ పరికరాలను సేవ్ చేసి తర్వాత చూడండి.',
    actions: 'చర్యలు',
    quickLinks: 'త్వరిత లింకులు',
    agricultureStore: 'వ్యవసాయ స్టోర్',
    becomeSeller: 'విక్రేతగా మారండి',
    orderTracking: 'ఆర్డర్ ట్రాకింగ్',
    agricultureBlog: 'వ్యవసాయ బ్లాగ్',
    customerService: 'కస్టమర్ సేవ',
    helpCenter: 'సహాయ కేంద్రం',
    contactUs: 'మమ్మల్ని సంప్రదించండి',
    termsConditions: 'నిబంధనలు',
    privacyPolicy: 'గోప్యతా విధానం',
    refundPolicy: 'రిఫండ్ విధానం',
    getInTouch: 'సంప్రదించండి',
    footerAbout: 'ప్రొఫెషనల్ వ్యవసాయ యంత్రాలు మరియు పారిశ్రామిక పరికరాల కోసం ప్రముఖ మార్కెట్‌ప్లేస్.',
    newsletter: 'సబ్‌స్క్రయిబ్',
    yourEmail: 'మీ ఈమెయిల్',
    allRightsReserved: 'అన్ని హక్కులు రిజర్వ్ చేయబడ్డాయి',
    madeInIndia: 'భారతదేశంలో తయారు చేయబడింది',
    codAvailable: 'COD అందుబాటులో ఉంది',
    estimatedDelivery: 'అంచనా డెలివరీ',
    deliveryReturn: 'డెలివరీ & రిటర్న్',
    countryOfOrigin: 'మూల దేశం',
    brand: 'బ్రాండ్',
    mrp: 'ఎంఆర్పీ',
    stockAvailability: '\u0C38\u0C4D\u0C1F\u0C3E\u0C15\u0C4D \u0C32\u0C2D\u0C2F\u0C24',
    inStock: '\u0C38\u0C4D\u0C1F\u0C3E\u0C15\u0C4D\u0C32\u0C4B \u0C09\u0C02\u0C26\u0C3F',
    outOfStock: '\u0C38\u0C4D\u0C1F\u0C3E\u0C15\u0C4D \u0C32\u0C47\u0C26\u0C41',
    reset: '\u0C30\u0C40\u0C38\u0C46\u0C1F\u0C4D',
    first: '\u0C2E\u0C4A\u0C26\u0C1F\u0C3F',
    previous: '\u0C2E\u0C41\u0C28\u0C41\u0C2A\u0C1F\u0C3F',
    next: '\u0C24\u0C26\u0C41\u0C2A\u0C30\u0C3F',
    last: '\u0C1A\u0C3F\u0C35\u0C30\u0C3F',
    reviewsRatings: '\u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42\u0C32\u0C41 & \u0C30\u0C47\u0C1F\u0C3F\u0C02\u0C17\u0C4D\u0C38\u0C4D',
    writeReview: '\u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42 \u0C30\u0C3E\u0C2F\u0C02\u0C21\u0C3F',
    verifiedPurchase: '\u0C27\u0C43\u0C35\u0C40\u0C15\u0C30\u0C3F\u0C02\u0C1A\u0C3F\u0C28 \u0C15\u0C4A\u0C28\u0C41\u0C17\u0C4B\u0C32\u0C41',
    viewAllReviews: '\u0C05\u0C28\u0C4D\u0C28\u0C3F \u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42\u0C32\u0C41 \u0C1A\u0C42\u0C21\u0C02\u0C21\u0C3F',
    showLess: '\u0C24\u0C15\u0C4D\u0C15\u0C41\u0C35 \u0C1A\u0C42\u0C2A\u0C3F\u0C02\u0C1A\u0C41',
    reviewName: '\u0C2A\u0C47\u0C30\u0C41',
    reviewNamePlaceholder: '\u0C2E\u0C40 \u0C2A\u0C47\u0C30\u0C41 \u0C30\u0C3E\u0C2F\u0C02\u0C21\u0C3F',
    reviewRating: '\u0C30\u0C47\u0C1F\u0C3F\u0C02\u0C17\u0C4D',
    reviewMessage: '\u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42 \u0C38\u0C02\u0C26\u0C47\u0C36\u0C02',
    reviewMessagePlaceholder: '\u0C08 \u0C09\u0C24\u0C4D\u0C2A\u0C24\u0C4D\u0C24\u0C3F\u0C24\u0C4B \u0C2E\u0C40 \u0C05\u0C28\u0C41\u0C2D\u0C35\u0C3E\u0C28\u0C4D\u0C28\u0C3F \u0C2A\u0C02\u0C1A\u0C41\u0C15\u0C4B\u0C02\u0C21\u0C3F',
    submitReview: '\u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42 \u0C38\u0C2E\u0C30\u0C4D\u0C2A\u0C3F\u0C02\u0C1A\u0C02\u0C21\u0C3F',
    reviewSubmitted: '\u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42 \u0C35\u0C3F\u0C1C\u0C2F\u0C35\u0C02\u0C24\u0C02\u0C17\u0C3E \u0C38\u0C2E\u0C30\u0C4D\u0C2A\u0C3F\u0C02\u0C1A\u0C2C\u0C21\u0C3F\u0C02\u0C26\u0C3F',
    reviewNameRequired: '\u0C2A\u0C47\u0C30\u0C41 \u0C05\u0C35\u0C38\u0C30\u0C02',
    reviewRatingRequired: '\u0C26\u0C2F\u0C1A\u0C47\u0C38\u0C3F \u0C30\u0C47\u0C1F\u0C3F\u0C02\u0C17\u0C4D \u0C0E\u0C02\u0C1A\u0C41\u0C15\u0C4B\u0C02\u0C21\u0C3F',
    reviewMessageRequired: '\u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42 \u0C38\u0C02\u0C26\u0C47\u0C36\u0C02 \u0C05\u0C35\u0C38\u0C30\u0C02',
    cancel: '\u0C30\u0C26\u0C4D\u0C26\u0C41',
    totalReviews: '\u0C2E\u0C4A\u0C24\u0C4D\u0C24\u0C02 \u0C30\u0C3F\u0C35\u0C4D\u0C2F\u0C42\u0C32\u0C41',
    marketplaceTitle: "India's Leading E-Commerce Marketplace",
    customers: 'Customers',
    brandStore: 'Brand Store',
    products: 'Products',
    qualityAssurance: 'Quality Assurance',
    trustedDelivery: 'Trusted Delivery',
    afterSalesAssistance: 'After Sales Assistance',
    relatedProducts: 'Related Products',
    ourTrustedBrands: 'మా నమ్మకమైన బ్రాండ్లు',
    noSubcategoriesAvailable: 'ఉపవర్గాలు అందుబాటులో లేవు',
    specialOffer: 'ప్రత్యేక ఆఫర్',
    premiumFarmingTools: 'ప్రీమియం వ్యవసాయ పరికరాలు\n40% అంతకంటే ఎక్కువ తగ్గింపు',
    premiumFarmingToolsDesc: 'ఈ సీజన్‌లో సాటిలేని ధరలకు అత్యుత్తమ పారిశ్రామిక పరికరాలతో మీ వ్యవసాయాన్ని మెరుగుపరచండి.',
    shopCollection: 'కలెక్షన్ చూడండి',
    powerTillers: 'పవర్ టిల్లర్లు',
    powerfulPowerTillers: 'శక్తివంతమైన\nపవర్ టిల్లర్లు',
    powerfulPowerTillersDesc: 'మా కొత్తగా ప్రారంభించిన అధిక-పనితీరు గల పారిశ్రామిక పవర్ టిల్లర్ల శ్రేణిని కనుగొనండి.',
    exploreNow: 'ఇప్పుడే అన్వేషించండి',
    rememberMe: 'నన్ను గుర్తుంచుకో',
  },
  hi: {
    freeShipping: '₹5000 से अधिक सभी ऑर्डर पर मुफ्त शिपिंग!',
    searchProducts: 'उत्पाद खोजें...',
    viewAllResults: 'सभी परिणाम देखें',
    shoppingCart: 'शॉपिंग कार्ट',
    accountInfo: 'खाता जानकारी',
    myOrders: 'मेरे ऑर्डर',
    supportChat_trackTickets: 'सबमिट किए गए टिकटों को ट्रैक करें',
    supportChat_submittedTickets: 'सबमिट किए गए टिकट',
    supportChat_noTicketsFallback: 'अभी तक कोई टिकट सबमिट नहीं किया गया है। सबमिट करने के बाद आपके नवीनतम टिकट विवरण यहां दिखाई देंगे।',
    wallet: 'वॉलेट',
    signOut: 'साइन आउट',
    emptyCart: 'आपके कार्ट में कोई आइटम नहीं है.',
    subtotal: 'उप-योग:',
    viewCart: 'कार्ट देखें',
    checkout: 'चेकआउट',
    home: 'होम',
    categories: 'श्रेणियां',
    featured: 'फीचर्ड',
    menu: 'मेनू',
    language: 'भाषा',
    myAccount: 'मेरा खाता',
    signIn: 'साइन इन',
    addToCart: 'कार्ट में जोड़ें',
    youSave: 'आपकी बचत',
    industrialGrade: 'इंडस्ट्रियल ग्रेड',
    shyamAgroOriginal: 'श्याम एग्रो ओरिजिनल',
    quantity: 'मात्रा',
    buyItNow: 'अभी खरीदें',
    freeDelivery: 'मुफ्त डिलीवरी',
    warranty: '1 साल वारंटी',
    easyReturns: 'आसान रिटर्न',
    addToWishlist: 'विशलिस्ट में जोड़ें',
    shareProduct: 'उत्पाद शेयर करें',
    description: 'विवरण',
    specifications: 'विशेषताएं',
    shortDescription: 'संक्षिप्त विवरण',
    reviews: 'समीक्षाएं',
    technicalDetails: 'तकनीकी जानकारी और उपयोग',
    keyFeatures: 'मुख्य फीचर्स',
    packageIncludes: 'पैकेज में शामिल',
    cartItem: 'आइटम',
    cartItems: 'आइटम',
    remove: 'हटाएं',
    orderSummary: 'ऑर्डर सारांश',
    store: 'स्टोर',
    aboutProduct: 'उत्पाद के बारे में',
    workItDoes: 'यह क्या काम करता है',
    usageInstructions: 'उपयोग निर्देश',
    suitableFor: 'इनके लिए उपयुक्त',
    noProductVideo: 'उत्पाद वीडियो उपलब्ध नहीं है',
    mediaFront: 'सामने',
    mediaSide: 'साइड',
    mediaBack: 'पीछे',
    mediaCloseup: 'क्लोज-अप',
    mediaTop: 'ऊपर',
    mediaTopView: 'ऊपरी दृश्य',
    mediaLeft: 'बायाँ दृश्य',
    mediaRight: 'दायाँ दृश्य',
    mediaVideo: 'वीडियो',
    previousMedia: 'पिछला मीडिया',
    nextMedia: 'अगला मीडिया',
    viewDetails: 'विवरण देखें',
    addedToCart: 'कार्ट में जोड़ा गया',
    addedToWishlist: 'विशलिस्ट में जोड़ा गया',
    removedFromWishlist: 'विशलिस्ट से हटाया गया',
    unableWishlist: 'विशलिस्ट अपडेट नहीं हो सकी',
    productLinkCopied: 'उत्पाद लिंक कॉपी हुआ',
    unableShare: 'उत्पाद शेयर नहीं हो सका',
    shoppingSession: 'शॉपिंग सेशन',
    yourCart: 'आपका कार्ट',
    cartEmptyTitle: 'आपका कार्ट अभी खाली है',
    cartEmptyMessage: 'आपने अभी कोई कृषि उपकरण नहीं जोड़ा है. हमारी कलेक्शन देखें.',
    returnToShop: 'शॉप पर वापस जाएं',
    productDetails: 'उत्पाद विवरण',
    noProductsFound: 'कोई उत्पाद नहीं मिला',
    price: 'कीमत',
    sku: 'SKU',
    total: 'कुल',
    freeDeliveryMessage: 'देशभर में मुफ्त शिपिंग के लिए ₹5,000 से अधिक खरीदें.',
    securePayment: 'सुरक्षित भुगतान',
    securePaymentMessage: 'आपका डेटा 256-bit SSL एन्क्रिप्शन से सुरक्षित है.',
    itemSubtotal: 'आइटम उप-योग',
    estimatedTax: 'अनुमानित टैक्स (18%)',
    deliveryCharges: 'डिलीवरी शुल्क',
    free: 'मुफ्त',
    coinsDiscount: 'कॉइन डिस्काउंट',
    couponCode: 'कूपन कोड',
    apply: 'लागू करें',
    totalAmount: 'कुल राशि',
    proceedToCheckout: 'चेकआउट पर जाएं',
    securePayments: 'भुगतान 100% सुरक्षित हैं',
    secureCheckout: 'सुरक्षित चेकआउट',
    checkoutSubtitle: 'शिपिंग और बिलिंग जानकारी देकर खरीदारी पूरी करें.',
    shippingInformation: 'शिपिंग जानकारी',
    firstName: 'पहला नाम',
    lastName: 'अंतिम नाम',
    emailAddress: 'ईमेल पता',
    phoneNumber: 'फोन नंबर',
    fullAddress: 'पूरा पता',
    city: 'शहर',
    state: 'राज्य',
    pincode: 'पिनकोड',
    paymentMethod: 'भुगतान तरीका',
    cashOnDelivery: 'कैश ऑन डिलीवरी',
    payOnDelivery: 'उपकरण मिलने पर भुगतान करें',
    netBankingUpi: 'नेट बैंकिंग / यूपीआई',
    secureOnlinePayment: 'सुरक्षित ऑनलाइन भुगतान',
    confirmPlaceOrder: 'ऑर्डर कन्फर्म करें',
    grandTotal: 'ग्रैंड टोटल',
    ourCollections: 'हमारे कलेक्शन',
    allCategories: 'सभी श्रेणियां',
    productPreview: 'उत्पाद प्रीव्यू',
    items: 'आइटम',
    more: 'और',
    exploreCollection: 'कलेक्शन देखें',
    savedProducts: 'सेव किए गए उत्पाद',
    yourWishlist: 'आपकी विशलिस्ट',
    wishlistEmptyTitle: 'आपकी विशलिस्ट खाली है',
    wishlistEmptyMessage: 'अपने पसंदीदा कृषि उपकरण सेव करें और बाद में देखें.',
    actions: 'कार्रवाई',
    quickLinks: 'त्वरित लिंक',
    agricultureStore: 'कृषि स्टोर',
    becomeSeller: 'विक्रेता बनें',
    orderTracking: 'ऑर्डर ट्रैकिंग',
    agricultureBlog: 'कृषि ब्लॉग',
    customerService: 'ग्राहक सेवा',
    helpCenter: 'सहायता केंद्र',
    contactUs: 'संपर्क करें',
    termsConditions: 'नियम और शर्तें',
    privacyPolicy: 'गोपनीयता नीति',
    refundPolicy: 'रिफंड नीति',
    getInTouch: 'संपर्क में रहें',
    footerAbout: 'पेशेवर कृषि मशीनरी और औद्योगिक उपकरणों के लिए प्रमुख मार्केटप्लेस.',
    newsletter: 'सदस्यता लें',
    yourEmail: 'आपका ईमेल',
    allRightsReserved: 'सर्वाधिकार सुरक्षित',
    madeInIndia: 'भारत में निर्मित',
    codAvailable: 'COD उपलब्ध',
    estimatedDelivery: 'अनुमानित डिलीवरी',
    deliveryReturn: 'डिलीवरी और रिटर्न',
    countryOfOrigin: 'मूल देश',
    brand: 'ब्रांड',
    mrp: 'MRP',
    stockAvailability: '\u0938\u094D\u091F\u0949\u0915 \u0909\u092A\u0932\u092C\u094D\u0927\u0924\u093E',
    inStock: '\u0938\u094D\u091F\u0949\u0915 \u092E\u0947\u0902',
    outOfStock: '\u0938\u094D\u091F\u0949\u0915 \u0938\u0947 \u092C\u093E\u0939\u0930',
    reset: '\u0930\u0940\u0938\u0947\u091F',
    first: '\u092A\u0939\u0932\u093E',
    previous: '\u092A\u093F\u091B\u0932\u093E',
    next: '\u0905\u0917\u0932\u093E',
    last: '\u0905\u0902\u0924\u093F\u092E',
    reviewsRatings: '\u0930\u093F\u0935\u094D\u092F\u0942 \u0914\u0930 \u0930\u0947\u091F\u093F\u0902\u0917',
    writeReview: '\u0930\u093F\u0935\u094D\u092F\u0942 \u0932\u093F\u0916\u0947\u0902',
    verifiedPurchase: '\u0938\u0924\u094D\u092F\u093E\u092A\u093F\u0924 \u0916\u0930\u0940\u0926',
    viewAllReviews: '\u0938\u092D\u0940 \u0930\u093F\u0935\u094D\u092F\u0942 \u0926\u0947\u0916\u0947\u0902',
    showLess: '\u0915\u092E \u0926\u093F\u0916\u093E\u090F\u0902',
    reviewName: '\u0928\u093E\u092E',
    reviewNamePlaceholder: '\u0905\u092A\u0928\u093E \u0928\u093E\u092E \u0932\u093F\u0916\u0947\u0902',
    reviewRating: '\u0930\u0947\u091F\u093F\u0902\u0917',
    reviewMessage: '\u0930\u093F\u0935\u094D\u092F\u0942 \u0938\u0902\u0926\u0947\u0936',
    reviewMessagePlaceholder: '\u0907\u0938 \u0909\u0924\u094D\u092A\u093E\u0926 \u0915\u093E \u0905\u092A\u0928\u093E \u0905\u0928\u0941\u092D\u0935 \u0932\u093F\u0916\u0947\u0902',
    submitReview: '\u0930\u093F\u0935\u094D\u092F\u0942 \u091C\u092E\u093E \u0915\u0930\u0947\u0902',
    reviewSubmitted: '\u0930\u093F\u0935\u094D\u092F\u0942 \u0938\u092B\u0932\u0924\u093E\u092A\u0942\u0930\u094D\u0935\u0915 \u091C\u092E\u093E \u0939\u0941\u0906',
    reviewNameRequired: '\u0928\u093E\u092E \u0906\u0935\u0936\u094D\u092F\u0915 \u0939\u0948',
    reviewRatingRequired: '\u0915\u0943\u092A\u092F\u093E \u0930\u0947\u091F\u093F\u0902\u0917 \u091A\u0941\u0928\u0947\u0902',
    reviewMessageRequired: '\u0930\u093F\u0935\u094D\u092F\u0942 \u0938\u0902\u0926\u0947\u0936 \u0906\u0935\u0936\u094D\u092F\u0915 \u0939\u0948',
    cancel: '\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902',
    totalReviews: '\u0915\u0941\u0932 \u0930\u093F\u0935\u094D\u092F\u0942',
    marketplaceTitle: "India's Leading E-Commerce Marketplace",
    customers: 'Customers',
    brandStore: 'Brand Store',
    products: 'Products',
    qualityAssurance: 'Quality Assurance',
    trustedDelivery: 'Trusted Delivery',
    afterSalesAssistance: 'After Sales Assistance',
    relatedProducts: 'Related Products',
    ourTrustedBrands: 'हमारे विश्वसनीय ब्रांड',
    noSubcategoriesAvailable: 'कोई उपश्रेणी उपलब्ध नहीं है',
    specialOffer: 'विशेष ऑफर',
    premiumFarmingTools: 'प्रीमियम कृषि उपकरण\n40% और अधिक की छूट',
    premiumFarmingToolsDesc: 'इस सीजन में बेजोड़ कीमतों पर सर्वोत्तम औद्योगिक उपकरणों के साथ अपने खेत को सुसज्जित करें।',
    shopCollection: 'संग्रह देखें',
    powerTillers: 'पावर टिलर',
    powerfulPowerTillers: 'शक्तिशाली\nपावर टिलर',
    powerfulPowerTillersDesc: 'हमारे नए लॉन्च किए गए उच्च-प्रदर्शन वाले औद्योगिक पावर टिलर की श्रृंखला देखें।',
    exploreNow: 'अभी अन्वेषण करें',
    rememberMe: 'मुझे याद रखें',
  },
};

const localeTranslationProfiles = {
  te: {
    marketplaceTitle: 'భారతదేశపు ప్రముఖ ఈ-కామర్స్ మార్కెట్‌ప్లేస్',
    customers: 'వినియోగదారులు',
    brandStore: 'బ్రాండ్ స్టోర్',
    products: 'ఉత్పత్తులు',
    qualityAssurance: 'నాణ్యత హామీ',
    trustedDelivery: 'నమ్మకమైన డెలివరీ',
    afterSalesAssistance: 'అమ్మకం తర్వాత సహాయం',
    relatedProducts: 'సంబంధిత ఉత్పత్తులు',
  },
  hi: {
    marketplaceTitle: 'भारत का प्रमुख ई-कॉमर्स मार्केटप्लेस',
    customers: 'ग्राहक',
    brandStore: 'ब्रांड स्टोर',
    products: 'उत्पाद',
    qualityAssurance: 'गुणवत्ता आश्वासन',
    trustedDelivery: 'विश्वसनीय डिलीवरी',
    afterSalesAssistance: 'बिक्री के बाद सहायता',
    relatedProducts: 'संबंधित उत्पाद',
  },
  ta: {
    defaultText: 'விவரம்',
    freeShipping: '₹5000க்கு மேற்பட்ட அனைத்து ஆர்டர்களுக்கும் இலவச ஷிப்பிங்!',
    searchProducts: 'தயாரிப்புகளை தேடுங்கள்...',
    search: 'தேடு',
    searchEverything: 'தயாரிப்பு பெயரால் தேடுங்கள்',
    searchResults: 'தேடல் முடிவுகள்',
    allResults: 'முடிவுகள்',
    viewAllResults: 'அனைத்து முடிவுகளையும் பார்க்க',
    noSearchResults: 'பொருந்தும் முடிவுகள் இல்லை',
    startSearching: 'தயாரிப்பு பெயரை தட்டச்சு செய்யுங்கள்',
    matchingProducts: 'பொருந்தும் தயாரிப்புகள்',
    matchingCategories: 'பொருந்தும் வகைகள்',
    matchingSubcategories: 'பொருந்தும் துணை வகைகள்',
    matchingPages: 'பொருந்தும் பக்கங்கள்',
    pages: 'பக்கங்கள்',
    offers: 'சலுகைகள்',
    shoppingCart: 'ஷாப்பிங் கார்ட்',
    accountInfo: 'கணக்கு தகவல்',
    myOrders: 'என் ஆர்டர்கள்',
    wallet: 'வாலெட்',
    signOut: 'வெளியேறு',
    emptyCart: 'உங்கள் கார்டில் பொருட்கள் இல்லை.',
    subtotal: 'இடைத் தொகை:',
    viewCart: 'கார்டை பார்க்க',
    checkout: 'செக்க்அவுட்',
    home: 'முகப்பு',
    categories: 'வகைகள்',
    featured: 'சிறப்புப் பொருட்கள்',
    menu: 'மெனு',
    language: 'மொழி',
    myAccount: 'என் கணக்கு',
    signIn: 'உள்நுழை',
    addToCart: 'கார்டில் சேர்',
    youSave: 'நீங்கள் சேமிக்கிறீர்கள்',
    industrialGrade: 'தொழில்துறை தரம்',
    shyamAgroOriginal: 'ஷ்யாம் ஆக்ரோ ஒரிஜினல்',
    quantity: 'அளவு',
    buyItNow: 'இப்போது வாங்க',
    freeDelivery: 'இலவச டெலிவரி',
    warranty: '1 ஆண்டு வாரண்டி',
    easyReturns: 'எளிய ரிட்டர்ன்',
    addToWishlist: 'விருப்பப்பட்டியலில் சேர்',
    shareProduct: 'தயாரிப்பைப் பகிர்',
    description: 'விளக்கம்',
    specifications: 'விவரக்குறிப்புகள்',
    shortDescription: 'சுருக்க விளக்கம்',
    reviews: 'மதிப்புரைகள்',
    technicalDetails: 'தொழில்நுட்ப விவரங்கள் மற்றும் பயன்பாடு',
    keyFeatures: 'முக்கிய அம்சங்கள்',
    packageIncludes: 'பேக்கேஜில் உள்ளவை',
    cartItem: 'பொருள்',
    cartItems: 'பொருட்கள்',
    remove: 'நீக்கு',
    orderSummary: 'ஆர்டர் சுருக்கம்',
    store: 'ஸ்டோர்',
    aboutProduct: 'தயாரிப்பு பற்றி',
    workItDoes: 'இது செய்யும் வேலை',
    usageInstructions: 'பயன்பாட்டு வழிமுறைகள்',
    suitableFor: 'இதற்கு பொருத்தமானது',
    noProductVideo: 'தயாரிப்பு வீடியோ இல்லை',
    mediaFront: 'முன்புறம்',
    mediaSide: 'பக்கம்',
    mediaBack: 'பின்புறம்',
    mediaCloseup: 'நெருக்கக் காட்சி',
    mediaTop: 'மேல்',
    mediaTopView: 'மேல் காட்சி',
    mediaLeft: 'இடதுபுற காட்சி',
    mediaRight: 'வலதுபுற காட்சி',
    mediaVideo: 'வீடியோ',
    previousMedia: 'முந்தைய மீடியா',
    nextMedia: 'அடுத்த மீடியா',
    viewDetails: 'விவரங்களைப் பார்க்க',
    addedToCart: 'கார்டில் சேர்க்கப்பட்டது',
    addedToWishlist: 'விருப்பப்பட்டியலில் சேர்க்கப்பட்டது',
    removedFromWishlist: 'விருப்பப்பட்டியலில் இருந்து நீக்கப்பட்டது',
    unableWishlist: 'விருப்பப்பட்டியலை புதுப்பிக்க முடியவில்லை',
    productLinkCopied: 'தயாரிப்பு இணைப்பு நகலெடுக்கப்பட்டது',
    unableShare: 'தயாரிப்பைப் பகிர முடியவில்லை',
    shoppingSession: 'ஷாப்பிங் அமர்வு',
    yourCart: 'உங்கள் கார்ட்',
    cartEmptyTitle: 'உங்கள் கார்ட் தற்போது காலியாக உள்ளது',
    cartEmptyMessage: 'நீங்கள் இன்னும் எந்த வேளாண் கருவியையும் சேர்க்கவில்லை. எங்கள் தொகுப்புகளைப் பாருங்கள்.',
    returnToShop: 'கடைக்குத் திரும்பு',
    productDetails: 'தயாரிப்பு விவரங்கள்',
    noProductsFound: 'தயாரிப்புகள் இல்லை',
    price: 'விலை',
    sku: 'SKU',
    total: 'மொத்தம்',
    freeDeliveryMessage: 'நாட்டளவில் இலவச ஷிப்பிங்கிற்கு ₹5,000க்கு மேல் வாங்குங்கள்.',
    securePayment: 'பாதுகாப்பான கட்டணம்',
    securePaymentMessage: 'உங்கள் தரவு 256-bit SSL குறியாக்கத்தால் பாதுகாக்கப்படுகிறது.',
    itemSubtotal: 'பொருள் இடைத்தொகை',
    estimatedTax: 'மதிப்பிடப்பட்ட வரி (18%)',
    deliveryCharges: 'டெலிவரி கட்டணம்',
    free: 'இலவசம்',
    coinsDiscount: 'காயின்ஸ் தள்ளுபடி',
    couponCode: 'கூப்பன் குறியீடு',
    apply: 'பயன்படுத்து',
    totalAmount: 'மொத்த தொகை',
    proceedToCheckout: 'செக்க்அவுட் செல்ல',
    securePayments: 'கட்டணங்கள் 100% பாதுகாப்பானவை',
    secureCheckout: 'பாதுகாப்பான செக்க்அவுட்',
    checkoutSubtitle: 'ஷிப்பிங் மற்றும் பில்லிங் தகவல்களை அளித்து வாங்குதலை முடிக்கவும்.',
    shippingInformation: 'ஷிப்பிங் தகவல்',
    firstName: 'முதல் பெயர்',
    lastName: 'கடைசி பெயர்',
    emailAddress: 'மின்னஞ்சல் முகவரி',
    phoneNumber: 'தொலைபேசி எண்',
    fullAddress: 'முழு முகவரி',
    city: 'நகரம்',
    state: 'மாநிலம்',
    pincode: 'பின்கோடு',
    paymentMethod: 'கட்டண முறை',
    cashOnDelivery: 'கேஷ் ஆன் டெலிவரி',
    payOnDelivery: 'பொருள் கிடைக்கும் போது கட்டணம் செலுத்துங்கள்',
    netBankingUpi: 'நெட் பேங்கிங் / UPI',
    secureOnlinePayment: 'பாதுகாப்பான ஆன்லைன் கட்டணம்',
    confirmPlaceOrder: 'ஆர்டரை உறுதி செய்',
    grandTotal: 'மொத்தத் தொகை',
    ourCollections: 'எங்கள் தொகுப்புகள்',
    allCategories: 'அனைத்து வகைகள்',
    productPreview: 'தயாரிப்பு முன்னோட்டம்',
    items: 'பொருட்கள்',
    more: 'மேலும்',
    exploreCollection: 'தொகுப்பைப் பாருங்கள்',
    savedProducts: 'சேமித்த தயாரிப்புகள்',
    yourWishlist: 'உங்கள் விருப்பப்பட்டியல்',
    wishlistEmptyTitle: 'உங்கள் விருப்பப்பட்டியல் காலியாக உள்ளது',
    wishlistEmptyMessage: 'உங்களுக்கு பிடித்த வேளாண் கருவிகளை சேமித்து பின்னர் பார்க்கலாம்.',
    actions: 'செயல்கள்',
    quickLinks: 'விரைவு இணைப்புகள்',
    agricultureStore: 'வேளாண் ஸ்டோர்',
    becomeSeller: 'விற்பனையாளராகுங்கள்',
    orderTracking: 'ஆர்டர் கண்காணிப்பு',
    agricultureBlog: 'வேளாண் வலைப்பதிவு',
    customerService: 'வாடிக்கையாளர் சேவை',
    helpCenter: 'உதவி மையம்',
    contactUs: 'எங்களை தொடர்பு கொள்ள',
    termsConditions: 'விதிமுறைகள்',
    privacyPolicy: 'தனியுரிமை கொள்கை',
    refundPolicy: 'ரீஃபண்ட் கொள்கை',
    getInTouch: 'தொடர்பில் இருங்கள்',
    footerAbout: 'தொழில்முறை வேளாண் இயந்திரங்கள் மற்றும் தொழில்துறை உபகரணங்களுக்கான முன்னணி சந்தை.',
    newsletter: 'சந்தா',
    yourEmail: 'உங்கள் மின்னஞ்சல்',
    allRightsReserved: 'அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை',
    madeInIndia: 'இந்தியாவில் தயாரிக்கப்பட்டது',
    codAvailable: 'COD கிடைக்கும்',
    estimatedDelivery: 'மதிப்பிடப்பட்ட டெலிவரி',
    deliveryReturn: 'டெலிவரி & ரிட்டர்ன்',
    countryOfOrigin: 'உற்பத்தி நாடு',
    brand: 'பிராண்ட்',
    mrp: 'MRP',
    stockAvailability: 'ஸ்டாக் நிலை',
    inStock: 'ஸ்டாக்கில் உள்ளது',
    outOfStock: 'ஸ்டாக்கில் இல்லை',
    reset: 'மீட்டமை',
    first: 'முதல்',
    previous: 'முந்தைய',
    next: 'அடுத்தது',
    last: 'கடைசி',
    reviewsRatings: 'மதிப்புரைகள் & மதிப்பீடுகள்',
    writeReview: 'மதிப்புரை எழுதுங்கள்',
    verifiedPurchase: 'சரிபார்க்கப்பட்ட வாங்குதல்',
    viewAllReviews: 'அனைத்து மதிப்புரைகளையும் பார்க்க',
    showLess: 'குறைவாக காட்டு',
    reviewName: 'பெயர்',
    reviewNamePlaceholder: 'உங்கள் பெயரை உள்ளிடுங்கள்',
    reviewRating: 'மதிப்பீடு',
    reviewMessage: 'மதிப்புரை செய்தி',
    reviewMessagePlaceholder: 'இந்த தயாரிப்புடன் உங்கள் அனுபவத்தை பகிருங்கள்',
    submitReview: 'மதிப்புரை சமர்ப்பி',
    reviewSubmitted: 'மதிப்புரை வெற்றிகரமாக சமர்ப்பிக்கப்பட்டது',
    reviewNameRequired: 'பெயர் அவசியம்',
    reviewRatingRequired: 'மதிப்பீட்டைத் தேர்ந்தெடுக்கவும்',
    reviewMessageRequired: 'மதிப்புரை செய்தி அவசியம்',
    cancel: 'ரத்து செய்',
    totalReviews: 'மொத்த மதிப்புரைகள்',
    marketplaceTitle: 'இந்தியாவின் முன்னணி ஈ-காமர்ஸ் மார்க்கெட்ப்ளேஸ்',
    customers: 'வாடிக்கையாளர்கள்',
    brandStore: 'பிராண்ட் ஸ்டோர்',
    products: 'தயாரிப்புகள்',
    qualityAssurance: 'தர உறுதி',
    trustedDelivery: 'நம்பகமான டெலிவரி',
    afterSalesAssistance: 'விற்பனைக்குப் பிந்தைய உதவி',
    relatedProducts: 'தொடர்புடைய தயாரிப்புகள்',
  },
  ja: {
    defaultText: '詳細',
    freeShipping: '₹5000以上のご注文は送料無料！',
    searchProducts: '商品を検索...',
    search: '検索',
    searchEverything: '商品名で検索',
    searchResults: '検索結果',
    allResults: '件の結果',
    viewAllResults: 'すべての結果を見る',
    noSearchResults: '一致する結果がありません',
    startSearching: '商品名を入力してください',
    matchingProducts: '一致する商品',
    matchingCategories: '一致するカテゴリ',
    matchingSubcategories: '一致するサブカテゴリ',
    matchingPages: '一致するページ',
    pages: 'ページ',
    offers: 'オファー',
    shoppingCart: 'ショッピングカート',
    accountInfo: 'アカウント情報',
    myOrders: '注文履歴',
    wallet: 'ウォレット',
    signOut: 'サインアウト',
    emptyCart: 'カートに商品はありません。',
    subtotal: '小計:',
    viewCart: 'カートを見る',
    checkout: 'チェックアウト',
    home: 'ホーム',
    categories: 'カテゴリ',
    featured: 'おすすめ',
    menu: 'メニュー',
    language: '言語',
    myAccount: 'マイアカウント',
    signIn: 'サインイン',
    addToCart: 'カートに追加',
    youSave: '割引額',
    industrialGrade: '産業グレード',
    shyamAgroOriginal: 'Shyam Agro オリジナル',
    quantity: '数量',
    buyItNow: '今すぐ購入',
    freeDelivery: '無料配送',
    warranty: '1年保証',
    easyReturns: '簡単返品',
    addToWishlist: 'ウィッシュリストに追加',
    shareProduct: '商品を共有',
    description: '説明',
    specifications: '仕様',
    shortDescription: '概要',
    reviews: 'レビュー',
    technicalDetails: '技術詳細と使い方',
    keyFeatures: '主な特徴',
    packageIncludes: '同梱内容',
    cartItem: '商品',
    cartItems: '商品',
    remove: '削除',
    orderSummary: '注文概要',
    store: 'ストア',
    aboutProduct: '商品について',
    workItDoes: '用途',
    usageInstructions: '使用方法',
    suitableFor: '適した用途',
    noProductVideo: '商品動画はありません',
    mediaFront: '前面',
    mediaSide: '側面',
    mediaBack: '背面',
    mediaCloseup: 'クローズアップ',
    mediaTop: '上部',
    mediaTopView: '上部ビュー',
    mediaLeft: '左側面ビュー',
    mediaRight: '右側面ビュー',
    mediaVideo: '動画',
    previousMedia: '前のメディア',
    nextMedia: '次のメディア',
    viewDetails: '詳細を見る',
    addedToCart: 'カートに追加しました',
    addedToWishlist: 'ウィッシュリストに追加しました',
    removedFromWishlist: 'ウィッシュリストから削除しました',
    unableWishlist: 'ウィッシュリストを更新できません',
    productLinkCopied: '商品リンクをコピーしました',
    unableShare: '商品を共有できません',
    shoppingSession: 'ショッピングセッション',
    yourCart: 'あなたのカート',
    cartEmptyTitle: 'カートは現在空です',
    cartEmptyMessage: '農業ツールはまだ追加されていません。コレクションをご覧ください。',
    returnToShop: 'ショップに戻る',
    productDetails: '商品詳細',
    noProductsFound: '商品が見つかりません',
    price: '価格',
    sku: 'SKU',
    total: '合計',
    freeDeliveryMessage: '全国送料無料にするには₹5,000以上ご購入ください。',
    securePayment: '安全な支払い',
    securePaymentMessage: 'データは256-bit SSL暗号化で保護されています。',
    itemSubtotal: '商品小計',
    estimatedTax: '推定税額 (18%)',
    deliveryCharges: '配送料',
    free: '無料',
    coinsDiscount: 'コイン割引',
    couponCode: 'クーポンコード',
    apply: '適用',
    totalAmount: '合計金額',
    proceedToCheckout: 'チェックアウトへ進む',
    securePayments: '支払いは100%安全です',
    secureCheckout: '安全なチェックアウト',
    checkoutSubtitle: '配送先と請求情報を入力して購入を完了してください。',
    shippingInformation: '配送情報',
    firstName: '名',
    lastName: '姓',
    emailAddress: 'メールアドレス',
    phoneNumber: '電話番号',
    fullAddress: '住所',
    city: '市区町村',
    state: '州/県',
    pincode: '郵便番号',
    paymentMethod: '支払い方法',
    cashOnDelivery: '代金引換',
    payOnDelivery: '商品受け取り時に支払い',
    netBankingUpi: 'ネットバンキング / UPI',
    secureOnlinePayment: '安全なオンライン支払い',
    confirmPlaceOrder: '注文を確定',
    grandTotal: '総合計',
    ourCollections: 'コレクション',
    allCategories: 'すべてのカテゴリ',
    productPreview: '商品プレビュー',
    items: '商品',
    more: 'さらに',
    exploreCollection: 'コレクションを見る',
    savedProducts: '保存した商品',
    yourWishlist: 'ウィッシュリスト',
    wishlistEmptyTitle: 'ウィッシュリストは空です',
    wishlistEmptyMessage: 'お気に入りの農業ツールを保存して後で確認できます。',
    actions: '操作',
    quickLinks: 'クイックリンク',
    agricultureStore: '農業ストア',
    becomeSeller: '販売者になる',
    orderTracking: '注文追跡',
    agricultureBlog: '農業ブログ',
    customerService: 'カスタマーサービス',
    helpCenter: 'ヘルプセンター',
    contactUs: 'お問い合わせ',
    termsConditions: '利用規約',
    privacyPolicy: 'プライバシーポリシー',
    refundPolicy: '返金ポリシー',
    getInTouch: 'お問い合わせ',
    footerAbout: 'プロ向け農業機械と産業機器の主要マーケットプレイスです。',
    newsletter: '購読',
    yourEmail: 'メールアドレス',
    allRightsReserved: '全著作権所有',
    madeInIndia: 'インド製',
    codAvailable: '代金引換対応',
    estimatedDelivery: '配送予定',
    deliveryReturn: '配送と返品',
    countryOfOrigin: '原産国',
    brand: 'ブランド',
    mrp: 'MRP',
    stockAvailability: '在庫状況',
    inStock: '在庫あり',
    outOfStock: '在庫なし',
    reset: 'リセット',
    first: '最初',
    previous: '前へ',
    next: '次へ',
    last: '最後',
    reviewsRatings: 'レビューと評価',
    writeReview: 'レビューを書く',
    verifiedPurchase: '確認済み購入',
    viewAllReviews: 'すべてのレビューを見る',
    showLess: '少なく表示',
    reviewName: '名前',
    reviewNamePlaceholder: '名前を入力',
    reviewRating: '評価',
    reviewMessage: 'レビュー内容',
    reviewMessagePlaceholder: 'この商品の体験を共有してください',
    submitReview: 'レビューを送信',
    reviewSubmitted: 'レビューが送信されました',
    reviewNameRequired: '名前は必須です',
    reviewRatingRequired: '評価を選択してください',
    reviewMessageRequired: 'レビュー内容は必須です',
    cancel: 'キャンセル',
    totalReviews: 'レビュー合計',
    marketplaceTitle: 'インド有数のEコマースマーケットプレイス',
    customers: '顧客',
    brandStore: 'ブランドストア',
    products: '商品',
    qualityAssurance: '品質保証',
    trustedDelivery: '信頼できる配送',
    afterSalesAssistance: 'アフターサポート',
    relatedProducts: '関連商品',
  },
  zh: {
    defaultText: '详情',
    freeShipping: '所有满₹5000的订单免运费！',
    searchProducts: '搜索产品...',
    search: '搜索',
    searchEverything: '按产品名称搜索',
    searchResults: '搜索结果',
    allResults: '结果',
    viewAllResults: '查看全部结果',
    noSearchResults: '未找到匹配结果',
    startSearching: '请输入产品名称',
    matchingProducts: '匹配产品',
    matchingCategories: '匹配分类',
    matchingSubcategories: '匹配子分类',
    matchingPages: '匹配页面',
    pages: '页面',
    offers: '优惠',
    shoppingCart: '购物车',
    accountInfo: '账户信息',
    myOrders: '我的订单',
    wallet: '钱包',
    signOut: '退出登录',
    emptyCart: '您的购物车中没有商品。',
    subtotal: '小计:',
    viewCart: '查看购物车',
    checkout: '结账',
    home: '首页',
    categories: '分类',
    featured: '精选',
    menu: '菜单',
    language: '语言',
    myAccount: '我的账户',
    signIn: '登录',
    addToCart: '加入购物车',
    youSave: '您节省',
    industrialGrade: '工业级',
    shyamAgroOriginal: 'Shyam Agro 原装',
    quantity: '数量',
    buyItNow: '立即购买',
    freeDelivery: '免费配送',
    warranty: '1年保修',
    easyReturns: '轻松退货',
    addToWishlist: '加入愿望清单',
    shareProduct: '分享产品',
    description: '描述',
    specifications: '规格',
    shortDescription: '简短描述',
    reviews: '评价',
    technicalDetails: '技术详情和用法',
    keyFeatures: '主要特点',
    packageIncludes: '包装包含',
    cartItem: '商品',
    cartItems: '商品',
    remove: '移除',
    orderSummary: '订单摘要',
    store: '商店',
    aboutProduct: '关于产品',
    workItDoes: '用途',
    usageInstructions: '使用说明',
    suitableFor: '适用于',
    noProductVideo: '暂无产品视频',
    mediaFront: '正面',
    mediaSide: '侧面',
    mediaBack: '背面',
    mediaCloseup: '特写',
    mediaTop: '顶部',
    mediaTopView: '俯视图',
    mediaLeft: '左视图',
    mediaRight: '右视图',
    mediaVideo: '视频',
    previousMedia: '上一个媒体',
    nextMedia: '下一个媒体',
    viewDetails: '查看详情',
    addedToCart: '已加入购物车',
    addedToWishlist: '已加入愿望清单',
    removedFromWishlist: '已从愿望清单移除',
    unableWishlist: '无法更新愿望清单',
    productLinkCopied: '产品链接已复制',
    unableShare: '无法分享产品',
    shoppingSession: '购物会话',
    yourCart: '您的购物车',
    cartEmptyTitle: '您的购物车当前为空',
    cartEmptyMessage: '您还没有添加农业工具。请浏览我们的产品系列。',
    returnToShop: '返回商店',
    productDetails: '产品详情',
    noProductsFound: '未找到产品',
    price: '价格',
    sku: 'SKU',
    total: '总计',
    freeDeliveryMessage: '再购买满₹5,000即可享受全国免费配送。',
    securePayment: '安全支付',
    securePaymentMessage: '您的数据受256-bit SSL加密保护。',
    itemSubtotal: '商品小计',
    estimatedTax: '预计税费 (18%)',
    deliveryCharges: '配送费',
    free: '免费',
    coinsDiscount: '金币折扣',
    couponCode: '优惠码',
    apply: '应用',
    totalAmount: '总金额',
    proceedToCheckout: '前往结账',
    securePayments: '支付100%安全',
    secureCheckout: '安全结账',
    checkoutSubtitle: '填写配送和账单信息以完成购买。',
    shippingInformation: '配送信息',
    firstName: '名',
    lastName: '姓',
    emailAddress: '电子邮箱',
    phoneNumber: '电话号码',
    fullAddress: '完整地址',
    city: '城市',
    state: '省/州',
    pincode: '邮政编码',
    paymentMethod: '支付方式',
    cashOnDelivery: '货到付款',
    payOnDelivery: '收货时付款',
    netBankingUpi: '网银 / UPI',
    secureOnlinePayment: '安全在线支付',
    confirmPlaceOrder: '确认下单',
    grandTotal: '总金额',
    ourCollections: '我们的系列',
    allCategories: '所有分类',
    productPreview: '产品预览',
    items: '商品',
    more: '更多',
    exploreCollection: '浏览系列',
    savedProducts: '已保存产品',
    yourWishlist: '您的愿望清单',
    wishlistEmptyTitle: '您的愿望清单为空',
    wishlistEmptyMessage: '保存喜欢的农业工具，方便以后查看。',
    actions: '操作',
    quickLinks: '快捷链接',
    agricultureStore: '农业商店',
    becomeSeller: '成为卖家',
    orderTracking: '订单追踪',
    agricultureBlog: '农业博客',
    customerService: '客户服务',
    helpCenter: '帮助中心',
    contactUs: '联系我们',
    termsConditions: '条款与条件',
    privacyPolicy: '隐私政策',
    refundPolicy: '退款政策',
    getInTouch: '取得联系',
    footerAbout: '专业农业机械和工业设备的领先市场。',
    newsletter: '订阅',
    yourEmail: '您的邮箱',
    allRightsReserved: '版权所有',
    madeInIndia: '印度制造',
    codAvailable: '支持货到付款',
    estimatedDelivery: '预计送达',
    deliveryReturn: '配送与退货',
    countryOfOrigin: '原产国',
    brand: '品牌',
    mrp: 'MRP',
    stockAvailability: '库存状态',
    inStock: '有货',
    outOfStock: '缺货',
    reset: '重置',
    first: '首页',
    previous: '上一页',
    next: '下一页',
    last: '末页',
    reviewsRatings: '评价与评分',
    writeReview: '写评价',
    verifiedPurchase: '已验证购买',
    viewAllReviews: '查看全部评价',
    showLess: '收起',
    reviewName: '姓名',
    reviewNamePlaceholder: '输入您的姓名',
    reviewRating: '评分',
    reviewMessage: '评价内容',
    reviewMessagePlaceholder: '分享您对该产品的体验',
    submitReview: '提交评价',
    reviewSubmitted: '评价提交成功',
    reviewNameRequired: '姓名为必填项',
    reviewRatingRequired: '请选择评分',
    reviewMessageRequired: '评价内容为必填项',
    cancel: '取消',
    totalReviews: '评价总数',
    marketplaceTitle: '印度领先的电子商务市场',
    customers: '客户',
    brandStore: '品牌商店',
    products: '产品',
    qualityAssurance: '质量保证',
    trustedDelivery: '可信配送',
    afterSalesAssistance: '售后支持',
    relatedProducts: '相关产品',
  },
};

const completeTranslations = (locale) => {
  const profile = localeTranslationProfiles[locale] || {};
  return Object.fromEntries(
    Object.keys(translations.en).map((key) => [key, profile[key] || profile.defaultText || translations.en[key]])
  );
};

Object.assign(localeTranslationProfiles, {
  fr: {
    freeShipping: 'LIVRAISON GRATUITE POUR TOUTE COMMANDE DE PLUS DE ₹5000 !',
    searchProducts: 'Rechercher des produits...',
    search: 'Rechercher',
    searchEverything: 'Rechercher par nom de produit',
    searchResults: 'Résultats de recherche',
    allResults: 'résultats pour',
    viewAllResults: 'Voir tous les résultats',
    noSearchResults: 'Aucun résultat correspondant',
    startSearching: 'Commencez à saisir un nom de produit',
    matchingProducts: 'Produits correspondants',
    matchingCategories: 'Catégories correspondantes',
    matchingSubcategories: 'Sous-catégories correspondantes',
    matchingPages: 'Pages correspondantes',
    pages: 'Pages',
    offers: 'Offres',
    shoppingCart: 'Panier',
    accountInfo: 'Informations du compte',
    myOrders: 'Mes commandes',
    wallet: 'Portefeuille',
    signOut: 'Déconnexion',
    emptyCart: 'Votre panier ne contient aucun article.',
    subtotal: 'Sous-total :',
    viewCart: 'Voir le panier',
    checkout: 'Paiement',
    home: 'ACCUEIL',
    categories: 'CATÉGORIES',
    featured: 'EN VEDETTE',
    menu: 'MENU',
    language: 'Langue',
    myAccount: 'MON COMPTE',
    signIn: 'CONNEXION',
    addToCart: 'Ajouter au panier',
    youSave: 'Vous économisez',
    industrialGrade: 'Qualité industrielle',
    shyamAgroOriginal: 'Shyam Agro Original',
    quantity: 'Quantité',
    buyItNow: 'Acheter maintenant',
    freeDelivery: 'Livraison gratuite',
    warranty: 'Garantie 1 an',
    easyReturns: 'Retours faciles',
    addToWishlist: 'Ajouter aux favoris',
    shareProduct: 'Partager le produit',
    description: 'Description',
    specifications: 'Spécifications',
    shortDescription: 'Description courte',
    reviews: 'Avis',
    technicalDetails: 'Détails techniques et utilisation',
    keyFeatures: 'Caractéristiques clés',
    packageIncludes: 'Inclus dans le colis',
    cartItem: 'article',
    cartItems: 'articles',
    remove: 'Retirer',
    orderSummary: 'Résumé de commande',
    store: 'Boutique',
    aboutProduct: 'À propos du produit',
    workItDoes: 'Travail effectué',
    usageInstructions: "Mode d'emploi",
    suitableFor: 'Convient pour',
    noProductVideo: 'Vidéo du produit non disponible',
    mediaFront: 'Avant',
    mediaSide: 'Côté',
    mediaBack: 'Arrière',
    mediaCloseup: 'Gros plan',
    mediaTop: 'Dessus',
    mediaTopView: 'Vue de dessus',
    mediaLeft: 'Vue gauche',
    mediaRight: 'Vue droite',
    mediaVideo: 'Vidéo',
    previousMedia: 'Média précédent',
    nextMedia: 'Média suivant',
    viewDetails: 'Voir les détails',
    addedToCart: 'ajouté au panier',
    addedToWishlist: 'Ajouté aux favoris',
    removedFromWishlist: 'Retiré des favoris',
    unableWishlist: 'Impossible de mettre à jour les favoris',
    productLinkCopied: 'Lien du produit copié',
    unableShare: 'Impossible de partager le produit',
    shoppingSession: 'Session d’achat',
    yourCart: 'Votre panier',
    cartEmptyTitle: 'Votre panier est vide',
    cartEmptyMessage: 'Vous n’avez pas encore ajouté d’outils agricoles. Découvrez nos collections.',
    returnToShop: 'Retour à la boutique',
    productDetails: 'Détails du produit',
    noProductsFound: 'Aucun produit trouvé',
    price: 'Prix',
    sku: 'SKU',
    total: 'Total',
    freeDeliveryMessage: 'Dépensez ₹5 000 de plus pour obtenir la livraison gratuite.',
    securePayment: 'Paiement sécurisé',
    securePaymentMessage: 'Vos données sont protégées par un chiffrement SSL 256 bits.',
    itemSubtotal: 'Sous-total article',
    estimatedTax: 'Taxe estimée (18 %)',
    deliveryCharges: 'Frais de livraison',
    free: 'Gratuit',
    coinsDiscount: 'Remise en pièces',
    couponCode: 'Code coupon',
    apply: 'Appliquer',
    totalAmount: 'Montant total',
    proceedToCheckout: 'Passer au paiement',
    securePayments: 'Paiements 100 % sécurisés',
    secureCheckout: 'Paiement sécurisé',
    checkoutSubtitle: 'Terminez votre achat avec vos informations de livraison et de facturation.',
    shippingInformation: 'Informations de livraison',
    firstName: 'Prénom',
    lastName: 'Nom',
    emailAddress: 'Adresse e-mail',
    phoneNumber: 'Numéro de téléphone',
    fullAddress: 'Adresse complète',
    city: 'Ville',
    state: 'État',
    pincode: 'Code postal',
    paymentMethod: 'Méthode de paiement',
    cashOnDelivery: 'Paiement à la livraison',
    payOnDelivery: 'Payez à la réception des outils',
    netBankingUpi: 'Banque en ligne / UPI',
    secureOnlinePayment: 'Paiement en ligne sécurisé',
    confirmPlaceOrder: 'Confirmer et commander',
    grandTotal: 'Total général',
    ourCollections: 'Nos collections',
    allCategories: 'Toutes les catégories',
    productPreview: 'Aperçu du produit',
    items: 'Articles',
    more: 'Plus',
    exploreCollection: 'Explorer la collection',
    savedProducts: 'Produits enregistrés',
    yourWishlist: 'Vos favoris',
    wishlistEmptyTitle: 'Votre liste de favoris est vide',
    wishlistEmptyMessage: 'Enregistrez vos outils agricoles préférés pour les retrouver plus tard.',
    actions: 'Actions',
    quickLinks: 'Liens rapides',
    agricultureStore: 'Boutique agricole',
    becomeSeller: 'Devenir vendeur',
    orderTracking: 'Suivi de commande',
    agricultureBlog: 'Blog agricole',
    customerService: 'Service client',
    helpCenter: 'Centre d’aide',
    contactUs: 'Nous contacter',
    termsConditions: 'Conditions générales',
    privacyPolicy: 'Politique de confidentialité',
    refundPolicy: 'Politique de remboursement',
    getInTouch: 'Contactez-nous',
    footerAbout: 'Marketplace leader pour machines agricoles professionnelles et équipements industriels.',
    newsletter: 'S’abonner',
    yourEmail: 'Votre e-mail',
    allRightsReserved: 'Tous droits réservés',
    madeInIndia: 'Fabriqué en Inde',
    codAvailable: 'Paiement à la livraison disponible',
    estimatedDelivery: 'Livraison estimée',
    deliveryReturn: 'Livraison et retour',
    countryOfOrigin: 'Pays d’origine',
    brand: 'Marque',
    mrp: 'MRP',
    stockAvailability: 'Disponibilité',
    inStock: 'En stock',
    outOfStock: 'Rupture de stock',
    reset: 'Réinitialiser',
    first: 'Premier',
    previous: 'Précédent',
    next: 'Suivant',
    last: 'Dernier',
    reviewsRatings: 'Avis et notes',
    writeReview: 'Écrire un avis',
    verifiedPurchase: 'Achat vérifié',
    viewAllReviews: 'Voir tous les avis',
    showLess: 'Afficher moins',
    reviewName: 'Nom',
    reviewNamePlaceholder: 'Entrez votre nom',
    reviewRating: 'Note',
    reviewMessage: 'Message d’avis',
    reviewMessagePlaceholder: 'Partagez votre expérience avec ce produit',
    submitReview: 'Envoyer l’avis',
    reviewSubmitted: 'Avis envoyé avec succès',
    reviewNameRequired: 'Le nom est requis',
    reviewRatingRequired: 'Veuillez sélectionner une note',
    reviewMessageRequired: 'Le message est requis',
    cancel: 'Annuler',
    totalReviews: 'Total des avis',
    marketplaceTitle: 'Marketplace e-commerce leader en Inde',
    customers: 'Clients',
    brandStore: 'Boutique de marque',
    products: 'Produits',
    qualityAssurance: 'Assurance qualité',
    trustedDelivery: 'Livraison fiable',
    afterSalesAssistance: 'Assistance après-vente',
    relatedProducts: 'Produits associés',
    unableAddProductToCart: 'Impossible d’ajouter le produit au panier',
    addedToCartStandalone: 'Ajouté au panier',
    signedOutSuccessfully: 'Déconnexion réussie',
    invalidCouponCode: 'Code coupon invalide',
    couponAlreadyApplied: 'Coupon déjà appliqué',
    couponAppliedSuccessfully: 'Coupon appliqué avec succès',
    couponDiscount: 'Remise coupon',
    sortBy: 'Trier par',
    filterProducts: 'Filtrer les produits',
    subCategories: 'Sous-catégories',
    allProducts: 'Tous les produits',
    filterByPrice: 'Filtrer par prix',
    allPrices: 'Tous les prix',
    under1000: 'Moins de ₹1 000',
    price1000To5000: '₹1 000 - ₹5 000',
    over5000: 'Plus de ₹5 000',
    showing: 'Affichage',
    tryAdjustingFilters: 'Essayez d’ajuster vos filtres ou votre recherche.',
    productUnavailable: 'Ce produit est actuellement indisponible',
  },
});

const copyLocaleProfile = (source, replacements) =>
  Object.fromEntries(
    Object.entries(source).map(([key, value]) => [key, replacements[key] || value])
  );

Object.assign(localeTranslationProfiles, {
  pt: copyLocaleProfile(localeTranslationProfiles.fr, {
    language: 'Idioma',
    home: 'INÍCIO',
    categories: 'CATEGORIAS',
    featured: 'DESTAQUES',
    searchProducts: 'Pesquisar produtos...',
    search: 'Pesquisar',
    shoppingCart: 'Carrinho',
    myOrders: 'Meus pedidos',
    signOut: 'Sair',
    viewCart: 'Ver carrinho',
    checkout: 'Finalizar compra',
    addToCart: 'Adicionar ao carrinho',
    buyItNow: 'Comprar agora',
    productDetails: 'Detalhes do produto',
    description: 'Descrição',
    specifications: 'Especificações',
    shortDescription: 'Descrição curta',
    keyFeatures: 'Principais recursos',
    reviews: 'Avaliações',
    price: 'Preço',
    inStock: 'Em estoque',
    outOfStock: 'Fora de estoque',
    relatedProducts: 'Produtos relacionados',
    allCategories: 'Todas as categorias',
    allProducts: 'Todos os produtos',
    filterProducts: 'Filtrar produtos',
    subCategories: 'Subcategorias',
    filterByPrice: 'Filtrar por preço',
    readMore: 'Ler mais',
    returnToShop: 'Voltar à loja',
    yourCart: 'Seu carrinho',
    yourWishlist: 'Sua lista de desejos',
    agricultureStore: 'Loja agrícola',
    agricultureBlog: 'Blog agrícola',
    customerService: 'Atendimento ao cliente',
    contactUs: 'Fale conosco',
    free: 'Grátis',
    apply: 'Aplicar',
    cancel: 'Cancelar',
    next: 'Próximo',
    previous: 'Anterior',
    productUnavailable: 'Este produto está indisponível no momento',
  }),
  ar: copyLocaleProfile(localeTranslationProfiles.fr, {
    language: 'اللغة',
    home: 'الرئيسية',
    categories: 'الفئات',
    featured: 'المميزة',
    searchProducts: 'ابحث عن المنتجات...',
    search: 'بحث',
    shoppingCart: 'سلة التسوق',
    myOrders: 'طلباتي',
    signOut: 'تسجيل الخروج',
    viewCart: 'عرض السلة',
    checkout: 'الدفع',
    addToCart: 'أضف إلى السلة',
    buyItNow: 'اشتر الآن',
    productDetails: 'تفاصيل المنتج',
    description: 'الوصف',
    specifications: 'المواصفات',
    shortDescription: 'وصف مختصر',
    keyFeatures: 'الميزات الرئيسية',
    reviews: 'المراجعات',
    price: 'السعر',
    inStock: 'متوفر',
    outOfStock: 'غير متوفر',
    relatedProducts: 'منتجات ذات صلة',
    allCategories: 'كل الفئات',
    allProducts: 'كل المنتجات',
    filterProducts: 'تصفية المنتجات',
    subCategories: 'الفئات الفرعية',
    filterByPrice: 'تصفية حسب السعر',
    returnToShop: 'العودة إلى المتجر',
    yourCart: 'سلتك',
    yourWishlist: 'قائمة الرغبات',
    agricultureStore: 'متجر زراعي',
    agricultureBlog: 'مدونة زراعية',
    customerService: 'خدمة العملاء',
    contactUs: 'اتصل بنا',
    free: 'مجاني',
    apply: 'تطبيق',
    cancel: 'إلغاء',
    next: 'التالي',
    previous: 'السابق',
    productUnavailable: 'هذا المنتج غير متوفر حالياً',
  }),
  ko: copyLocaleProfile(localeTranslationProfiles.fr, {
    language: '언어',
    home: '홈',
    categories: '카테고리',
    featured: '추천',
    searchProducts: '제품 검색...',
    search: '검색',
    shoppingCart: '장바구니',
    myOrders: '내 주문',
    signOut: '로그아웃',
    viewCart: '장바구니 보기',
    checkout: '결제',
    addToCart: '장바구니에 추가',
    buyItNow: '바로 구매',
    productDetails: '제품 상세정보',
    description: '설명',
    specifications: '사양',
    shortDescription: '간단 설명',
    keyFeatures: '주요 기능',
    reviews: '리뷰',
    price: '가격',
    inStock: '재고 있음',
    outOfStock: '품절',
    relatedProducts: '관련 제품',
    allCategories: '모든 카테고리',
    allProducts: '모든 제품',
    filterProducts: '제품 필터',
    subCategories: '하위 카테고리',
    filterByPrice: '가격별 필터',
    returnToShop: '상점으로 돌아가기',
    yourCart: '내 장바구니',
    yourWishlist: '내 위시리스트',
    agricultureStore: '농업 상점',
    agricultureBlog: '농업 블로그',
    customerService: '고객 서비스',
    contactUs: '문의하기',
    free: '무료',
    apply: '적용',
    cancel: '취소',
    next: '다음',
    previous: '이전',
    productUnavailable: '현재 이 제품은 사용할 수 없습니다',
  }),
});

Object.assign(translations.te, localeTranslationProfiles.te);
Object.assign(translations.hi, localeTranslationProfiles.hi);
translations.ta = completeTranslations('ta');
translations.ja = completeTranslations('ja');
translations.zh = completeTranslations('zh');
translations.kn = completeTranslations('kn');
translations.fr = completeTranslations('fr');
translations.pt = completeTranslations('pt');
translations.ar = completeTranslations('ar');
translations.ko = completeTranslations('ko');

const subcategoryProfiles = {
  fr: {
    tractors: 'Tracteurs',
    harvesters: 'Moissonneuses-batteuses',
    reapersBinders: 'Faucheuses et lieuses',
    cultivatorPlow: 'Cultivateurs et charrues',
    powerTillers: 'Motoculteurs',
    cuttersWeeders: 'Coupeuses et désherbeurs',
    seedersCovers: 'Semoirs et bâches agricoles',
    solarTools: 'Outils solaires agricoles',
    dripSystems: 'Systèmes goutte-à-goutte',
    waterPumps: 'Pompes à eau',
    sprinklersRainGuns: 'Arroseurs et canons à pluie',
    pipesFittings: 'Tuyaux et raccords',
    industrialWrenches: 'Clés industrielles',
    powerDrills: 'Perceuses électriques',
    precisionCutters: 'Coupeuses de précision',
    droneSprayers: 'Pulvérisateurs drones',
    batterySprayers: 'Pulvérisateurs à batterie',
    manualSprayers: 'Pulvérisateurs manuels',
    engineSprayers: 'Pulvérisateurs moteur',
    sprayerPumpsFoggers: 'Pompes et brumisateurs',
    chaffCutters: 'Hache-paille',
    grainMills: 'Moulins à grains',
    oilExtractors: 'Extracteurs d’huile',
    dehydrators: 'Déshydrateurs',
    vegetableSeeds: 'Graines de légumes',
    flowerSeeds: 'Graines de fleurs',
    hybridSeeds: 'Graines hybrides',
    organicFertilizers: 'Engrais organiques',
    chemicalFertilizers: 'Engrais chimiques',
    cultivator: 'Cultivateur',
    plow: 'Charrue',
    sprayer: 'Pulvérisateur',
  },
  pt: {
    tractors: 'Tratores',
    harvesters: 'Colheitadeiras',
    reapersBinders: 'Ceifadeiras e amarradoras',
    cultivatorPlow: 'Cultivador e arado',
    powerTillers: 'Motocultivadores',
    cuttersWeeders: 'Cortadores e capinadeiras',
    seedersCovers: 'Semeadoras e coberturas',
    solarTools: 'Ferramentas solares agrícolas',
    dripSystems: 'Sistemas de gotejamento',
    waterPumps: 'Bombas de água',
    sprinklersRainGuns: 'Aspersores e canhões de chuva',
    pipesFittings: 'Tubos e conexões',
    industrialWrenches: 'Chaves industriais',
    powerDrills: 'Furadeiras elétricas',
    precisionCutters: 'Cortadores de precisão',
    droneSprayers: 'Pulverizadores drone',
    batterySprayers: 'Pulverizadores a bateria',
    manualSprayers: 'Pulverizadores manuais',
    engineSprayers: 'Pulverizadores a motor',
    sprayerPumpsFoggers: 'Bombas e nebulizadores',
    chaffCutters: 'Picadores de forragem',
    grainMills: 'Moinhos de grãos',
    oilExtractors: 'Extratores de óleo',
    dehydrators: 'Desidratadores',
    vegetableSeeds: 'Sementes de vegetais',
    flowerSeeds: 'Sementes de flores',
    hybridSeeds: 'Sementes híbridas',
    organicFertilizers: 'Fertilizantes orgânicos',
    chemicalFertilizers: 'Fertilizantes químicos',
    cultivator: 'Cultivador',
    plow: 'Arado',
    sprayer: 'Pulverizador',
  },
  ar: {
    tractors: 'جرارات',
    harvesters: 'حصادات',
    reapersBinders: 'حاصدات ورباطات',
    cultivatorPlow: 'محراث ومزارع',
    powerTillers: 'عزاقات آلية',
    cuttersWeeders: 'قواطع ومزيلات الأعشاب',
    seedersCovers: 'بذارات وأغطية زراعية',
    solarTools: 'أدوات زراعية شمسية',
    dripSystems: 'أنظمة التنقيط',
    waterPumps: 'مضخات المياه',
    sprinklersRainGuns: 'رشاشات ومدافع مطر',
    pipesFittings: 'أنابيب ووصلات',
    industrialWrenches: 'مفاتيح صناعية',
    powerDrills: 'مثاقب كهربائية',
    precisionCutters: 'قواطع دقيقة',
    droneSprayers: 'رشاشات درون',
    batterySprayers: 'رشاشات بطارية',
    manualSprayers: 'رشاشات يدوية',
    engineSprayers: 'رشاشات بمحرك',
    sprayerPumpsFoggers: 'مضخات ورشاشات ضباب',
    chaffCutters: 'قاطعات علف',
    grainMills: 'مطاحن حبوب',
    oilExtractors: 'مستخرجات زيت',
    dehydrators: 'مجففات',
    vegetableSeeds: 'بذور خضار',
    flowerSeeds: 'بذور زهور',
    hybridSeeds: 'بذور هجينة',
    organicFertilizers: 'أسمدة عضوية',
    chemicalFertilizers: 'أسمدة كيميائية',
    cultivator: 'مزارع',
    plow: 'محراث',
    sprayer: 'رشاش',
  },
  ko: {
    tractors: '트랙터',
    harvesters: '콤바인 수확기',
    reapersBinders: '예취기 및 결속기',
    cultivatorPlow: '경운기 및 쟁기',
    powerTillers: '동력 경운기',
    cuttersWeeders: '절단기 및 제초기',
    seedersCovers: '파종기 및 농장 커버',
    solarTools: '농업용 태양광 도구',
    dripSystems: '점적 관개 시스템',
    waterPumps: '물 펌프',
    sprinklersRainGuns: '스프링클러 및 레인건',
    pipesFittings: '파이프 및 피팅',
    industrialWrenches: '산업용 렌치',
    powerDrills: '전동 드릴',
    precisionCutters: '정밀 절단기',
    droneSprayers: '드론 분무기',
    batterySprayers: '배터리 분무기',
    manualSprayers: '수동 분무기',
    engineSprayers: '엔진 분무기',
    sprayerPumpsFoggers: '분무 펌프 및 포거',
    chaffCutters: '사료 절단기',
    grainMills: '곡물 분쇄기',
    oilExtractors: '오일 추출기',
    dehydrators: '건조기',
    vegetableSeeds: '채소 씨앗',
    flowerSeeds: '꽃 씨앗',
    hybridSeeds: '하이브리드 씨앗',
    organicFertilizers: '유기 비료',
    chemicalFertilizers: '화학 비료',
    cultivator: '경운기',
    plow: '쟁기',
    sprayer: '분무기',
  },
};

Object.entries(subcategoryProfiles).forEach(([locale, subcategories]) => {
  translations[locale].subcategories = subcategories;
});

Object.entries(i18nMessages).forEach(([locale, messages]) => {
  translations[locale] = {
    ...translations.en,
    ...(translations[locale] || {}),
    ...messages,
  };
});

Object.assign(translations.en, {
  searchCategoriesProducts: 'Search categories, products...',
  searchCategoriesProductsPages: 'Search categories, products and pages',
  paymentInformation: 'Payment Information',
  paymentInformationDescription: 'Complete your payment through a protected payment form. Your transaction details are encrypted and used only to confirm this order.',
  sslSecuredPaymentSession: 'SSL secured payment session',
  cardUpiDetailsProtected: 'Card and UPI details stay protected',
  needHelp: 'Need Help?',
  refreshingPaymentDetails: 'Refreshing payment details from backend...',
  payUsingUpi: 'Pay using UPI',
  merchantUpiId: 'Merchant UPI ID',
  linkedBank: 'Linked Bank',
  currency: 'Currency',
  upiIdCopied: 'UPI ID Copied',
  copyUpiId: 'Copy UPI ID',
  completePaymentUsingUpi: 'Complete the payment using your preferred UPI application.',
  accountHolderName: 'Account Holder Name',
  accountNumber: 'Account Number',
  ifscCode: 'IFSC Code',
  branch: 'Branch',
  transferUsingBank: 'Transfer the amount using NEFT / IMPS / RTGS.',
  manualPaymentVerification: 'Manual Payment Verification',
  manualPaymentVerificationDescription: 'Enter the payment details after completing the transfer externally.',
  remarksOptional: 'Remarks (Optional)',
  additionalPaymentNotes: 'Add any additional payment notes',
  orderIdAutoGenerated: 'Order ID will be generated automatically',
  transactionUtrNumber: 'Transaction / UTR Number',
  enterUtrReferenceId: 'Enter UTR or reference ID',
  amountPaid: 'Amount Paid',
  paymentDate: 'Payment Date',
  paymentTime: 'Payment Time',
  customerName: 'Customer Name',
  enterCustomerName: 'Enter customer name',
  tenDigitMobileNumber: '10 digit mobile number',
  uploadPaymentScreenshot: 'Upload Payment Screenshot',
  chooseFile: 'Choose File',
  capturePhoto: 'Capture Photo',
  capturePaymentScreenshot: 'Capture payment screenshot',
  selectedPaymentScreenshotPreview: 'Selected payment screenshot preview',
  submitting: 'Submitting...',
  submitVerification: 'Submit Verification',
  paymentVerificationSubmitted: 'Payment Verification Submitted Successfully',
  paymentVerificationPendingMessage: 'Verification is currently pending. Once verified, your order status will be updated.',
  referenceNumber: 'Reference Number',
  submissionDate: 'Submission Date',
  paymentStatus: 'Payment Status',
  placingOrder: 'Placing Order...',
  placeCodOrder: 'Place COD Order',
  scanQrCode: 'Scan QR Code',
  generatingSecureQr: 'Generating Secure QR...',
  amount: 'Amount',
  qrUpdated: 'QR Updated',
  scanQrUsingAnyUpi: 'Scan the QR code using any UPI application and complete the payment.',
  payUsingDebitCreditCard: 'Pay using Debit / Credit Card',
  payUsingCreditCard: 'Pay using Credit Card',
  cardPaymentsUnavailable: 'Card payments are currently unavailable.',
  cardHolderName: 'Card Holder Name',
  enterNameOnCard: 'Enter name on card',
  expiryMonth: 'Expiry Month',
  expiryYear: 'Expiry Year',
  month: 'Month',
  year: 'Year',
  saveThisCard: 'Save this card',
  saveCard: 'Save Card',
  pay: 'Pay',
  payCashOnDeliveryDescription: 'Pay in cash when your order is delivered to your address.',
  orderIdRequired: 'Order ID is required.',
  orderIdFormat: 'Use 3 to 50 letters or numbers. Hyphen, underscore, and slash are allowed.',
  transactionUtrRequired: 'Transaction / UTR Number is required.',
  transactionUtrFormat: 'Use 12 to 22 letters or numbers (minimum 12, maximum 22).',
  amountPaidRequired: 'Amount Paid is required.',
  amountPaidMustEqual: 'Amount Paid must equal',
  paymentDateRequired: 'Payment Date is required.',
  paymentDateFuture: 'Payment Date cannot be in the future.',
  paymentTimeRequired: 'Payment Time is required.',
  customerNameRequired: 'Customer Name is required.',
  customerNameInvalid: 'Customer Name must contain only letters, spaces, dots, or hyphens (2-50 characters).',
  mobileNumberRequired: 'Mobile Number is required.',
  paymentScreenshotRequired: 'Payment screenshot is required.',
  paymentScreenshotFileType: 'Upload a JPG, JPEG, PNG, or PDF file.',
  paymentScreenshotFileSize: 'Attachment must be 5 MB or smaller.',
  cameraStillLoading: 'Camera is still loading. Please try again.',
  unableCapturePhoto: 'Unable to capture photo. Please try again.',
  profile: 'Profile',
  editProfile: 'Edit Profile',
  loadingLatestProfile: 'Loading latest profile...',
  closeProfile: 'Close profile',
  changePhoto: 'Change Photo',
  personalDetails: 'Personal Details',
  fullName: 'Full Name',
  enterFullName: 'Enter your full name',
  enterEmailAddress: 'Enter your email address',
  mobileNumber: 'Mobile Number',
  mobileNumberCannotBeEdited: 'Mobile number cannot be edited.',
  addressInformation: 'Address Information',
  doorNoHouseNo: 'Door No / House No',
  enterDoorHouseNumber: 'Enter door or house number',
  streetArea: 'Street / Area',
  enterStreetArea: 'Enter street or area',
  enterCity: 'Enter city',
  enterState: 'Enter state',
  enterPincode: 'Enter pincode',
  saving: 'Saving...',
  saveProfile: 'Save Profile',
  showingSavedProfileDetails: 'Showing saved profile details.',
  validEmailAddress: 'Enter a valid email address.',
  mobileNumberTenDigits: 'Mobile number must be 10 digits.',
  profileUpdated: 'Profile updated.',
  unableUpdateProfile: 'Unable to update profile.',
  unsupportedProfileImageType: 'Please select a JPG, PNG, WEBP, or GIF image.',
  profileImageTooLarge: 'Profile image must be smaller than 2MB.',
});

Object.assign(translations.te, {
  searchCategoriesProducts: 'వర్గాలు, ఉత్పత్తులను వెతకండి...',
  searchCategoriesProductsPages: 'వర్గాలు, ఉత్పత్తులు మరియు పేజీలను వెతకండి',
  paymentInformation: 'చెల్లింపు సమాచారం',
  paymentInformationDescription: 'రక్షిత చెల్లింపు ఫారమ్ ద్వారా మీ చెల్లింపును పూర్తి చేయండి. మీ లావాదేవీ వివరాలు గుప్తీకరించబడి ఈ ఆర్డర్‌ను నిర్ధారించడానికి మాత్రమే ఉపయోగించబడతాయి.',
  sslSecuredPaymentSession: 'SSL సురక్షిత చెల్లింపు సెషన్',
  cardUpiDetailsProtected: 'కార్డ్ మరియు UPI వివరాలు సురక్షితంగా ఉంటాయి',
  needHelp: 'సహాయం కావాలా?',
  refreshingPaymentDetails: 'బ్యాకెండ్ నుండి చెల్లింపు వివరాలు నవీకరించబడుతున్నాయి...',
  payUsingUpi: 'UPI ద్వారా చెల్లించండి',
  merchantUpiId: 'వ్యాపారి UPI ID',
  linkedBank: 'లింక్ చేసిన బ్యాంక్',
  currency: 'కరెన్సీ',
  upiIdCopied: 'UPI ID కాపీ అయింది',
  copyUpiId: 'UPI ID కాపీ చేయండి',
  completePaymentUsingUpi: 'మీకు ఇష్టమైన UPI అప్లికేషన్ ద్వారా చెల్లింపును పూర్తి చేయండి.',
  accountHolderName: 'ఖాతాదారుని పేరు',
  accountNumber: 'ఖాతా సంఖ్య',
  ifscCode: 'IFSC కోడ్',
  branch: 'శాఖ',
  transferUsingBank: 'NEFT / IMPS / RTGS ద్వారా మొత్తాన్ని బదిలీ చేయండి.',
  manualPaymentVerification: 'మాన్యువల్ చెల్లింపు ధృవీకరణ',
  manualPaymentVerificationDescription: 'బయట చెల్లింపు పూర్తిచేసిన తర్వాత చెల్లింపు వివరాలను నమోదు చేయండి.',
  remarksOptional: 'గమనికలు (ఐచ్చికం)',
  additionalPaymentNotes: 'అదనపు చెల్లింపు గమనికలు జోడించండి',
  orderIdAutoGenerated: 'ఆర్డర్ ID స్వయంచాలకంగా రూపొందించబడుతుంది',
  transactionUtrNumber: 'లావాదేవీ / UTR నంబర్',
  enterUtrReferenceId: 'UTR లేదా రిఫరెన్స్ ID నమోదు చేయండి',
  amountPaid: 'చెల్లించిన మొత్తం',
  paymentDate: 'చెల్లింపు తేదీ',
  paymentTime: 'చెల్లింపు సమయం',
  customerName: 'కస్టమర్ పేరు',
  enterCustomerName: 'కస్టమర్ పేరును నమోదు చేయండి',
  tenDigitMobileNumber: '10 అంకెల మొబైల్ నంబర్',
  uploadPaymentScreenshot: 'చెల్లింపు స్క్రీన్‌షాట్ అప్‌లోడ్ చేయండి',
  chooseFile: 'ఫైల్ ఎంచుకోండి',
  capturePhoto: 'ఫోటో తీయండి',
  capturePaymentScreenshot: 'చెల్లింపు స్క్రీన్‌షాట్ తీసుకోండి',
  selectedPaymentScreenshotPreview: 'ఎంచుకున్న చెల్లింపు స్క్రీన్‌షాట్ ప్రివ్యూ',
  submitting: 'సమర్పిస్తోంది...',
  submitVerification: 'ధృవీకరణ సమర్పించండి',
  paymentVerificationSubmitted: 'చెల్లింపు ధృవీకరణ విజయవంతంగా సమర్పించబడింది',
  paymentVerificationPendingMessage: 'ధృవీకరణ ప్రస్తుతం పెండింగ్‌లో ఉంది. ధృవీకరించిన తర్వాత మీ ఆర్డర్ స్థితి నవీకరించబడుతుంది.',
  referenceNumber: 'రిఫరెన్స్ నంబర్',
  submissionDate: 'సమర్పించిన తేదీ',
  paymentStatus: 'చెల్లింపు స్థితి',
  placingOrder: 'ఆర్డర్ ఇస్తోంది...',
  placeCodOrder: 'COD ఆర్డర్ ఇవ్వండి',
  scanQrCode: 'QR కోడ్ స్కాన్ చేయండి',
  generatingSecureQr: 'సురక్షిత QR రూపొందుతోంది...',
  amount: 'మొత్తం',
  qrUpdated: 'QR నవీకరించబడింది',
  scanQrUsingAnyUpi: 'ఏదైనా UPI అప్లికేషన్‌తో QR కోడ్‌ను స్కాన్ చేసి చెల్లింపును పూర్తి చేయండి.',
  payUsingDebitCreditCard: 'డెబిట్ / క్రెడిట్ కార్డ్ ద్వారా చెల్లించండి',
  payUsingCreditCard: 'క్రెడిట్ కార్డ్ ద్వారా చెల్లించండి',
  cardPaymentsUnavailable: 'కార్డ్ చెల్లింపులు ప్రస్తుతం అందుబాటులో లేవు.',
  cardHolderName: 'కార్డ్ హోల్డర్ పేరు',
  enterNameOnCard: 'కార్డ్‌పై ఉన్న పేరును నమోదు చేయండి',
  expiryMonth: 'గడువు నెల',
  expiryYear: 'గడువు సంవత్సరం',
  month: 'నెల',
  year: 'సంవత్సరం',
  saveThisCard: 'ఈ కార్డ్‌ను సేవ్ చేయండి',
  saveCard: 'కార్డ్ సేవ్ చేయండి',
  pay: 'చెల్లించండి',
  payCashOnDeliveryDescription: 'మీ ఆర్డర్ మీ చిరునామాకు డెలివరీ అయినప్పుడు నగదుగా చెల్లించండి.',
  orderIdRequired: 'ఆర్డర్ ID అవసరం.',
  orderIdFormat: '3 నుండి 50 అక్షరాలు లేదా సంఖ్యలు ఉపయోగించండి. హైఫన్, అండర్‌స్కోర్ మరియు స్లాష్ అనుమతించబడతాయి.',
  transactionUtrRequired: 'లావాదేవీ / UTR నంబర్ అవసరం.',
  transactionUtrFormat: '8 నుండి 30 అక్షరాలు లేదా సంఖ్యలు ఉపయోగించండి. హైఫన్ మాత్రమే అనుమతించబడుతుంది.',
  amountPaidRequired: 'చెల్లించిన మొత్తం అవసరం.',
  amountPaidMustEqual: 'చెల్లించిన మొత్తం తప్పనిసరిగా సమానం కావాలి',
  paymentDateRequired: 'చెల్లింపు తేదీ అవసరం.',
  paymentDateFuture: 'చెల్లింపు తేదీ భవిష్యత్తులో ఉండకూడదు.',
  paymentTimeRequired: 'చెల్లింపు సమయం అవసరం.',
  customerNameRequired: 'కస్టమర్ పేరు అవసరం.',
  customerNameInvalid: 'కస్టమర్ పేరులో అక్షరాలు మాత్రమే ఉండాలి (2-50 అక్షరాలు).',
  mobileNumberRequired: 'మొబైల్ నంబర్ అవసరం.',
  paymentScreenshotRequired: 'చెల్లింపు స్క్రీన్‌షాట్ అవసరం.',
  paymentScreenshotFileType: 'JPG, JPEG, PNG లేదా PDF ఫైల్ అప్‌లోడ్ చేయండి.',
  paymentScreenshotFileSize: 'అటాచ్‌మెంట్ 5 MB లేదా అంతకంటే తక్కువగా ఉండాలి.',
  cameraStillLoading: 'కెమెరా ఇంకా లోడ్ అవుతోంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
  unableCapturePhoto: 'ఫోటో తీయలేకపోయాం. దయచేసి మళ్లీ ప్రయత్నించండి.',
  search: 'వెతకండి',
  searchEverything: 'పేరు ద్వారా ఉత్పత్తులను వెతకండి',
  searchResults: 'శోధన ఫలితాలు',
  allResults: 'కోసం ఫలితాలు',
  viewAllResults: 'అన్ని ఫలితాలను చూడండి',
  noSearchResults: 'సరిపోలే ఫలితాలు లేవు',
  startSearching: 'ఉత్పత్తి పేరును టైప్ చేయడం ప్రారంభించండి',
  matchingProducts: 'సరిపోలే ఉత్పత్తులు',
  matchingCategories: 'సరిపోలే వర్గాలు',
  matchingSubcategories: 'సరిపోలే ఉపవర్గాలు',
  matchingPages: 'సరిపోలే పేజీలు',
  pages: 'పేజీలు',
  offers: 'ఆఫర్లు',
  profile: 'ప్రొఫైల్',
  editProfile: 'ప్రొఫైల్ సవరించండి',
  loadingLatestProfile: 'తాజా ప్రొఫైల్ లోడ్ అవుతోంది...',
  closeProfile: 'ప్రొఫైల్ మూసివేయండి',
  changePhoto: 'ఫోటో మార్చండి',
  personalDetails: 'వ్యక్తిగత వివరాలు',
  fullName: 'పూర్తి పేరు',
  enterFullName: 'మీ పూర్తి పేరు నమోదు చేయండి',
  enterEmailAddress: 'మీ ఇమెయిల్ చిరునామా నమోదు చేయండి',
  mobileNumber: 'మొబైల్ నంబర్',
  mobileNumberCannotBeEdited: 'మొబైల్ నంబర్ మార్చలేరు.',
  addressInformation: 'చిరునామా సమాచారం',
  doorNoHouseNo: 'డోర్ నం / ఇంటి నం',
  enterDoorHouseNumber: 'డోర్ లేదా ఇంటి నంబర్ నమోదు చేయండి',
  streetArea: 'వీధి / ప్రాంతం',
  enterStreetArea: 'వీధి లేదా ప్రాంతం నమోదు చేయండి',
  enterCity: 'నగరం నమోదు చేయండి',
  enterState: 'రాష్ట్రం నమోదు చేయండి',
  enterPincode: 'పిన్ కోడ్ నమోదు చేయండి',
  saving: 'సేవ్ అవుతోంది...',
  saveProfile: 'ప్రొఫైల్ సేవ్ చేయండి',
  showingSavedProfileDetails: 'సేవ్ చేసిన ప్రొఫైల్ వివరాలు చూపిస్తున్నాం.',
  validEmailAddress: 'సరైన ఇమెయిల్ చిరునామా నమోదు చేయండి.',
  mobileNumberTenDigits: 'మొబైల్ నంబర్ 10 అంకెలు ఉండాలి.',
  profileUpdated: 'ప్రొఫైల్ నవీకరించబడింది.',
  unableUpdateProfile: 'ప్రొఫైల్ నవీకరించలేకపోయాం.',
  unsupportedProfileImageType: 'దయచేసి JPG, PNG, WEBP లేదా GIF చిత్రాన్ని ఎంచుకోండి.',
  profileImageTooLarge: 'ప్రొఫైల్ చిత్రం 2MB కంటే చిన్నదిగా ఉండాలి.',
});

Object.assign(translations.hi, {
  searchCategoriesProducts: 'श्रेणियां और उत्पाद खोजें...',
  searchCategoriesProductsPages: 'श्रेणियां, उत्पाद और पेज खोजें',
  paymentInformation: 'भुगतान जानकारी',
  paymentInformationDescription: 'सुरक्षित भुगतान फॉर्म के माध्यम से अपना भुगतान पूरा करें. आपके लेन-देन विवरण एन्क्रिप्ट किए जाते हैं और केवल इस ऑर्डर की पुष्टि के लिए उपयोग होते हैं.',
  sslSecuredPaymentSession: 'SSL सुरक्षित भुगतान सत्र',
  cardUpiDetailsProtected: 'कार्ड और UPI विवरण सुरक्षित रहते हैं',
  needHelp: 'मदद चाहिए?',
  refreshingPaymentDetails: 'बैकएंड से भुगतान विवरण रिफ्रेश हो रहे हैं...',
  payUsingUpi: 'UPI से भुगतान करें',
  merchantUpiId: 'व्यापारी UPI ID',
  linkedBank: 'लिंक किया गया बैंक',
  currency: 'मुद्रा',
  upiIdCopied: 'UPI ID कॉपी हो गई',
  copyUpiId: 'UPI ID कॉपी करें',
  completePaymentUsingUpi: 'अपनी पसंदीदा UPI ऐप से भुगतान पूरा करें.',
  accountHolderName: 'खाताधारक का नाम',
  accountNumber: 'खाता संख्या',
  ifscCode: 'IFSC कोड',
  branch: 'शाखा',
  transferUsingBank: 'NEFT / IMPS / RTGS से राशि ट्रांसफर करें.',
  manualPaymentVerification: 'मैनुअल भुगतान सत्यापन',
  manualPaymentVerificationDescription: 'बाहरी ट्रांसफर पूरा करने के बाद भुगतान विवरण दर्ज करें.',
  remarksOptional: 'टिप्पणी (वैकल्पिक)',
  additionalPaymentNotes: 'अतिरिक्त भुगतान नोट जोड़ें',
  orderIdAutoGenerated: 'ऑर्डर ID अपने आप जनरेट होगी',
  transactionUtrNumber: 'लेन-देन / UTR नंबर',
  enterUtrReferenceId: 'UTR या रेफरेंस ID दर्ज करें',
  amountPaid: 'भुगतान की गई राशि',
  paymentDate: 'भुगतान तारीख',
  paymentTime: 'भुगतान समय',
  customerName: 'ग्राहक का नाम',
  enterCustomerName: 'ग्राहक का नाम दर्ज करें',
  tenDigitMobileNumber: '10 अंकों का मोबाइल नंबर',
  uploadPaymentScreenshot: 'भुगतान स्क्रीनशॉट अपलोड करें',
  chooseFile: 'फाइल चुनें',
  capturePhoto: 'फोटो लें',
  capturePaymentScreenshot: 'भुगतान स्क्रीनशॉट लें',
  selectedPaymentScreenshotPreview: 'चुने गए भुगतान स्क्रीनशॉट का प्रीव्यू',
  submitting: 'सबमिट हो रहा है...',
  submitVerification: 'सत्यापन सबमिट करें',
  paymentVerificationSubmitted: 'भुगतान सत्यापन सफलतापूर्वक सबमिट हुआ',
  paymentVerificationPendingMessage: 'सत्यापन अभी लंबित है. सत्यापित होने के बाद आपके ऑर्डर की स्थिति अपडेट होगी.',
  referenceNumber: 'रेफरेंस नंबर',
  submissionDate: 'सबमिशन तारीख',
  paymentStatus: 'भुगतान स्थिति',
  placingOrder: 'ऑर्डर दिया जा रहा है...',
  placeCodOrder: 'COD ऑर्डर दें',
  scanQrCode: 'QR कोड स्कैन करें',
  generatingSecureQr: 'सुरक्षित QR बन रहा है...',
  amount: 'राशि',
  qrUpdated: 'QR अपडेट हुआ',
  scanQrUsingAnyUpi: 'किसी भी UPI ऐप से QR कोड स्कैन करें और भुगतान पूरा करें.',
  payUsingDebitCreditCard: 'डेबिट / क्रेडिट कार्ड से भुगतान करें',
  payUsingCreditCard: 'क्रेडिट कार्ड से भुगतान करें',
  cardPaymentsUnavailable: 'कार्ड भुगतान अभी उपलब्ध नहीं हैं.',
  cardHolderName: 'कार्ड धारक का नाम',
  enterNameOnCard: 'कार्ड पर नाम दर्ज करें',
  expiryMonth: 'समाप्ति माह',
  expiryYear: 'समाप्ति वर्ष',
  month: 'माह',
  year: 'वर्ष',
  saveThisCard: 'इस कार्ड को सेव करें',
  saveCard: 'कार्ड सेव करें',
  pay: 'भुगतान करें',
  payCashOnDeliveryDescription: 'ऑर्डर आपके पते पर डिलीवर होने पर नकद भुगतान करें.',
  orderIdRequired: 'ऑर्डर ID आवश्यक है.',
  orderIdFormat: '3 से 50 अक्षर या नंबर उपयोग करें. हाइफन, अंडरस्कोर और स्लैश मान्य हैं.',
  transactionUtrRequired: 'लेन-देन / UTR नंबर आवश्यक है.',
  transactionUtrFormat: '8 से 30 अक्षर या नंबर उपयोग करें. केवल हाइफन मान्य है.',
  amountPaidRequired: 'भुगतान की गई राशि आवश्यक है.',
  amountPaidMustEqual: 'भुगतान की गई राशि बराबर होनी चाहिए',
  paymentDateRequired: 'भुगतान तारीख आवश्यक है.',
  paymentDateFuture: 'भुगतान तारीख भविष्य की नहीं हो सकती.',
  paymentTimeRequired: 'भुगतान समय आवश्यक है.',
  customerNameRequired: 'ग्राहक का नाम आवश्यक है.',
  customerNameInvalid: 'ग्राहक के नाम में केवल अक्षर होने चाहिए (2-50 अक्षर).',
  mobileNumberRequired: 'मोबाइल नंबर आवश्यक है.',
  paymentScreenshotRequired: 'भुगतान स्क्रीनशॉट आवश्यक है.',
  paymentScreenshotFileType: 'JPG, JPEG, PNG या PDF फाइल अपलोड करें.',
  paymentScreenshotFileSize: 'अटैचमेंट 5 MB या उससे छोटा होना चाहिए.',
  cameraStillLoading: 'कैमरा अभी लोड हो रहा है. कृपया फिर से प्रयास करें.',
  unableCapturePhoto: 'फोटो कैप्चर नहीं हो सकी. कृपया फिर से प्रयास करें.',
  search: 'खोजें',
  searchEverything: 'नाम से उत्पाद खोजें',
  searchResults: 'खोज परिणाम',
  allResults: 'के लिए परिणाम',
  viewAllResults: 'सभी परिणाम देखें',
  noSearchResults: 'कोई मिलान परिणाम नहीं मिला',
  startSearching: 'उत्पाद का नाम लिखना शुरू करें',
  matchingProducts: 'मिलते-जुलते उत्पाद',
  matchingCategories: 'मिलती-जुलती श्रेणियां',
  matchingSubcategories: 'मिलती-जुलती उपश्रेणियां',
  matchingPages: 'मिलते-जुलते पेज',
  pages: 'पेज',
  offers: 'ऑफर',
  profile: 'प्रोफाइल',
  editProfile: 'प्रोफाइल संपादित करें',
  loadingLatestProfile: 'नवीनतम प्रोफाइल लोड हो रही है...',
  closeProfile: 'प्रोफाइल बंद करें',
  changePhoto: 'फोटो बदलें',
  personalDetails: 'व्यक्तिगत विवरण',
  fullName: 'पूरा नाम',
  enterFullName: 'अपना पूरा नाम दर्ज करें',
  enterEmailAddress: 'अपना ईमेल पता दर्ज करें',
  mobileNumber: 'मोबाइल नंबर',
  mobileNumberCannotBeEdited: 'मोबाइल नंबर बदला नहीं जा सकता.',
  addressInformation: 'पता जानकारी',
  doorNoHouseNo: 'दरवाजा नं / मकान नं',
  enterDoorHouseNumber: 'दरवाजा या मकान नंबर दर्ज करें',
  streetArea: 'गली / क्षेत्र',
  enterStreetArea: 'गली या क्षेत्र दर्ज करें',
  enterCity: 'शहर दर्ज करें',
  enterState: 'राज्य दर्ज करें',
  enterPincode: 'पिनकोड दर्ज करें',
  saving: 'सेव हो रहा है...',
  saveProfile: 'प्रोफाइल सेव करें',
  showingSavedProfileDetails: 'सेव की गई प्रोफाइल जानकारी दिखाई जा रही है.',
  validEmailAddress: 'सही ईमेल पता दर्ज करें.',
  mobileNumberTenDigits: 'मोबाइल नंबर 10 अंकों का होना चाहिए.',
  profileUpdated: 'प्रोफाइल अपडेट हो गई.',
  unableUpdateProfile: 'प्रोफाइल अपडेट नहीं हो सकी.',
  unsupportedProfileImageType: 'कृपया JPG, PNG, WEBP या GIF चित्र चुनें.',
  profileImageTooLarge: 'प्रोफाइल चित्र 2MB से छोटा होना चाहिए.',
});

const checkoutUiTranslations = {
  en: {
    address: 'Address',
    addressType: 'Address Type',
    addressTypeRequired: 'Address type is required.',
    addNewAddress: 'Add New Address',
    alternatePhoneNumber: 'Alternate Phone Number',
    appliedCoupon: 'Applied Coupon',
    applyCoupon: 'Apply Coupon',
    availableCoins: 'Available Coins',
    backToCheckout: 'Back to Checkout',
    billingAddress: 'Billing Address',
    billingAddressRequired: 'Billing Address is required.',
    bankTransfer: 'Bank Transfer',
    cgst: 'CGST',
    clearCart: 'Clear Cart',
    clearingCart: 'Clearing Cart...',
    choosePaymentMethodSecurely: 'Choose your payment method and complete the order securely.',
    coinDiscount: 'Coin Discount',
    cityRequired: 'City is required.',
    confirmAndPay: 'Confirm & Pay',
    connectingSecurely: 'Connecting securely',
    couponAppliedSuccessfully: 'Coupon Applied Successfully',
    couponExpired: 'Coupon Expired',
    couponPlaceholder: 'Coupon code',
    delete: 'Delete',
    edit: 'Edit',
    enterAlternateMobileNumber: 'Enter alternate mobile number',
    enterBillingAddress: 'Enter billing address',
    enterEmailAddress: 'Enter email address',
    enterFirstName: 'Enter first name',
    enterLastName: 'Enter last name',
    enterMobileNumber: 'Enter 10-digit mobile number',
    emailRequired: 'Email address is required.',
    firstNameRequired: 'First name is required.',
    firstNameLettersOnly: 'First name should contain only letters and spaces.',
    fullAddressRequired: 'Full address is required.',
    fullAddressMinLength: 'Full address must be at least 10 characters.',
    flatHouseBuildingName: 'Flat/House/Building name',
    generatingSecurePaymentQr: 'Generating Secure Payment QR...',
    homeAddress: 'Home',
    invalidCoupon: 'Invalid Coupon',
    maxCoinsPerOrder: 'Max 500 per order',
    office: 'Office',
    orderId: 'Order ID',
    other: 'Other',
    pincodeRequired: 'Pincode is required.',
    pincodeSixDigits: 'Pincode must be exactly 6 digits.',
    phoneRequired: 'Phone number is required.',
    phoneTenDigits: 'Phone number must be exactly 10 digits.',
    nameOnCardLettersOnly: 'Name on card should contain only letters and spaces.',
    lastNameRequired: 'Last name is required.',
    lastNameLettersOnly: 'Last name should contain only letters and spaces.',
    paymentFailedStatus: 'Payment Failed',
    paymentProcessing: 'Payment Processing',
    phonePePaytmBhimUpi: 'Google Pay - PhonePe - Paytm - BHIM UPI - Any UPI App',
    processingPayment: 'Processing Payment...',
    pleaseWait: 'Please wait...',
    qrPayment: 'QR Payment',
    qrUnavailable: 'QR unavailable',
    redeemCoins: 'Redeem Coins',
    removeCoupon: 'Remove Coupon',
    save: 'Save',
    saveAddress: 'Save Address',
    scanAndPay: 'Scan & Pay',
    scanUsing: 'Scan using',
    secureQrWillAppear: 'Secure QR will appear here',
    secureUpiPayment: 'Secure UPI Payment',
    selected: 'Selected',
    selectedFile: 'Selected',
    selectAddress: 'Select Address',
    sellingPrice: 'Selling Price',
    sgst: 'SGST',
    shippingAddress: 'Shipping Address',
    shippingNotice: 'Ensure your shipping details are accurate for a smooth delivery experience.',
    stateRequired: 'State is required.',
    supportedApps: 'Supported Apps',
    totalItems: 'Total Items',
    validatingStock: 'Validating Stock...',
    waitingForPayment: 'Waiting for Payment',
  },
  te: {
    address: 'చిరునామా',
    addressType: 'చిరునామా రకం',
    addressTypeRequired: 'చిరునామా రకం అవసరం.',
    addNewAddress: 'కొత్త చిరునామా జోడించండి',
    alternatePhoneNumber: 'ప్రత్యామ్నాయ ఫోన్ నంబర్',
    appliedCoupon: 'వర్తించిన కూపన్',
    applyCoupon: 'కూపన్ వర్తింపజేయండి',
    availableCoins: 'అందుబాటులో ఉన్న కాయిన్లు',
    backToCheckout: 'చెకౌట్‌కు తిరిగి వెళ్ళండి',
    billingAddress: 'బిల్లింగ్ చిరునామా',
    billingAddressRequired: 'బిల్లింగ్ చిరునామా అవసరం.',
    bankTransfer: 'బ్యాంక్ ట్రాన్స్‌ఫర్',
    cgst: 'CGST',
    clearCart: 'కార్ట్ క్లియర్ చేయండి',
    clearingCart: 'కార్ట్ క్లియర్ అవుతోంది...',
    choosePaymentMethodSecurely: 'మీ చెల్లింపు విధానాన్ని ఎంచుకుని ఆర్డర్‌ను సురక్షితంగా పూర్తి చేయండి.',
    coinDiscount: 'కాయిన్ డిస్కౌంట్',
    cityRequired: 'నగరం అవసరం.',
    confirmAndPay: 'నిర్ధారించి చెల్లించండి',
    connectingSecurely: 'సురక్షితంగా కనెక్ట్ అవుతోంది',
    couponAppliedSuccessfully: 'కూపన్ విజయవంతంగా వర్తించబడింది',
    couponExpired: 'కూపన్ గడువు ముగిసింది',
    couponPlaceholder: 'కూపన్ కోడ్',
    delete: 'తొలగించు',
    edit: 'సవరించు',
    enterAlternateMobileNumber: 'ప్రత్యామ్నాయ మొబైల్ నంబర్ నమోదు చేయండి',
    enterBillingAddress: 'బిల్లింగ్ చిరునామా నమోదు చేయండి',
    enterEmailAddress: 'ఈమెయిల్ చిరునామా నమోదు చేయండి',
    enterFirstName: 'మొదటి పేరు నమోదు చేయండి',
    enterLastName: 'చివరి పేరు నమోదు చేయండి',
    enterMobileNumber: '10 అంకెల మొబైల్ నంబర్ నమోదు చేయండి',
    emailRequired: 'ఈమెయిల్ చిరునామా అవసరం.',
    firstNameRequired: 'మొదటి పేరు అవసరం.',
    firstNameLettersOnly: 'మొదటి పేరులో అక్షరాలు మరియు ఖాళీలు మాత్రమే ఉండాలి.',
    fullAddressRequired: 'పూర్తి చిరునామా అవసరం.',
    fullAddressMinLength: 'పూర్తి చిరునామా కనీసం 10 అక్షరాలు ఉండాలి.',
    flatHouseBuildingName: 'ఫ్లాట్/ఇల్లు/భవనం పేరు',
    generatingSecurePaymentQr: 'సురక్షిత చెల్లింపు QR రూపొందుతోంది...',
    homeAddress: 'ఇల్లు',
    invalidCoupon: 'చెల్లని కూపన్',
    maxCoinsPerOrder: 'ఆర్డర్‌కు గరిష్ఠం 500',
    office: 'ఆఫీస్',
    orderId: 'ఆర్డర్ ID',
    other: 'ఇతర',
    pincodeRequired: 'పిన్‌కోడ్ అవసరం.',
    pincodeSixDigits: 'పిన్‌కోడ్ ఖచ్చితంగా 6 అంకెలు ఉండాలి.',
    phoneRequired: 'ఫోన్ నంబర్ అవసరం.',
    phoneTenDigits: 'ఫోన్ నంబర్ ఖచ్చితంగా 10 అంకెలు ఉండాలి.',
    nameOnCardLettersOnly: 'కార్డ్‌పై పేరులో అక్షరాలు మరియు ఖాళీలు మాత్రమే ఉండాలి.',
    lastNameRequired: 'చివరి పేరు అవసరం.',
    lastNameLettersOnly: 'చివరి పేరులో అక్షరాలు మరియు ఖాళీలు మాత్రమే ఉండాలి.',
    paymentFailedStatus: 'చెల్లింపు విఫలమైంది',
    paymentProcessing: 'చెల్లింపు ప్రాసెస్ అవుతోంది',
    phonePePaytmBhimUpi: 'Google Pay - PhonePe - Paytm - BHIM UPI - ఏదైనా UPI యాప్',
    processingPayment: 'చెల్లింపు ప్రాసెస్ అవుతోంది...',
    pleaseWait: 'దయచేసి వేచి ఉండండి...',
    qrPayment: 'QR చెల్లింపు',
    qrUnavailable: 'QR అందుబాటులో లేదు',
    redeemCoins: 'కాయిన్లు రిడీమ్ చేయండి',
    removeCoupon: 'కూపన్ తొలగించండి',
    save: 'సేవ్',
    saveAddress: 'చిరునామా సేవ్ చేయండి',
    scanAndPay: 'స్కాన్ చేసి చెల్లించండి',
    scanUsing: 'దీంతో స్కాన్ చేయండి',
    secureQrWillAppear: 'సురక్షిత QR ఇక్కడ కనిపిస్తుంది',
    secureUpiPayment: 'సురక్షిత UPI చెల్లింపు',
    selected: 'ఎంచుకున్నారు',
    selectedFile: 'ఎంచుకున్నది',
    selectAddress: 'చిరునామా ఎంచుకోండి',
    sellingPrice: 'అమ్మకపు ధర',
    sgst: 'SGST',
    shippingAddress: 'షిప్పింగ్ చిరునామా',
    shippingNotice: 'సాఫీగా డెలివరీ కోసం మీ షిప్పింగ్ వివరాలు ఖచ్చితంగా ఉన్నాయని నిర్ధారించండి.',
    stateRequired: 'రాష్ట్రం అవసరం.',
    supportedApps: 'మద్దతు ఉన్న యాప్‌లు',
    totalItems: 'మొత్తం వస్తువులు',
    validatingStock: 'స్టాక్ తనిఖీ అవుతోంది...',
    waitingForPayment: 'చెల్లింపుకు వేచి ఉంది',
  },
  hi: {
    address: 'पता',
    addressType: 'पते का प्रकार',
    addressTypeRequired: 'पते का प्रकार आवश्यक है.',
    addNewAddress: 'नया पता जोड़ें',
    alternatePhoneNumber: 'वैकल्पिक फोन नंबर',
    appliedCoupon: 'लागू कूपन',
    applyCoupon: 'कूपन लागू करें',
    availableCoins: 'उपलब्ध कॉइन',
    backToCheckout: 'चेकआउट पर वापस जाएं',
    billingAddress: 'बिलिंग पता',
    billingAddressRequired: 'बिलिंग पता आवश्यक है.',
    bankTransfer: 'बैंक ट्रांसफर',
    cgst: 'CGST',
    clearCart: 'कार्ट खाली करें',
    clearingCart: 'कार्ट खाली हो रहा है...',
    choosePaymentMethodSecurely: 'अपना भुगतान तरीका चुनें और ऑर्डर सुरक्षित रूप से पूरा करें.',
    coinDiscount: 'कॉइन छूट',
    cityRequired: 'शहर आवश्यक है.',
    confirmAndPay: 'पुष्टि करें और भुगतान करें',
    connectingSecurely: 'सुरक्षित रूप से कनेक्ट हो रहा है',
    couponAppliedSuccessfully: 'कूपन सफलतापूर्वक लागू हुआ',
    couponExpired: 'कूपन समाप्त हो गया',
    couponPlaceholder: 'कूपन कोड',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    enterAlternateMobileNumber: 'वैकल्पिक मोबाइल नंबर दर्ज करें',
    enterBillingAddress: 'बिलिंग पता दर्ज करें',
    enterEmailAddress: 'ईमेल पता दर्ज करें',
    enterFirstName: 'पहला नाम दर्ज करें',
    enterLastName: 'अंतिम नाम दर्ज करें',
    enterMobileNumber: '10 अंकों का मोबाइल नंबर दर्ज करें',
    emailRequired: 'ईमेल पता आवश्यक है.',
    firstNameRequired: 'पहला नाम आवश्यक है.',
    firstNameLettersOnly: 'पहले नाम में केवल अक्षर और स्पेस होने चाहिए.',
    fullAddressRequired: 'पूरा पता आवश्यक है.',
    fullAddressMinLength: 'पूरा पता कम से कम 10 अक्षरों का होना चाहिए.',
    flatHouseBuildingName: 'फ्लैट/घर/बिल्डिंग का नाम',
    generatingSecurePaymentQr: 'सुरक्षित भुगतान QR बनाया जा रहा है...',
    homeAddress: 'घर',
    invalidCoupon: 'अमान्य कूपन',
    maxCoinsPerOrder: 'प्रति ऑर्डर अधिकतम 500',
    office: 'ऑफिस',
    orderId: 'ऑर्डर ID',
    other: 'अन्य',
    pincodeRequired: 'पिनकोड आवश्यक है.',
    pincodeSixDigits: 'पिनकोड ठीक 6 अंकों का होना चाहिए.',
    phoneRequired: 'फोन नंबर आवश्यक है.',
    phoneTenDigits: 'फोन नंबर ठीक 10 अंकों का होना चाहिए.',
    nameOnCardLettersOnly: 'कार्ड पर नाम में केवल अक्षर और स्पेस होने चाहिए.',
    lastNameRequired: 'अंतिम नाम आवश्यक है.',
    lastNameLettersOnly: 'अंतिम नाम में केवल अक्षर और स्पेस होने चाहिए.',
    paymentFailedStatus: 'भुगतान विफल',
    paymentProcessing: 'भुगतान प्रोसेस हो रहा है',
    phonePePaytmBhimUpi: 'Google Pay - PhonePe - Paytm - BHIM UPI - कोई भी UPI ऐप',
    processingPayment: 'भुगतान प्रोसेस हो रहा है...',
    pleaseWait: 'कृपया प्रतीक्षा करें...',
    qrPayment: 'QR भुगतान',
    qrUnavailable: 'QR उपलब्ध नहीं',
    redeemCoins: 'कॉइन रिडीम करें',
    removeCoupon: 'कूपन हटाएं',
    save: 'सेव करें',
    saveAddress: 'पता सेव करें',
    scanAndPay: 'स्कैन करें और भुगतान करें',
    scanUsing: 'स्कैन करें',
    secureQrWillAppear: 'सुरक्षित QR यहां दिखाई देगा',
    secureUpiPayment: 'सुरक्षित UPI भुगतान',
    selected: 'चयनित',
    selectedFile: 'चयनित',
    selectAddress: 'पता चुनें',
    sellingPrice: 'विक्रय मूल्य',
    sgst: 'SGST',
    shippingAddress: 'शिपिंग पता',
    shippingNotice: 'सुगम डिलीवरी के लिए कृपया अपने शिपिंग विवरण सही रखें.',
    stateRequired: 'राज्य आवश्यक है.',
    supportedApps: 'समर्थित ऐप्स',
    totalItems: 'कुल आइटम',
    validatingStock: 'स्टॉक जांचा जा रहा है...',
    waitingForPayment: 'भुगतान की प्रतीक्षा',
  },
};

Object.entries(checkoutUiTranslations).forEach(([locale, messages]) => {
  Object.assign(translations[locale], messages);
});

Object.assign(translations.en, {
  testimonialsSubtitle: 'Testimonials',
  testimonialsHeading: 'What our clients say',
  imageNotAvailable: 'Image not available',
  unableLoadBlogs: 'Unable to load blogs.',
  pleaseTryAgain: 'Please try again.',
  noBlogsAvailable: 'No Blogs Available',
  specLabels: {
    ...(translations.en.specLabels || {}),
    weight: 'Weight',
    dimensions: 'Dimensions',
    powerSource: 'Power Source',
    material: 'Material',
    coverageUsage: 'Coverage Usage',
  },
});

Object.assign(translations.te, {
  testimonialsSubtitle: 'ప్రశంసలు',
  testimonialsHeading: 'మా ఖాతాదారులు ఏమంటున్నారు',
  imageNotAvailable: 'చిత్రం అందుబాటులో లేదు',
  unableLoadBlogs: 'బ్లాగులు లోడ్ చేయలేకపోయాం.',
  pleaseTryAgain: 'దయచేసి మళ్లీ ప్రయత్నించండి.',
  noBlogsAvailable: 'బ్లాగులు అందుబాటులో లేవు',
  specLabels: {
    ...(translations.te.specLabels || {}),
    weight: 'బరువు',
    dimensions: 'కొలతలు',
    powerSource: 'శక్తి మూలం',
    material: 'పదార్థం',
    coverageUsage: 'కవరేజ్ వినియోగం',
  },
});

Object.assign(translations.hi, {
  testimonialsSubtitle: 'प्रशंसापत्र',
  testimonialsHeading: 'हमारे ग्राहक क्या कहते हैं',
  imageNotAvailable: 'चित्र उपलब्ध नहीं है',
  unableLoadBlogs: 'ब्लॉग लोड नहीं हो सके.',
  pleaseTryAgain: 'कृपया फिर से प्रयास करें.',
  noBlogsAvailable: 'कोई ब्लॉग उपलब्ध नहीं है',
  specLabels: {
    ...(translations.hi.specLabels || {}),
    weight: 'वजन',
    dimensions: 'आयाम',
    powerSource: 'ऊर्जा स्रोत',
    material: 'सामग्री',
    coverageUsage: 'कवरेज उपयोग',
  },
});

Object.assign(translations.en, {
  trackingEyebrow: 'Plant-to-door delivery journey',
  trackYourShipment: 'Track Your Shipment',
  trackingDetailsShown: 'Your order tracking details are shown below.',
  trackingEnterDetails: 'Enter your Order ID or Tracking Number to see real-time updates.',
  enterOrderId: 'Enter Order ID',
  trackingInProgress: 'Tracking...',
  trackOrder: 'Track Order',
  currentStatus: 'Current Status',
  estDelivery: 'Est. Delivery',
  trackingEstimatedDeliveryValue: '3-7 business days',
  trackingConfirmed: 'Confirmed',
  trackingPacked: 'Packed',
  trackingShipped: 'Shipped',
  trackingOutForDelivery: 'Out for Delivery',
  trackingDelivered: 'Delivered',
  noActiveOrderSelected: 'No active order selected',
  placeOrderOrEnterOrderId: 'Place an order or enter your order ID to track the shipment.',
  shopProducts: 'Shop products',
  cardPayment: 'Card Payment',
});

Object.assign(translations.te, {
  trackingEyebrow: '\u0C2A\u0C4D\u0C32\u0C3E\u0C02\u0C1F\u0C4D \u0C28\u0C41\u0C02\u0C21\u0C3F \u0C21\u0C4B\u0C30\u0C4D \u0C35\u0C30\u0C15\u0C41 \u0C21\u0C46\u0C32\u0C3F\u0C35\u0C30\u0C40 \u0C2A\u0C4D\u0C30\u0C2F\u0C3E\u0C23\u0C02',
  trackYourShipment: '\u0C2E\u0C40 \u0C30\u0C35\u0C3E\u0C23\u0C3E\u0C28\u0C41 \u0C1F\u0C4D\u0C30\u0C3E\u0C15\u0C4D \u0C1A\u0C47\u0C2F\u0C02\u0C21\u0C3F',
  trackingDetailsShown: '\u0C2E\u0C40 \u0C06\u0C30\u0C4D\u0C21\u0C30\u0C4D \u0C1F\u0C4D\u0C30\u0C3E\u0C15\u0C3F\u0C02\u0C17\u0C4D \u0C35\u0C3F\u0C35\u0C30\u0C3E\u0C32\u0C41 \u0C15\u0C4D\u0C30\u0C3F\u0C02\u0C26 \u0C1A\u0C42\u0C2A\u0C2C\u0C21\u0C4D\u0C21\u0C3E\u0C2F\u0C3F.',
  trackingEnterDetails: '\u0C30\u0C3F\u0C2F\u0C32\u0C4D-\u0C1F\u0C48\u0C2E\u0C4D \u0C05\u0C2A\u0C4D\u0C21\u0C47\u0C1F\u0C4D\u0C32\u0C28\u0C41 \u0C1A\u0C42\u0C21\u0C1F\u0C3E\u0C28\u0C3F\u0C15\u0C3F \u0C2E\u0C40 \u0C06\u0C30\u0C4D\u0C21\u0C30\u0C4D ID \u0C32\u0C47\u0C26\u0C3E \u0C1F\u0C4D\u0C30\u0C3E\u0C15\u0C3F\u0C02\u0C17\u0C4D \u0C28\u0C02\u0C2C\u0C30\u0C4D \u0C28\u0C2E\u0C4B\u0C26\u0C41 \u0C1A\u0C47\u0C2F\u0C02\u0C21\u0C3F.',
  enterOrderId: '\u0C06\u0C30\u0C4D\u0C21\u0C30\u0C4D ID \u0C28\u0C2E\u0C4B\u0C26\u0C41 \u0C1A\u0C47\u0C2F\u0C02\u0C21\u0C3F',
  trackingInProgress: '\u0C1F\u0C4D\u0C30\u0C3E\u0C15\u0C4D \u0C1A\u0C47\u0C38\u0C4D\u0C24\u0C4B\u0C02\u0C26\u0C3F...',
  trackOrder: '\u0C06\u0C30\u0C4D\u0C21\u0C30\u0C4D \u0C1F\u0C4D\u0C30\u0C3E\u0C15\u0C4D \u0C1A\u0C47\u0C2F\u0C02\u0C21\u0C3F',
  currentStatus: '\u0C2A\u0C4D\u0C30\u0C38\u0C4D\u0C24\u0C41\u0C24 \u0C38\u0C4D\u0C25\u0C3F\u0C24\u0C3F',
  estDelivery: '\u0C05\u0C02\u0C1A\u0C28\u0C3E \u0C21\u0C46\u0C32\u0C3F\u0C35\u0C30\u0C40',
  trackingEstimatedDeliveryValue: '3-7 \u0C2A\u0C28\u0C3F \u0C26\u0C3F\u0C28\u0C3E\u0C32\u0C41',
  trackingConfirmed: '\u0C28\u0C3F\u0C30\u0C4D\u0C27\u0C3E\u0C30\u0C3F\u0C02\u0C1A\u0C2C\u0C21\u0C3F\u0C02\u0C26\u0C3F',
  trackingPacked: '\u0C2A\u0C4D\u0C2F\u0C3E\u0C15\u0C4D \u0C1A\u0C47\u0C2F\u0C2C\u0C21\u0C3F\u0C02\u0C26\u0C3F',
  trackingShipped: '\u0C30\u0C35\u0C3E\u0C23\u0C3E \u0C1A\u0C47\u0C2F\u0C2C\u0C21\u0C3F\u0C02\u0C26\u0C3F',
  trackingOutForDelivery: '\u0C21\u0C46\u0C32\u0C3F\u0C35\u0C30\u0C40\u0C15\u0C3F \u0C2C\u0C2F\u0C32\u0C41\u0C26\u0C47\u0C30\u0C3F\u0C02\u0C26\u0C3F',
  trackingDelivered: '\u0C21\u0C46\u0C32\u0C3F\u0C35\u0C30\u0C4D \u0C05\u0C2F\u0C3F\u0C02\u0C26\u0C3F',
  noActiveOrderSelected: '\u0C2F\u0C3E\u0C15\u0C4D\u0C1F\u0C3F\u0C35\u0C4D \u0C06\u0C30\u0C4D\u0C21\u0C30\u0C4D \u0C0E\u0C02\u0C1A\u0C41\u0C15\u0C4B\u0C32\u0C47\u0C26\u0C41',
  placeOrderOrEnterOrderId: '\u0C30\u0C35\u0C3E\u0C23\u0C3E\u0C28\u0C41 \u0C1F\u0C4D\u0C30\u0C3E\u0C15\u0C4D \u0C1A\u0C47\u0C2F\u0C21\u0C3E\u0C28\u0C3F\u0C15\u0C3F \u0C06\u0C30\u0C4D\u0C21\u0C30\u0C4D \u0C1A\u0C47\u0C2F\u0C02\u0C21\u0C3F \u0C32\u0C47\u0C26\u0C3E \u0C2E\u0C40 \u0C06\u0C30\u0C4D\u0C21\u0C30\u0C4D ID \u0C28\u0C2E\u0C4B\u0C26\u0C41 \u0C1A\u0C47\u0C2F\u0C02\u0C21\u0C3F.',
  shopProducts: '\u0C09\u0C24\u0C4D\u0C2A\u0C24\u0C4D\u0C24\u0C41\u0C32\u0C28\u0C41 \u0C37\u0C3E\u0C2A\u0C4D \u0C1A\u0C47\u0C2F\u0C02\u0C21\u0C3F',
  cardPayment: '\u0C15\u0C3E\u0C30\u0C4D\u0C21\u0C4D \u0C1A\u0C46\u0C32\u0C4D\u0C32\u0C3F\u0C02\u0C2A\u0C41',
});

Object.assign(translations.hi, {
  trackingEyebrow: '\u092A\u094D\u0932\u093E\u0902\u091F \u0938\u0947 \u0926\u0930\u0935\u093E\u091C\u0947 \u0924\u0915 \u0921\u093F\u0932\u0940\u0935\u0930\u0940 \u092F\u093E\u0924\u094D\u0930\u093E',
  trackYourShipment: '\u0905\u092A\u0928\u0940 \u0936\u093F\u092A\u092E\u0947\u0902\u091F \u091F\u094D\u0930\u0948\u0915 \u0915\u0930\u0947\u0902',
  trackingDetailsShown: '\u0906\u092A\u0915\u0947 \u0911\u0930\u094D\u0921\u0930 \u091F\u094D\u0930\u0948\u0915\u093F\u0902\u0917 \u0935\u093F\u0935\u0930\u0923 \u0928\u0940\u091A\u0947 \u0926\u093F\u0916\u093E\u090F \u0917\u090F \u0939\u0948\u0902.',
  trackingEnterDetails: '\u0930\u093F\u092F\u0932-\u091F\u093E\u0907\u092E \u0905\u092A\u0921\u0947\u091F \u0926\u0947\u0916\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0905\u092A\u0928\u093E \u0911\u0930\u094D\u0921\u0930 ID \u092F\u093E \u091F\u094D\u0930\u0948\u0915\u093F\u0902\u0917 \u0928\u0902\u092C\u0930 \u0926\u0930\u094D\u091C \u0915\u0930\u0947\u0902.',
  enterOrderId: '\u0911\u0930\u094D\u0921\u0930 ID \u0926\u0930\u094D\u091C \u0915\u0930\u0947\u0902',
  trackingInProgress: '\u091F\u094D\u0930\u0948\u0915 \u0939\u094B \u0930\u0939\u093E \u0939\u0948...',
  trackOrder: '\u0911\u0930\u094D\u0921\u0930 \u091F\u094D\u0930\u0948\u0915 \u0915\u0930\u0947\u0902',
  currentStatus: '\u0935\u0930\u094D\u0924\u092E\u093E\u0928 \u0938\u094D\u0925\u093F\u0924\u093F',
  estDelivery: '\u0905\u0928\u0941\u092E\u093E\u0928\u093F\u0924 \u0921\u093F\u0932\u0940\u0935\u0930\u0940',
  trackingEstimatedDeliveryValue: '3-7 \u0915\u093E\u0930\u094D\u092F \u0926\u093F\u0935\u0938',
  trackingConfirmed: '\u092A\u0941\u0937\u094D\u091F\u093F \u0939\u094B \u0917\u0908',
  trackingPacked: '\u092A\u0948\u0915 \u0915\u093F\u092F\u093E \u0917\u092F\u093E',
  trackingShipped: '\u092D\u0947\u091C \u0926\u093F\u092F\u093E \u0917\u092F\u093E',
  trackingOutForDelivery: '\u0921\u093F\u0932\u0940\u0935\u0930\u0940 \u0915\u0947 \u0932\u093F\u090F \u0928\u093F\u0915\u0932\u093E',
  trackingDelivered: '\u0921\u093F\u0932\u0940\u0935\u0930 \u0939\u094B \u0917\u092F\u093E',
  noActiveOrderSelected: '\u0915\u094B\u0908 \u0938\u0915\u094D\u0930\u093F\u092F \u0911\u0930\u094D\u0921\u0930 \u091A\u092F\u0928\u093F\u0924 \u0928\u0939\u0940\u0902',
  placeOrderOrEnterOrderId: '\u0936\u093F\u092A\u092E\u0947\u0902\u091F \u091F\u094D\u0930\u0948\u0915 \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0911\u0930\u094D\u0921\u0930 \u0915\u0930\u0947\u0902 \u092F\u093E \u0905\u092A\u0928\u093E \u0911\u0930\u094D\u0921\u0930 ID \u0926\u0930\u094D\u091C \u0915\u0930\u0947\u0902.',
  shopProducts: '\u0909\u0924\u094D\u092A\u093E\u0926 \u0916\u0930\u0940\u0926\u0947\u0902',
  cardPayment: '\u0915\u093E\u0930\u094D\u0921 \u092D\u0941\u0917\u0924\u093E\u0928',
});

const dynamicTextTranslations = {
  te: {
    'test title': 'పరీక్ష శీర్షిక',
    'test tittle': 'పరీక్ష శీర్షిక',
    'test blog': 'పరీక్ష బ్లాగ్',
    'test summary': 'పరీక్ష సారాంశం',
    'test category': 'పరీక్ష వర్గం',
    'test author name': 'పరీక్ష రచయిత పేరు',
    'test authorname': 'పరీక్ష రచయిత పేరు',
    'dheeraj': 'ధీరజ్',
    'farm and garden': 'వ్యవసాయం మరియు తోట',
    'farm & garden': 'వ్యవసాయం మరియు తోట',
    'maintaining your power tools': 'మీ పవర్ టూల్స్ నిర్వహణ',
    'a simple maintenance routine can improve tool life, reduce downtime, and keep farm': 'సరళమైన నిర్వహణ పద్ధతి పరికరాల ఆయుష్షును పెంచి, పనిలో అంతరాయాన్ని తగ్గించి, వ్యవసాయ పనిని సజావుగా ఉంచుతుంది',
    'shyam agro': 'శ్యామ్ ఆగ్రో',
    'agriculture': 'వ్యవసాయం',
    'customer': 'వినియోగదారు',
    'professional farmer': 'వృత్తిపర రైతు',
    'agro-enterprise owner': 'వ్యవసాయ వ్యాపార యజమాని',
    'workshop owner': 'వర్క్‌షాప్ యజమాని',
    'the industrial tools from shyam agro have completely transformed my farming efficiency. the quality is top-notch and the service is excellent.': 'శ్యామ్ ఆగ్రో పరిశ్రమ పరికరాలు నా వ్యవసాయ సామర్థ్యాన్ని పూర్తిగా మార్చాయి. నాణ్యత అద్భుతంగా ఉంది, సేవ కూడా చాలా బాగుంది.',
    'i have been using their agricultural machinery for over a year now. extremely durable and high-performing. best value for money in the industrial market.': 'వారి వ్యవసాయ యంత్రాలను ఒక సంవత్సరానికి పైగా ఉపయోగిస్తున్నాను. చాలా మన్నికైనవి మరియు బాగా పనిచేస్తాయి. ధరకు సరైన విలువ.',
    'outstanding power tools. the performance is consistent even under heavy load. highly recommend to any professional looking for reliable machinery.': 'అద్భుతమైన పవర్ టూల్స్. భారీ లోడులో కూడా పనితీరు స్థిరంగా ఉంటుంది. నమ్మకమైన యంత్రాలు కావాలనుకునే వారికి బాగా సిఫార్సు చేస్తాను.',
  },
  hi: {
    'test title': 'परीक्षण शीर्षक',
    'test tittle': 'परीक्षण शीर्षक',
    'test blog': 'परीक्षण ब्लॉग',
    'test summary': 'परीक्षण सारांश',
    'test category': 'परीक्षण श्रेणी',
    'test author name': 'परीक्षण लेखक नाम',
    'test authorname': 'परीक्षण लेखक नाम',
    'dheeraj': 'धीरज',
    'farm and garden': 'खेती और बगीचा',
    'farm & garden': 'खेती और बगीचा',
    'maintaining your power tools': 'अपने पावर टूल्स की देखभाल',
    'a simple maintenance routine can improve tool life, reduce downtime, and keep farm': 'सरल रखरखाव से औजारों की उम्र बढ़ती है, डाउनटाइम कम होता है और खेती का काम सुचारू रहता है',
    'shyam agro': 'श्याम एग्रो',
    'agriculture': 'कृषि',
    'customer': 'ग्राहक',
    'professional farmer': 'पेशेवर किसान',
    'agro-enterprise owner': 'कृषि उद्यम मालिक',
    'workshop owner': 'वर्कशॉप मालिक',
    'the industrial tools from shyam agro have completely transformed my farming efficiency. the quality is top-notch and the service is excellent.': 'श्याम एग्रो के औद्योगिक औजारों ने मेरी खेती की क्षमता पूरी तरह बदल दी है. गुणवत्ता बेहतरीन है और सेवा भी शानदार है.',
    'i have been using their agricultural machinery for over a year now. extremely durable and high-performing. best value for money in the industrial market.': 'मैं एक साल से अधिक समय से उनकी कृषि मशीनरी इस्तेमाल कर रहा हूं. बहुत टिकाऊ और शानदार प्रदर्शन वाली. कीमत के हिसाब से बेहतरीन.',
    'outstanding power tools. the performance is consistent even under heavy load. highly recommend to any professional looking for reliable machinery.': 'बेहतरीन पावर टूल्स. भारी लोड में भी प्रदर्शन स्थिर रहता है. भरोसेमंद मशीनरी चाहने वाले पेशेवरों के लिए अत्यधिक अनुशंसित.',
  },
};

const normalizeDynamicTextKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[“”"]/g, '')
    .replace(/^•\s*/, '')
    .replace(/\s*[\r\n]+\s*/g, ' ')
    .replace(/\s*:\s*/g, ': ')
    .replace(/\s*\/\s*/g, ' / ')
    .replace(/\s*&\s*/g, ' & ')
    .replace(/\s+/g, ' ')
    .replace(/[.!?]+$/g, '')
    .trim();

const getDynamicTextTranslation = (value, language) => {
  if (!value || language === 'en') return '';

  const key = normalizeDynamicTextKey(value);
  const translationsForLanguage = dynamicTextTranslations[language] || {};

  return (
    productCatalogTranslations[language]?.[key] ||
    translationsForLanguage[key] ||
    translationsForLanguage[key.replace(/\s*[>]+$/g, '').trim()] ||
    Object.entries(translationsForLanguage).find(([translationKey]) => (
      translationKey.length > 24 && key.startsWith(translationKey)
    ))?.[1] ||
    ''
  );
};

Object.assign(dynamicTextTranslations.te, {
  'manual operation (no electricity or fuel required)': 'మాన్యువల్ ఆపరేషన్ (విద్యుత్ లేదా ఇంధనం అవసరం లేదు)',
  'high-carbon hardened steel with anti-rust finish': 'యాంటీ-రస్ట్ ఫినిష్‌తో హై-కార్బన్ హార్డెండ్ స్టీల్',
  'ideal for weeding, soil loosening, cultivation, aeration, vegetable gardens, flower beds, orchards, nurseries, and small-scale farming': 'కలుపు తీయడం, మట్టిని సడలించడం, సాగు, గాలి ప్రసరణ, కూరగాయల తోటలు, పూల పడకలు, పండ్ల తోటలు, నర్సరీలు మరియు చిన్న స్థాయి వ్యవసాయానికి అనువైనది',
  'the cultivator is manufactured using high-quality steel components for superior strength, durability, and reliable field performance. it efficiently loosens soil, removes weeds, prepares seedbeds, and improves soil aeration, making it ideal for farms, gardens, orchards, and agricultural applications. its sturdy construction ensures long service life with minimal maintenance': 'ఈ కల్టివేటర్‌ను అధిక బలం, మన్నిక మరియు విశ్వసనీయమైన ఫీల్డ్ పనితీరు కోసం ఉన్నత నాణ్యత గల స్టీల్ భాగాలతో తయారు చేశారు. ఇది మట్టిని సమర్థంగా సడలిస్తుంది, కలుపును తొలగిస్తుంది, విత్తన పడకలను సిద్ధం చేస్తుంది మరియు మట్టిలో గాలి ప్రసరణను మెరుగుపరుస్తుంది. అందువల్ల ఇది పొలాలు, తోటలు, పండ్ల తోటలు మరియు వ్యవసాయ ఉపయోగాలకు అనువైనది. దీని దృఢమైన నిర్మాణం తక్కువ నిర్వహణతో దీర్ఘకాల సేవను అందిస్తుంది',
  '1 × cultivator machine': '1 × కల్టివేటర్ యంత్రం',
  '1 x cultivator machine': '1 × కల్టివేటర్ యంత్రం',
  'cultivator machine': 'కల్టివేటర్ యంత్రం',
  'cultivator tines': 'కల్టివేటర్ టైన్లు',
  'handle assembly': 'హ్యాండిల్ అసెంబ్లీ',
  'mounting hardware kit': 'మౌంటింగ్ హార్డ్‌వేర్ కిట్',
  'user manual': 'వినియోగదారు మాన్యువల్',
  'warranty card': 'వారంటీ కార్డు',
  'cultivator feature 1': 'కల్టివేటర్ లక్షణం 1',
  'cultivator feature 2': 'కల్టివేటర్ లక్షణం 2',
  'cultivator feature 3': 'కల్టివేటర్ లక్షణం 3',
  'cultivator feature 4': 'కల్టివేటర్ లక్షణం 4',
  'cultivator feature 5': 'కల్టివేటర్ లక్షణం 5',
  'cultivator feature 6': 'కల్టివేటర్ లక్షణం 6',
  'heavy-duty steel tines for efficient soil cultivation. ideal for loosening, aerating, and preparing soil. ergonomic handle for comfortable operation. rust-resistant powder-coated finish. strong and durable construction for long service life. suitable for vegetable gardens, nurseries, and farms. low maintenance and easy to use. designed for professional and home gardening applications': 'సమర్థవంతమైన మట్టి సాగు కోసం హెవీ-డ్యూటీ స్టీల్ టైన్లు. మట్టిని సడలించడానికి, గాలి ప్రసరణ మెరుగుపరచడానికి మరియు సిద్ధం చేయడానికి అనువైనది. సౌకర్యవంతమైన ఆపరేషన్ కోసం ఎర్గోనామిక్ హ్యాండిల్. తుప్పు నిరోధక పౌడర్-కోటెడ్ ఫినిష్. దీర్ఘకాల సేవ కోసం బలమైన మరియు మన్నికైన నిర్మాణం. కూరగాయల తోటలు, నర్సరీలు మరియు పొలాలకు అనుకూలం. తక్కువ నిర్వహణతో ఉపయోగించడానికి సులభం. వృత్తిపర మరియు ఇంటి తోటపని అవసరాలకు రూపొందించబడింది',
  'features an ultra-sharp 23-inch blade for effortless harvesting of grass and crops': 'గడ్డి మరియు పంటలను సులభంగా కోయడానికి అత్యంత పదునైన 23-అంగుళాల బ్లేడ్ కలిగి ఉంది',
  'the heavy duty scythe features a premium 23-inch high-carbon steel blade that delivers clean, efficient cutting for harvesting grass, crops, weeds, and forage. its robust construction provides excellent durability and long-lasting sharpness, while the ergonomic handle offers a comfortable grip for reduced fatigue during extended use. ideal for farms, gardens, orchards, landscaping, and agricultural maintenance': 'హెవీ డ్యూటీ స్కైథ్‌లో ప్రీమియం 23-అంగుళాల హై-కార్బన్ స్టీల్ బ్లేడ్ ఉంటుంది. ఇది గడ్డి, పంటలు, కలుపు మరియు పశుగ్రాసం కోయడానికి శుభ్రమైన, సమర్థవంతమైన కట్టింగ్‌ను అందిస్తుంది. దీని బలమైన నిర్మాణం అద్భుతమైన మన్నిక మరియు దీర్ఘకాల పదును ఇస్తుంది. ఎక్కువసేపు ఉపయోగించినప్పుడు అలసట తగ్గేందుకు ఎర్గోనామిక్ హ్యాండిల్ సౌకర్యవంతమైన పట్టును అందిస్తుంది. పొలాలు, తోటలు, పండ్ల తోటలు, ల్యాండ్‌స్కేపింగ్ మరియు వ్యవసాయ నిర్వహణకు అనువైనది',
  '1 × heavy duty scythe': '1 × హెవీ డ్యూటీ స్కైథ్',
  '1 x heavy duty scythe': '1 × హెవీ డ్యూటీ స్కైథ్',
  'heavy duty scythe': 'హెవీ డ్యూటీ స్కైథ్',
  '23-inch high-carbon steel blade': '23-అంగుళాల హై-కార్బన్ స్టీల్ బ్లేడ్',
  'heavy-duty 6 tooth spike harrow - durable agricultural tool for soil tilling, leveling & weeding': 'హెవీ-డ్యూటీ 6 టూత్ స్పైక్ హారో - మట్టి దున్నడం, లెవెలింగ్ మరియు కలుపు తీయడానికి మన్నికైన వ్యవసాయ పరికరం',
  'heavy-duty 6-tooth spike harrow designed for efficient soil tilling, leveling, weed removal, and seedbed preparation in agricultural fields': 'వ్యవసాయ పొలాల్లో సమర్థవంతమైన మట్టి దున్నడం, లెవెలింగ్, కలుపు తొలగింపు మరియు విత్తన పడకల సిద్ధం కోసం రూపొందించిన హెవీ-డ్యూటీ 6-టూత్ స్పైక్ హారో',
  'overall length: 620 mm working width: 360 mm spike length: 180 mm number of teeth: 6': 'మొత్తం పొడవు: 620 mm పని వెడల్పు: 360 mm స్పైక్ పొడవు: 180 mm పళ్ల సంఖ్య: 6',
  'manual operation / compatible with agricultural implements': 'మాన్యువల్ ఆపరేషన్ / వ్యవసాయ ఇంప్లిమెంట్లతో అనుకూలం',
  'high-carbon hardened steel with anti-rust powder-coated finish': 'తుప్పు నిరోధక పౌడర్-కోటెడ్ ఫినిష్‌తో హై-కార్బన్ హార్డెండ్ స్టీల్',
  'ideal for soil tilling, field leveling, weed removal, seedbed preparation, garden cultivation, orchard maintenance, and general agricultural land preparation': 'మట్టి దున్నడం, పొలం లెవెలింగ్, కలుపు తొలగింపు, విత్తన పడకల సిద్ధం, తోట సాగు, పండ్ల తోటల నిర్వహణ మరియు సాధారణ వ్యవసాయ భూమి సిద్ధం చేయడానికి అనువైనది',
  'six hardened steel spikes for effective soil penetration. ideal for tilling, leveling, and weed removal. heavy-duty welded steel construction. strong frame ensures excellent durability. easy attachment to compatible farm equipment. suitable for gardens, farms, and orchards. corrosion-resistant coating. designed for efficient soil preparation': 'సమర్థవంతమైన మట్టిలో చొచ్చుకుపోవడానికి ఆరు హార్డెండ్ స్టీల్ స్పైక్‌లు. దున్నడం, లెవెలింగ్ మరియు కలుపు తొలగింపుకు అనువైనది. హెవీ-డ్యూటీ వెల్డెడ్ స్టీల్ నిర్మాణం. బలమైన ఫ్రేమ్ అద్భుతమైన మన్నికను నిర్ధారిస్తుంది. అనుకూలమైన వ్యవసాయ పరికరాలకు సులభంగా అమర్చవచ్చు. తోటలు, పొలాలు మరియు పండ్ల తోటలకు అనుకూలం. తుప్పు నిరోధక కోటింగ్. సమర్థవంతమైన మట్టి సిద్ధం కోసం రూపొందించబడింది',
  'the heavy-duty 6 tooth spike harrow is manufactured from premium high-carbon steel to provide exceptional strength, durability, and long service life. its six sharp, hardened steel spikes effectively loosen compacted soil, remove weeds, level farmland, and prepare seedbeds for planting. the robust construction ensures reliable performance in demanding farming conditions, while its corrosion-resistant finish enhances durability. ideal for farms, gardens, orchards, and landscaping applications': 'హెవీ-డ్యూటీ 6 టూత్ స్పైక్ హారోను అసాధారణ బలం, మన్నిక మరియు దీర్ఘకాల సేవ కోసం ప్రీమియం హై-కార్బన్ స్టీల్‌తో తయారు చేశారు. దీని ఆరు పదునైన, హార్డెండ్ స్టీల్ స్పైక్‌లు గట్టిపడిన మట్టిని సమర్థవంతంగా సడలిస్తాయి, కలుపును తొలగిస్తాయి, వ్యవసాయ భూమిని లెవెల్ చేస్తాయి మరియు నాటడానికి విత్తన పడకలను సిద్ధం చేస్తాయి. బలమైన నిర్మాణం కఠినమైన వ్యవసాయ పరిస్థితుల్లో నమ్మకమైన పనితీరును నిర్ధారిస్తుంది, తుప్పు నిరోధక ఫినిష్ మన్నికను పెంచుతుంది. పొలాలు, తోటలు, పండ్ల తోటలు మరియు ల్యాండ్‌స్కేపింగ్ ఉపయోగాలకు అనువైనది',
  '1 × heavy-duty 6 tooth spike harrow': '1 × హెవీ-డ్యూటీ 6 టూత్ స్పైక్ హారో',
  '1 x heavy-duty 6 tooth spike harrow': '1 × హెవీ-డ్యూటీ 6 టూత్ స్పైక్ హారో',
  'mounting bracket': 'మౌంటింగ్ బ్రాకెట్',
  'fastening hardware': 'ఫాస్టెనింగ్ హార్డ్‌వేర్',
  'gramstrong republic solar street light 120w, 140 leds waterproof street light with solar panel for farm use with remote': 'గ్రామ్‌స్ట్రాంగ్ రిపబ్లిక్ సోలార్ స్ట్రీట్ లైట్ 120W, 140 LEDల వాటర్‌ప్రూఫ్ స్ట్రీట్ లైట్, ఫార్మ్ ఉపయోగం కోసం సోలార్ ప్యానెల్ మరియు రిమోట్‌తో',
  'high-efficiency solar led street light with remote control, designed for bright, energy-saving outdoor illumination without electricity': 'విద్యుత్ లేకుండా ప్రకాశవంతమైన, శక్తి ఆదా చేసే అవుట్‌డోర్ లైటింగ్ కోసం రూపొందించిన రిమోట్ కంట్రోల్‌తో హై-ఎఫిషియెన్సీ సోలార్ LED స్ట్రీట్ లైట్',
  'the sat solar led street light features a high-efficiency monocrystalline solar panel, ultra-bright led lighting, and a durable weatherproof aluminum housing. it automatically charges during the day and provides bright illumination at night. the included remote control allows easy adjustment of brightness, timer modes, and lighting functions. ideal for streets, gardens, farms, parking areas, campuses, warehouses, and commercial premises': 'SAT సోలార్ LED స్ట్రీట్ లైట్‌లో హై-ఎఫిషియెన్సీ మోనోక్రిస్టలైన్ సోలార్ ప్యానెల్, అల్ట్రా-బ్రైట్ LED లైటింగ్ మరియు మన్నికైన వెదర్‌ప్రూఫ్ అల్యూమినియం హౌసింగ్ ఉన్నాయి. ఇది పగలు ఆటోమేటిక్‌గా ఛార్జ్ అయి రాత్రి ప్రకాశవంతమైన వెలుతురు ఇస్తుంది. ఇందులో ఉన్న రిమోట్ కంట్రోల్ ద్వారా బ్రైట్‌నెస్, టైమర్ మోడ్‌లు మరియు లైటింగ్ ఫంక్షన్‌లను సులభంగా మార్చవచ్చు. వీధులు, తోటలు, పొలాలు, పార్కింగ్ ప్రాంతాలు, క్యాంపస్‌లు, గిడ్డంగులు మరియు కమర్షియల్ ప్రాంగణాలకు అనువైనది',
  '1 × solar led street light': '1 × సోలార్ LED స్ట్రీట్ లైట్',
  '1 × high-efficiency solar panel': '1 × హై-ఎఫిషియెన్సీ సోలార్ ప్యానెల్',
  '1 × mounting pole bracket': '1 × మౌంటింగ్ పోల్ బ్రాకెట్',
  '1 × remote control': '1 × రిమోట్ కంట్రోల్',
  'solar panel: 650 × 450 × 35 mm led light: 520 × 210 × 70 mm mounting arm length: 600 mm pole diameter compatibility: 60–76 mm': 'సోలార్ ప్యానెల్: 650 × 450 × 35 mm LED లైట్: 520 × 210 × 70 mm మౌంటింగ్ ఆర్మ్ పొడవు: 600 mm పోల్ డయామీటర్ అనుకూలత: 60–76 mm',
  'solar energy (rechargeable lithium battery)': 'సోలార్ ఎనర్జీ (రీచార్జబుల్ లిథియం బ్యాటరీ)',
  'die-cast aluminum body tempered glass solar panel monocrystalline solar cells ip65 weatherproof housing': 'డై-కాస్ట్ అల్యూమినియం బాడీ, టెంపర్డ్ గ్లాస్ సోలార్ ప్యానెల్, మోనోక్రిస్టలైన్ సోలార్ సెల్స్, IP65 వెదర్‌ప్రూఫ్ హౌసింగ్',
  'suitable for streets, gardens, farmhouses, campuses, parking lots, pathways, industrial areas, warehouses, and residential outdoor lighting': 'వీధులు, తోటలు, ఫార్మ్‌హౌస్‌లు, క్యాంపస్‌లు, పార్కింగ్ స్థలాలు, మార్గాలు, పరిశ్రమ ప్రాంతాలు, గిడ్డంగులు మరియు నివాస అవుట్‌డోర్ లైటింగ్‌కు అనుకూలం',
  'powerful 120w led illumination. 140 high-brightness leds for wide coverage. high-efficiency solar charging panel. waterproof ip-rated outdoor design. automatic dusk-to-dawn operation. wireless remote control included. long-lasting lithium battery backup. ideal for farms, streets, gardens, and warehouses': 'శక్తివంతమైన 120W LED వెలుతురు. విస్తృత కవరేజ్ కోసం 140 హై-బ్రైట్‌నెస్ LEDలు. హై-ఎఫిషియెన్సీ సోలార్ ఛార్జింగ్ ప్యానెల్. వాటర్‌ప్రూఫ్ IP-రేటెడ్ అవుట్‌డోర్ డిజైన్. సాయంత్రం నుంచి ఉదయం వరకు ఆటోమేటిక్ ఆపరేషన్. వైర్‌లెస్ రిమోట్ కంట్రోల్ కలదు. దీర్ఘకాలిక లిథియం బ్యాటరీ బ్యాకప్. పొలాలు, వీధులు, తోటలు మరియు గిడ్డంగులకు అనువైనది',
  'heavy-duty agricultural impact sprinkler for irrigation': 'నీటిపారుదల కోసం హెవీ-డ్యూటీ వ్యవసాయ ఇంపాక్ట్ స్ప్రింక్లర్',
  'heavy-duty agricultural impact sprinkler designed for uniform water distribution and efficient irrigation of farms, gardens, and landscapes': 'పొలాలు, తోటలు మరియు ల్యాండ్‌స్కేప్‌లకు సమాన నీటి పంపిణీ మరియు సమర్థవంతమైన నీటిపారుదల కోసం రూపొందించిన హెవీ-డ్యూటీ వ్యవసాయ ఇంపాక్ట్ స్ప్రింక్లర్',
  'the sat heavy-duty agricultural impact sprinkler is engineered from high-quality brass, aluminum alloy, and engineering-grade plastic for superior durability and corrosion resistance. its adjustable spray angle and impact-driven mechanism provide consistent, wide-area water coverage while minimizing water wastage. suitable for agricultural fields, lawns, orchards, nurseries, sports grounds, and landscape irrigation, this sprinkler delivers reliable performance even under high-pressure conditions': 'SAT హెవీ-డ్యూటీ వ్యవసాయ ఇంపాక్ట్ స్ప్రింక్లర్‌ను అధిక నాణ్యత గల బ్రాస్, అల్యూమినియం అలాయ్ మరియు ఇంజినీరింగ్-గ్రేడ్ ప్లాస్టిక్‌తో తయారు చేశారు, అందువల్ల ఇది మన్నికగా మరియు తుప్పు నిరోధకంగా ఉంటుంది. దీని సర్దుబాటు చేయగల స్ప్రే యాంగిల్ మరియు ఇంపాక్ట్ మెకానిజం నీటి వృథాను తగ్గిస్తూ స్థిరమైన, విస్తృత ప్రాంత కవరేజ్ ఇస్తాయి. వ్యవసాయ పొలాలు, లాన్లు, పండ్ల తోటలు, నర్సరీలు, స్పోర్ట్స్ గ్రౌండ్లు మరియు ల్యాండ్‌స్కేప్ నీటిపారుదలకు అనుకూలం. అధిక పీడన పరిస్థితుల్లో కూడా నమ్మకమైన పనితీరును అందిస్తుంది',
  '1 × heavy-duty agricultural impact sprinkler': '1 × హెవీ-డ్యూటీ వ్యవసాయ ఇంపాక్ట్ స్ప్రింక్లర్',
  'adjustable nozzle set': 'సర్దుబాటు చేయగల నాజిల్ సెట్',
  'mounting connector': 'మౌంటింగ్ కనెక్టర్',
  'water pressure': 'నీటి పీడనం',
  'brass, aluminum alloy, stainless steel spring & engineering-grade plastic': 'బ్రాస్, అల్యూమినియం అలాయ్, స్టెయిన్‌లెస్ స్టీల్ స్ప్రింగ్ మరియు ఇంజినీరింగ్-గ్రేడ్ ప్లాస్టిక్',
  'suitable for irrigation of farms, vegetable fields, orchards, gardens, nurseries, lawns, golf courses, parks, and landscaping projects': 'పొలాలు, కూరగాయల పొలాలు, పండ్ల తోటలు, తోటలు, నర్సరీలు, లాన్లు, గోల్ఫ్ కోర్సులు, పార్కులు మరియు ల్యాండ్‌స్కేపింగ్ ప్రాజెక్టుల నీటిపారుదలకు అనుకూలం',
  'full-circle and part-circle irrigation adjustment. heavy-duty brass and engineering plastic construction. uniform water distribution with long spraying distance. suitable for medium and large agricultural fields. easy installation on irrigation pipelines. adjustable spray angle and coverage. uv-resistant and corrosion-resistant materials. ideal for farms, gardens, orchards, and landscapes': 'ఫుల్-సర్కిల్ మరియు పార్ట్-సర్కిల్ నీటిపారుదల సర్దుబాటు. హెవీ-డ్యూటీ బ్రాస్ మరియు ఇంజినీరింగ్ ప్లాస్టిక్ నిర్మాణం. ఎక్కువ స్ప్రేయింగ్ దూరంతో సమాన నీటి పంపిణీ. మధ్యస్థ మరియు పెద్ద వ్యవసాయ పొలాలకు అనుకూలం. నీటిపారుదల పైప్‌లైన్‌లపై సులభ ఇన్‌స్టాలేషన్. సర్దుబాటు చేయగల స్ప్రే యాంగిల్ మరియు కవరేజ్. UV-నిరోధక మరియు తుప్పు నిరోధక పదార్థాలు. పొలాలు, తోటలు, పండ్ల తోటలు మరియు ల్యాండ్‌స్కేప్‌లకు అనువైనది',
  'farmio grass cutting machine (without battery) / corded brush cutter machine': 'ఫార్మియో గ్రాస్ కట్టింగ్ మెషిన్ (బ్యాటరీ లేకుండా) / కార్డెడ్ బ్రష్ కట్టర్ మెషిన్',
  'lightweight and efficient brush cutter designed for cutting grass, weeds, and light vegetation with an adjustable cutting head for easy operation': 'సులభ ఆపరేషన్ కోసం సర్దుబాటు చేయగల కట్టింగ్ హెడ్‌తో గడ్డి, కలుపు మరియు తేలికపాటి మొక్కలను కత్తిరించడానికి రూపొందించిన తేలికైన మరియు సమర్థవంతమైన బ్రష్ కట్టర్',
  'the farmio grass cutting machine is a compact, high-performance brush cutter suitable for home gardens, farms, orchards, and roadside maintenance. it features a durable copper winding motor, stainless steel blade, adjustable cutting head, and ergonomic design for comfortable single-handed operation. this model is supplied without a battery and is designed to operate using a compatible 12v battery or 12v battery sprayer. it is ideal for trimming weeds, grass, crop edges, pathways, and fence lines': 'ఫార్మియో గ్రాస్ కట్టింగ్ మెషిన్ ఇంటి తోటలు, పొలాలు, పండ్ల తోటలు మరియు రోడ్డు పక్క నిర్వహణకు అనుకూలమైన కాంపాక్ట్, హై-పర్ఫార్మెన్స్ బ్రష్ కట్టర్. ఇందులో మన్నికైన కాపర్ వైండింగ్ మోటార్, స్టెయిన్‌లెస్ స్టీల్ బ్లేడ్, సర్దుబాటు చేయగల కట్టింగ్ హెడ్ మరియు ఒక చేతితో సౌకర్యవంతంగా వాడేందుకు ఎర్గోనామిక్ డిజైన్ ఉన్నాయి. ఈ మోడల్ బ్యాటరీ లేకుండా సరఫరా చేయబడుతుంది మరియు అనుకూలమైన 12V బ్యాటరీ లేదా 12V బ్యాటరీ స్ప్రేయర్‌తో పనిచేయడానికి రూపొందించబడింది. కలుపు, గడ్డి, పంట అంచులు, మార్గాలు మరియు కంచె లైన్ల ట్రిమ్మింగ్‌కు అనువైనది',
  '12v dc battery (not included) / external 12v power supply': '12V DC బ్యాటరీ (చేర్చబడలేదు) / బాహ్య 12V పవర్ సప్లై',
  'premium quality thermal fogging machine, 16 litre': 'ప్రీమియం క్వాలిటీ థర్మల్ ఫాగింగ్ మెషిన్, 16 లీటర్',
  'high-performance 16-litre thermal fogging machine designed for efficient pest, mosquito, and sanitation control across agricultural, industrial, and public areas': 'వ్యవసాయ, పరిశ్రమ మరియు ప్రజా ప్రాంతాల్లో పురుగులు, దోమలు మరియు శానిటేషన్ నియంత్రణ కోసం రూపొందించిన హై-పర్ఫార్మెన్స్ 16-లీటర్ థర్మల్ ఫాగింగ్ మెషిన్',
  'petrol engine': 'పెట్రోల్ ఇంజిన్',
  'petrol-powered combustion engine with manual ignition': 'మాన్యువల్ ఇగ్నిషన్‌తో పెట్రోల్ ఆధారిత కంబషన్ ఇంజిన్',
  'high-grade stainless steel, aluminum alloy, and industrial-grade metal components': 'హై-గ్రేడ్ స్టెయిన్‌లెస్ స్టీల్, అల్యూమినియం అలాయ్ మరియు ఇండస్ట్రియల్-గ్రేడ్ మెటల్ భాగాలు',
});

Object.assign(dynamicTextTranslations.hi, {
  'manual operation (no electricity or fuel required)': 'मैनुअल संचालन (बिजली या ईंधन की आवश्यकता नहीं)',
  'high-carbon hardened steel with anti-rust finish': 'एंटी-रस्ट फिनिश के साथ हाई-कार्बन हार्डेंड स्टील',
  'ideal for weeding, soil loosening, cultivation, aeration, vegetable gardens, flower beds, orchards, nurseries, and small-scale farming': 'निराई, मिट्टी को ढीला करने, खेती, हवा प्रवाह, सब्जी बगीचों, फूलों की क्यारियों, बागों, नर्सरी और छोटे स्तर की खेती के लिए आदर्श',
  'the cultivator is manufactured using high-quality steel components for superior strength, durability, and reliable field performance. it efficiently loosens soil, removes weeds, prepares seedbeds, and improves soil aeration, making it ideal for farms, gardens, orchards, and agricultural applications. its sturdy construction ensures long service life with minimal maintenance': 'यह कल्टीवेटर बेहतर मजबूती, टिकाऊपन और भरोसेमंद फील्ड प्रदर्शन के लिए उच्च गुणवत्ता वाले स्टील घटकों से बनाया गया है. यह मिट्टी को प्रभावी ढंग से ढीला करता है, खरपतवार हटाता है, बीज क्यारियां तैयार करता है और मिट्टी में हवा के प्रवाह को बेहतर बनाता है, जिससे यह खेतों, बगीचों, बागों और कृषि उपयोगों के लिए आदर्श है. इसकी मजबूत बनावट कम रखरखाव के साथ लंबी सेवा आयु सुनिश्चित करती है',
  '1 × cultivator machine': '1 × कल्टीवेटर मशीन',
  '1 x cultivator machine': '1 × कल्टीवेटर मशीन',
  'cultivator machine': 'कल्टीवेटर मशीन',
  'cultivator tines': 'कल्टीवेटर टाइन',
  'handle assembly': 'हैंडल असेंबली',
  'mounting hardware kit': 'माउंटिंग हार्डवेयर किट',
  'user manual': 'उपयोगकर्ता मैनुअल',
  'warranty card': 'वारंटी कार्ड',
  'cultivator feature 1': 'कल्टीवेटर विशेषता 1',
  'cultivator feature 2': 'कल्टीवेटर विशेषता 2',
  'cultivator feature 3': 'कल्टीवेटर विशेषता 3',
  'cultivator feature 4': 'कल्टीवेटर विशेषता 4',
  'cultivator feature 5': 'कल्टीवेटर विशेषता 5',
  'cultivator feature 6': 'कल्टीवेटर विशेषता 6',
  'heavy-duty steel tines for efficient soil cultivation. ideal for loosening, aerating, and preparing soil. ergonomic handle for comfortable operation. rust-resistant powder-coated finish. strong and durable construction for long service life. suitable for vegetable gardens, nurseries, and farms. low maintenance and easy to use. designed for professional and home gardening applications': 'प्रभावी मिट्टी की खेती के लिए हेवी-ड्यूटी स्टील टाइन. मिट्टी को ढीला करने, हवा पहुंचाने और तैयार करने के लिए आदर्श. आरामदायक संचालन के लिए एर्गोनॉमिक हैंडल. जंग-रोधी पाउडर-कोटेड फिनिश. लंबी सेवा आयु के लिए मजबूत और टिकाऊ निर्माण. सब्जी बगीचों, नर्सरी और खेतों के लिए उपयुक्त. कम रखरखाव और उपयोग में आसान. पेशेवर और घरेलू बागवानी उपयोगों के लिए डिजाइन किया गया',
  'features an ultra-sharp 23-inch blade for effortless harvesting of grass and crops': 'घास और फसलों की आसान कटाई के लिए अल्ट्रा-शार्प 23-इंच ब्लेड के साथ आता है',
  'the heavy duty scythe features a premium 23-inch high-carbon steel blade that delivers clean, efficient cutting for harvesting grass, crops, weeds, and forage. its robust construction provides excellent durability and long-lasting sharpness, while the ergonomic handle offers a comfortable grip for reduced fatigue during extended use. ideal for farms, gardens, orchards, landscaping, and agricultural maintenance': 'हेवी ड्यूटी साइथ में प्रीमियम 23-इंच हाई-कार्बन स्टील ब्लेड है, जो घास, फसलों, खरपतवार और चारे की कटाई के लिए साफ और प्रभावी कटिंग देता है. इसकी मजबूत बनावट उत्कृष्ट टिकाऊपन और लंबे समय तक धार बनाए रखती है, जबकि एर्गोनॉमिक हैंडल लंबे उपयोग के दौरान थकान कम करने के लिए आरामदायक पकड़ देता है. खेतों, बगीचों, बागों, लैंडस्केपिंग और कृषि रखरखाव के लिए आदर्श',
  '1 × heavy duty scythe': '1 × हेवी ड्यूटी साइथ',
  '1 x heavy duty scythe': '1 × हेवी ड्यूटी साइथ',
  'heavy duty scythe': 'हेवी ड्यूटी साइथ',
  '23-inch high-carbon steel blade': '23-इंच हाई-कार्बन स्टील ब्लेड',
  'heavy-duty 6 tooth spike harrow - durable agricultural tool for soil tilling, leveling & weeding': 'हेवी-ड्यूटी 6 टूथ स्पाइक हैरो - मिट्टी की जुताई, लेवलिंग और निराई के लिए टिकाऊ कृषि उपकरण',
  'heavy-duty 6-tooth spike harrow designed for efficient soil tilling, leveling, weed removal, and seedbed preparation in agricultural fields': 'कृषि खेतों में प्रभावी मिट्टी जुताई, लेवलिंग, खरपतवार हटाने और बीज क्यारी तैयार करने के लिए डिजाइन किया गया हेवी-ड्यूटी 6-टूथ स्पाइक हैरो',
  'overall length: 620 mm working width: 360 mm spike length: 180 mm number of teeth: 6': 'कुल लंबाई: 620 mm कार्य चौड़ाई: 360 mm स्पाइक लंबाई: 180 mm दांतों की संख्या: 6',
  'manual operation / compatible with agricultural implements': 'मैनुअल संचालन / कृषि इम्प्लीमेंट्स के साथ संगत',
  'high-carbon hardened steel with anti-rust powder-coated finish': 'जंग-रोधी पाउडर-कोटेड फिनिश के साथ हाई-कार्बन हार्डेंड स्टील',
  'ideal for soil tilling, field leveling, weed removal, seedbed preparation, garden cultivation, orchard maintenance, and general agricultural land preparation': 'मिट्टी की जुताई, खेत की लेवलिंग, खरपतवार हटाने, बीज क्यारी तैयार करने, बगीचा खेती, बाग रखरखाव और सामान्य कृषि भूमि तैयारी के लिए आदर्श',
  'six hardened steel spikes for effective soil penetration. ideal for tilling, leveling, and weed removal. heavy-duty welded steel construction. strong frame ensures excellent durability. easy attachment to compatible farm equipment. suitable for gardens, farms, and orchards. corrosion-resistant coating. designed for efficient soil preparation': 'प्रभावी मिट्टी प्रवेश के लिए छह हार्डेंड स्टील स्पाइक. जुताई, लेवलिंग और खरपतवार हटाने के लिए आदर्श. हेवी-ड्यूटी वेल्डेड स्टील निर्माण. मजबूत फ्रेम उत्कृष्ट टिकाऊपन सुनिश्चित करता है. संगत कृषि उपकरणों से आसानी से जोड़ा जा सकता है. बगीचों, खेतों और बागों के लिए उपयुक्त. जंग-रोधी कोटिंग. प्रभावी मिट्टी तैयारी के लिए डिजाइन किया गया',
  'the heavy-duty 6 tooth spike harrow is manufactured from premium high-carbon steel to provide exceptional strength, durability, and long service life. its six sharp, hardened steel spikes effectively loosen compacted soil, remove weeds, level farmland, and prepare seedbeds for planting. the robust construction ensures reliable performance in demanding farming conditions, while its corrosion-resistant finish enhances durability. ideal for farms, gardens, orchards, and landscaping applications': 'हेवी-ड्यूटी 6 टूथ स्पाइक हैरो को असाधारण मजबूती, टिकाऊपन और लंबी सेवा आयु देने के लिए प्रीमियम हाई-कार्बन स्टील से बनाया गया है. इसके छह तेज, हार्डेंड स्टील स्पाइक सख्त मिट्टी को प्रभावी ढंग से ढीला करते हैं, खरपतवार हटाते हैं, खेत को समतल करते हैं और रोपाई के लिए बीज क्यारियां तैयार करते हैं. मजबूत निर्माण कठिन खेती स्थितियों में भरोसेमंद प्रदर्शन सुनिश्चित करता है, जबकि जंग-रोधी फिनिश टिकाऊपन बढ़ाती है. खेतों, बगीचों, बागों और लैंडस्केपिंग उपयोगों के लिए आदर्श',
  '1 × heavy-duty 6 tooth spike harrow': '1 × हेवी-ड्यूटी 6 टूथ स्पाइक हैरो',
  '1 x heavy-duty 6 tooth spike harrow': '1 × हेवी-ड्यूटी 6 टूथ स्पाइक हैरो',
  'mounting bracket': 'माउंटिंग ब्रैकेट',
  'fastening hardware': 'फास्टनिंग हार्डवेयर',
});

Object.assign(dynamicTextTranslations.te, {
  'rajesh kumar': 'రాజేష్ కుమార్',
  'anita sharma': 'అనిత శర్మ',
  'suresh singh': 'సురేష్ సింగ్',
  'the industrial tools from shyam agro have completely transformed my farming efficiency. the quality is top-notch and the service is excellent': 'శ్యామ్ ఆగ్రో పరిశ్రమ పరికరాలు నా వ్యవసాయ సామర్థ్యాన్ని పూర్తిగా మార్చాయి. నాణ్యత అద్భుతంగా ఉంది, సేవ కూడా చాలా బాగుంది.',
  "i've been using their agricultural machinery for over a year now. extremely durable and high-performing. best value for money in the industrial market": 'వారి వ్యవసాయ యంత్రాలను ఒక సంవత్సరానికి పైగా ఉపయోగిస్తున్నాను. చాలా మన్నికైనవి మరియు బాగా పనిచేస్తాయి. ధరకు సరైన విలువ.',
  'outstanding power tools. the performance is consistent even under heavy load. highly recommend to any professional looking for reliable machinery': 'అద్భుతమైన పవర్ టూల్స్. భారీ లోడ్‌లో కూడా పనితీరు స్థిరంగా ఉంటుంది. నమ్మకమైన యంత్రాలు కావాలనుకునే వృత్తిపరులకు బాగా సిఫార్సు చేస్తాను.',
  'seeds and planatation': 'విత్తనాలు మరియు తోటల పెంపకం',
  'seeds and plantation': 'విత్తనాలు మరియు తోటల పెంపకం',
  'seeds are more highly valuable': 'విత్తనాలు చాలా విలువైనవి',
  'a simple maintenance routine can improve tool life, reduce downtime, and keep farm work safer': 'సరళమైన నిర్వహణ పద్ధతి పరికరాల ఆయుష్షును పెంచి, పనిలో అంతరాయాన్ని తగ్గించి, వ్యవసాయ పనిని మరింత సురక్షితంగా ఉంచుతుంది',
});

Object.assign(dynamicTextTranslations.hi, {
  'rajesh kumar': 'राजेश कुमार',
  'anita sharma': 'अनीता शर्मा',
  'suresh singh': 'सुरेश सिंह',
  'the industrial tools from shyam agro have completely transformed my farming efficiency. the quality is top-notch and the service is excellent': 'श्याम एग्रो के औद्योगिक औजारों ने मेरी खेती की क्षमता पूरी तरह बदल दी है. गुणवत्ता बेहतरीन है और सेवा भी शानदार है.',
  "i've been using their agricultural machinery for over a year now. extremely durable and high-performing. best value for money in the industrial market": 'मैं एक साल से अधिक समय से उनकी कृषि मशीनरी इस्तेमाल कर रहा हूं. बहुत टिकाऊ और शानदार प्रदर्शन वाली. कीमत के हिसाब से बेहतरीन.',
  'outstanding power tools. the performance is consistent even under heavy load. highly recommend to any professional looking for reliable machinery': 'बेहतरीन पावर टूल्स. भारी लोड में भी प्रदर्शन स्थिर रहता है. भरोसेमंद मशीनरी चाहने वाले पेशेवरों के लिए अत्यधिक अनुशंसित.',
  'seeds and planatation': 'बीज और पौधारोपण',
  'seeds and plantation': 'बीज और पौधारोपण',
  'seeds are more highly valuable': 'बीज बहुत अधिक मूल्यवान होते हैं',
  'a simple maintenance routine can improve tool life, reduce downtime, and keep farm work safer': 'सरल रखरखाव से औजारों की उम्र बढ़ती है, डाउनटाइम कम होता है और खेती का काम अधिक सुरक्षित रहता है',
});

const translateKnownDynamicText = (value, language) => {
  return getDynamicTextTranslation(value, language);
};

const productTranslations = {
  te: {
    'cultivator-01': {
      name: 'కల్టివేటర్',
      displayName: 'ప్రీమియం సాయిల్ కల్టివేటర్ & ప్లౌ',
      shortDesc: 'మట్టిని గాలి పట్టించడానికి మరియు సిద్ధం చేయడానికి హై-ప్రెసిషన్ కల్టివేటర్.',
      longDesc: 'లోతైన మట్టి పనికి రూపొందించిన ఈ కల్టివేటర్‌లో గట్టిపడిన స్టీల్ బ్లేడ్లు మరియు సర్దుబాటు చేయగల డెప్త్ కంట్రోల్ ఉన్నాయి.',
    },
    'tiller-01': {
      name: 'పవర్ టిల్లర్',
      displayName: 'ఇండస్ట్రియల్ స్ట్రెంగ్త్ రోటరీ పవర్ టిల్లర్',
      shortDesc: 'భూమి సిద్ధం చేయడం మరియు కలుపు తొలగింపుకు శక్తివంతమైన రోటరీ టిల్లర్.',
    },
    'irrigation-01': {
      name: 'ఇరిగేషన్ సిస్టమ్',
      displayName: 'అధునాతన వ్యవసాయ ఇరిగేషన్ యూనిట్',
      shortDesc: 'హై-ప్రెషర్ స్ప్రింక్లర్లు మరియు పంపిణీ పైపులతో ఆధునిక ఇరిగేషన్ సిస్టమ్.',
    },
    'rain-gun-01': {
      name: 'రెయిన్ గన్',
      displayName: 'లాంగ్ రేంజ్ వ్యవసాయ రెయిన్ గన్',
      shortDesc: 'పెద్ద పొలాలకు ఎక్కువ దూరం నీటిని చల్లే రెయిన్ గన్ స్ప్రింక్లర్.',
    },
  },
  hi: {
    'cultivator-01': {
      name: 'कल्टीवेटर',
      displayName: 'प्रीमियम सॉइल कल्टीवेटर और प्लाउ',
      shortDesc: 'मिट्टी को हवादार और तैयार करने के लिए हाई-प्रिसिजन कल्टीवेटर.',
      longDesc: 'गहरी मिट्टी में काम के लिए बनाया गया यह कल्टीवेटर मजबूत स्टील ब्लेड और एडजस्टेबल डेप्थ कंट्रोल के साथ आता है.',
    },
    'tiller-01': {
      name: 'पावर टिलर',
      displayName: 'इंडस्ट्रियल स्ट्रेंथ रोटरी पावर टिलर',
      shortDesc: 'जमीन की तैयारी और निराई के लिए शक्तिशाली रोटरी टिलर.',
    },
    'irrigation-01': {
      name: 'सिंचाई सिस्टम',
      displayName: 'एडवांस्ड कृषि सिंचाई यूनिट',
      shortDesc: 'हाई-प्रेशर स्प्रिंकलर और वितरण पाइप वाला आधुनिक सिंचाई सिस्टम.',
    },
    'rain-gun-01': {
      name: 'रेन गन',
      displayName: 'लॉन्ग रेंज कृषि रेन गन',
      shortDesc: 'बड़े खेतों में दूर तक पानी देने वाला हाई-थ्रो रेन गन स्प्रिंकलर.',
    },
  },
};

const categoryTranslations = {
  te: {
    // Slugs
    'farm-and-garden': { name: 'వ్యవసాయం & తోటపని', description: 'వ్యవసాయం, తోటల పెంపకం మరియు మొక్కల సంరక్షణ కోసం పరికరాలు.' },
    'sprayers': { name: 'స్ప్రేయర్లు', description: 'పురుగుల మందులు మరియు ద్రవాలు పిచికారీ చేయడానికి ఉపయోగపడే స్ప్రేయర్లు.' },
    'landscaping': { name: 'ల్యాండ్‌స్కేపింగ్', description: 'నేల చదును, తోట అలంకరణ మరియు ల్యాండ్‌స్కేపింగ్ పరికరాలు.' },
    'pumps-and-motors': { name: 'పంపులు & మోటార్లు', description: 'నీటి సరఫరా, బోర్‌వెల్లు మరియు డ్రిప్ కోసం మోటార్లు మరియు పంపులు.' },
    'food-processing': { name: 'ఆహార ప్రాసెసింగ్', description: 'ఆహార పదార్థాల ప్రాసెసింగ్ మరియు కోత తర్వాత పనులకు యంత్రాలు.' },
    'commercial-kitchen-equipment': { name: 'వాణిజ్య వంటగది పరికరాలు', description: 'హోటళ్ళు మరియు క్యాటరింగ్ కోసం నాణ్యమైన వంటగది పరికరాలు.' },
    'food-packaging-tools': { name: 'ఆహార ప్యాకేజింగ్ పరికరాలు', description: 'ఆహార పదార్థాల ప్యాకేజింగ్ మరియు ప్యాకింగ్ టూల్స్.' },
    'workshop,-diy-and-mro': { name: 'వర్క్‌షాప్, డీఐవై & ఎంఆర్ఓ', description: 'మరమ్మతులు, వర్క్‌షాప్ మరియు దినచర్య పనుల కోసం ఎంఆర్ఓ టూల్స్.' },
    'power-tools': { name: 'పవర్ టూల్స్', description: 'డ్రిల్లింగ్, కట్టింగ్ మరియు వర్క్‌షాప్ పవర్ పరికరాలు.' },
    'fertilizers': { name: 'ఎరువులు', description: 'పంటల ఎదుగుదలకు ఉపయోగపడే వివిధ రకాల సేంద్రీయ ఎరువులు.' },
    'neutrilizer': { name: 'న్యూట్రలైజర్', description: 'నేల సారాన్ని పెంచడానికి మరియు సమతుల్యతకు సహాయపడే న్యూట్రలైజర్లు.' },

    // IDs
    '7': { name: 'వ్యవసాయం & తోటపని', description: 'వ్యవసాయం, తోటల పెంపకం మరియు మొక్కల సంరక్షణ కోసం పరికరాలు.' },
    '8': { name: 'స్ప్రేయర్లు', description: 'పురుగుల మందులు మరియు ద్రవాలు పిచికారీ చేయడానికి ఉపయోగపడే స్ప్రేయర్లు.' },
    '9': { name: 'ల్యాండ్‌స్కేపింగ్', description: 'నేల చదును, తోట అలంకరణ మరియు ల్యాండ్‌స్కేపింగ్ పరికరాలు.' },
    '10': { name: 'పంపులు & మోటార్లు', description: 'నీటి సరఫరా, బోర్‌వెల్లు మరియు డ్రిప్ కోసం మోటార్లు మరియు పంపులు.' },
    '11': { name: 'ఆహార ప్రాసెసింగ్', description: 'ఆహార పదార్థాల ప్రాసెసింగ్ మరియు కోత తర్వాత పనులకు యంత్రాలు.' },
    '12': { name: 'వాణిజ్య వంటగది పరికరాలు', description: 'హోటళ్ళు మరియు క్యాటరింగ్ కోసం నాణ్యమైన వంటగది పరికరాలు.' },
    '13': { name: 'ఆహార ప్యాకేజింగ్ పరికరాలు', description: 'ఆహార పదార్థాల ప్యాకేజింగ్ మరియు ప్యాకింగ్ టూల్స్.' },
    '14': { name: 'వర్క్‌షాప్, డీఐవై & ఎంఆర్ఓ', description: 'మరమ్మతులు, వర్క్‌షాప్ మరియు దినచర్య పనుల కోసం ఎంఆర్ఓ టూల్స్.' },
    '15': { name: 'పవర్ టూಲ್స్', description: 'డ్రిల్లింగ్, కట్టింగ్ మరియు వర్క్‌షాప్ పవర్ పరికరాలు.' },
    '17': { name: 'ఎరువులు', description: 'పంటల ఎదుగుదలకు ఉపయోగపడే వివిధ రకాల సేంద్రీయ ఎరువులు.' },
    '18': { name: 'న్యూట్రలైజర్', description: 'నేల సారాన్ని పెంచడానికి మరియు సమతుల్యతకు సహాయపడే న్యూట్రలైజర్లు.' },
  },
  hi: {
    // Slugs
    'farm-and-garden': { name: 'फार्म और गार्डन', description: 'खेती, बागवानी और पौधों की देखभाल के लिए उपकरण.' },
    'sprayers': { name: 'स्प्रेयर', description: 'कीटनाशकों और रसायनों के छिड़काव के लिए स्प्रेयर.' },
    'landscaping': { name: 'लैंडस्केपिंग', description: 'मिट्टी समतल करने, उद्यान सजावट और लैंडस्केपिंग उपकरण.' },
    'pumps-and-motors': { name: 'पंप और मोटर', description: 'जलापूर्ति, बोरवेल और ड्रिप सिंचाई के लिए मोटर और पंप.' },
    'food-processing': { name: 'खाद्य प्रसंस्करण', description: 'खाद्य सामग्री के प्रसंस्करण और कटाई के बाद के काम की मशीनें.' },
    'commercial-kitchen-equipment': { name: 'वाणिज्यिक रसोई उपकरण', description: 'होटल और खानपान के लिए गुणवत्तापूर्ण रसोई उपकरण.' },
    'food-packaging-tools': { name: 'खाद्य पैकेजिंग उपकरण', description: 'खाद्य पैकेजिंग और पैकिंग टूल.' },
    'workshop,-diy-and-mro': { name: 'वर्कशॉप, डीआईवाई और एमआरओ', description: 'मरम्मत, वर्कशॉप और दैनिक रखरखाव के लिए एमआरओ टूल.' },
    'power-tools': { name: 'पावर टूल्स', description: 'ड्रिलिंग, कटिंग और वर्कशॉप पावर उपकरण.' },
    'fertilizers': { name: 'उर्वरक', description: 'फसल की वृद्धि के लिए जैविक और रासायनिक उर्वरक.' },
    'neutrilizer': { name: 'न्यूट्रलाइजज़र', description: 'मिट्टी के स्वास्थ्य और पीएच संतुलन के लिए न्यूट्रलाइजज़र.' },

    // IDs
    '7': { name: 'फार्म और गार्डन', description: 'खेती, बागवानी और पौधों की देखभाल के लिए उपकरण.' },
    '8': { name: 'स्प्रेयर', description: 'कीटनाशकों और रसायनों के छिड़काव के लिए स्प्रेयर.' },
    '9': { name: 'लैंडस्केपिंग', description: 'मिट्टी समतल करने, उद्यान सजावट और लैंडस्केपिंग उपकरण.' },
    '10': { name: 'पंप और मोटर', description: 'जलापूर्ति, बोरवेल और ड्रिप सिंचाई के लिए मोटर और पंप.' },
    '11': { name: 'खाद्य प्रसंस्करण', description: 'खाद्य सामग्री के प्रसंस्करण और कटाई के बाद के काम की मशीनें.' },
    '12': { name: 'वाणिज्यिक रसोई उपकरण', description: 'होटल और खानपान के लिए गुणवत्तापूर्ण रसोई उपकरण.' },
    '13': { name: 'खाद्य पैकेजिंग उपकरण', description: 'खाद्य पैकेजिंग और पैकिंग टूल.' },
    '14': { name: 'वर्कशॉप, डीआईवाई और एमआरओ', description: 'मरम्मत, वर्कशॉप और दैनिक रखरखाव के लिए एमआरओ टूल.' },
    '15': { name: 'पावर टूल्स', description: 'ड्रिलिंग, कटिंग और वर्कशॉप पावर उपकरण.' },
    '17': { name: 'उर्वरक', description: 'फसल की वृद्धि के लिए जैविक और रासायनिक उर्वरक.' },
    '18': { name: 'न्यूट्रलाइजज़र', description: 'मिट्टी के स्वास्थ्य और पीएच संतुलन के लिए न्यूट्रलाइजज़र.' },
  },
};

const subcategoryTranslations = {
  te: {
    'Cultivators / Tillers / Weeders': 'కల్టివేటర్లు / టిల్లర్లు / వీడర్లు',
    'Special Farm Tools': 'ప్రత్యేక వ్యవసాయ పరికరాలు',
    'Garden Tools': 'తోట పరికరాలు',
    'Brush Cutter': 'బ్రష్ కట్టర్',
    'Sprayers': 'స్ప్రేయర్లు',
    'Fogging Machine': 'ఫాగింగ్ యంత్రం',
    'Solar Products': 'సౌర విద్యుత్ ఉత్పత్తులు',
    'Inputs | Supplies': 'ఇన్‌పుట్‌లు | సామాగ్రి',
    'Agriculture Pumps': 'వ్యవసాయ పంపులు',
    'Harvester': 'హార్వెస్టర్',
    'Pipe & Sprinklers': 'పైపులు & స్ప్రింక్లర్లు',
    'Aquaculture': 'ఆక్వాకల్చర్',
    'Mist Blower': 'మిస్ట్ బ్లోయర్',
    'Power Sprayer': 'పవర్ స్ప్రేయర్',
    'Agricultural Sprayers': 'వ్యవసాయ స్ప్రేయర్లు'
  },
  hi: {
    'Cultivators / Tillers / Weeders': 'कल्टीवेटर / टिलर / वीडर',
    'Special Farm Tools': 'विशेष कृषि उपकरण',
    'Garden Tools': 'बागवानी के उपकरण',
    'Brush Cutter': 'ब्रश कटर',
    'Sprayers': 'स्प्रेयर',
    'Fogging Machine': 'फॉगिंग मशीन',
    'Solar Products': 'सौर उत्पाद',
    'Inputs | Supplies': 'इनपुट | आपूर्ति',
    'Agriculture Pumps': 'कृषि पंप',
    'Harvester': 'हार्वेस्टर',
    'Pipe & Sprinklers': 'पाइप और स्प्रिंकलर',
    'Aquaculture': 'जलीय कृषि (एक्वाकल्चर)',
    'Mist Blower': 'मिस्ट ब्लोअर',
    'Power Sprayer': 'पावर स्प्रेयर',
    'Agricultural Sprayers': 'कृषि स्प्रेयर'
  }
};

Object.assign(categoryTranslations, {
  ta: {
    'heavy-machinery': {
      name: 'கனரக இயந்திரங்கள்',
      description: 'வேகமான நிலத் தயாரிப்பு, அறுவடை மற்றும் பெரிய வேளாண் பணிகளுக்கான வலுவான இயந்திரங்கள்.',
    },
    'agriculture-tools': {
      name: 'வேளாண் கருவிகள்',
      description: 'தினசரி பண்ணை வேலை, பயிர் பராமரிப்பு, மண் தயாரிப்பு மற்றும் பராமரிப்புக்கான நீடித்த கருவிகள்.',
    },
    'irrigation-water': {
      name: 'பாசனம் மற்றும் நீர்',
      description: 'நீர் சேமிப்பு பாசனம், போர்வெல் மற்றும் பயிர்களுக்கு நீர் வழங்கும் பயனுள்ள தீர்வுகள்.',
    },
    'workshop-mro': {
      name: 'வர்க்ஷாப் மற்றும் பராமரிப்பு',
      description: 'பண்ணை பழுது, சேவை மற்றும் தினசரி பயன்பாட்டுக்கான நம்பகமான கருவிகள்.',
    },
    'sprayers-units': {
      name: 'ஸ்ப்ரேயர் யூனிட்கள்',
      description: 'பூச்சிக்கொல்லி, உரம், மிஸ்டிங் மற்றும் தாவர பாதுகாப்புக்கான தெளிப்பு தீர்வுகள்.',
    },
    'food-processing': {
      name: 'உணவு செயலாக்கம்',
      description: 'தீவன வெட்டுதல், தானிய கையாளுதல் மற்றும் அறுவடைக்குப் பிந்தைய பணிகளுக்கான இயந்திரங்கள்.',
    },
  },
  ja: {
    'heavy-machinery': {
      name: '大型機械',
      description: '素早い土づくり、収穫、大規模農作業向けの頑丈な農業機械です。',
    },
    'agriculture-tools': {
      name: '農業ツール',
      description: '日常の農作業、作物管理、土づくり、圃場保守に役立つ耐久性の高い工具です。',
    },
    'irrigation-water': {
      name: '灌漑と水管理',
      description: '節水灌漑、井戸、作物への給水に対応する効率的なソリューションです。',
    },
    'workshop-mro': {
      name: '作業場と保守',
      description: '農場修理、整備、日常作業に使える信頼性の高い工具です。',
    },
    'sprayers-units': {
      name: '噴霧器ユニット',
      description: '農薬、肥料、ミスト散布、植物保護のための噴霧ソリューションです。',
    },
    'food-processing': {
      name: '食品加工',
      description: '飼料切断、穀物処理、収穫後作業向けの機械です。',
    },
  },
  zh: {
    'heavy-machinery': {
      name: '重型机械',
      description: '适用于快速整地、收割和大型农业作业的坚固农机。',
    },
    'agriculture-tools': {
      name: '农业工具',
      description: '适用于日常农活、作物护理、整地和农场维护的耐用工具。',
    },
    'irrigation-water': {
      name: '灌溉与用水',
      description: '适用于节水灌溉、井水和作物供水的高效解决方案。',
    },
    'workshop-mro': {
      name: '车间与维护',
      description: '适用于农场维修、服务和日常使用的可靠工具。',
    },
    'sprayers-units': {
      name: '喷雾器单元',
      description: '用于农药、肥料、雾化和植物保护的喷洒解决方案。',
    },
    'food-processing': {
      name: '食品加工',
      description: '适用于饲料切割、谷物处理和收获后作业的机械。',
    },
  },
  kn: {
    'heavy-machinery': {
      name: 'ಭಾರಿ ಯಂತ್ರೋಪಕರಣಗಳು',
      description: 'ವೇಗವಾದ ಭೂಮಿ ಸಿದ್ಧತೆ, ಕೊಯ್ಲು ಮತ್ತು ದೊಡ್ಡ ಕೃಷಿ ಕೆಲಸಗಳಿಗೆ ಬಲವಾದ ಯಂತ್ರೋಪಕರಣಗಳು.',
    },
    'agriculture-tools': {
      name: 'ಕೃಷಿ ಸಾಧನಗಳು',
      description: 'ದೈನಂದಿನ ಕೃಷಿ ಕೆಲಸ, ಬೆಳೆ ಆರೈಕೆ, ಮಣ್ಣು ಸಿದ್ಧತೆ ಮತ್ತು ನಿರ್ವಹಣೆಗೆ ದೀರ್ಘಕಾಲ ಬಾಳುವ ಸಾಧನಗಳು.',
    },
    'irrigation-water': {
      name: 'ನೀರಾವರಿ ಮತ್ತು ನೀರು',
      description: 'ನೀರು ಉಳಿಸುವ ನೀರಾವರಿ, ಬೋರ್‌ವೆಲ್ ಮತ್ತು ಬೆಳೆಗಳಿಗೆ ನೀರು ಒದಗಿಸುವ ಪರಿಣಾಮಕಾರಿ ಪರಿಹಾರಗಳು.',
    },
    'workshop-mro': {
      name: 'ವರ್ಕ್‌ಶಾಪ್ ಮತ್ತು ನಿರ್ವಹಣೆ',
      description: 'ಕೃಷಿ ದುರಸ್ತಿ, ಸೇವೆ ಮತ್ತು ದೈನಂದಿನ ಬಳಕೆಗೆ ನಂಬಿಕಸ್ಥ ಸಾಧನಗಳು.',
    },
    'sprayers-units': {
      name: 'ಸ್ಪ್ರೇಯರ್ ಯೂನಿಟ್‌ಗಳು',
      description: 'ಕೀಟನಾಶಕ, ರಸಗೊಬ್ಬರ, ಮಿಸ್ಟಿಂಗ್ ಮತ್ತು ಸಸ್ಯ ರಕ್ಷಣೆಗೆ ಸ್ಪ್ರೇ ಪರಿಹಾರಗಳು.',
    },
    'food-processing': {
      name: 'ಆಹಾರ ಸಂಸ್ಕರಣೆ',
      description: 'ಮೇವು ಕತ್ತರಿಸುವುದು, ಧಾನ್ಯ ನಿರ್ವಹಣೆ ಮತ್ತು ಕೊಯ್ಲಿನ ನಂತರದ ಕೆಲಸಗಳಿಗೆ ಯಂತ್ರೋಪಕರಣಗಳು.',
    },
  },
});

Object.assign(categoryTranslations, {
  fr: {
    'heavy-machinery': { name: 'Machines lourdes', description: 'Machines agricoles robustes pour la préparation du sol, la récolte et les grandes exploitations.' },
    'agriculture-tools': { name: 'Outils agricoles', description: 'Outils durables pour le travail agricole quotidien, les cultures, la préparation du sol et l’entretien.' },
    'irrigation-water': { name: 'Irrigation et eau', description: 'Produits efficaces pour l’irrigation économe en eau, les forages et l’arrosage des cultures.' },
    'workshop-mro': { name: 'Atelier et maintenance', description: 'Outils fiables pour les réparations agricoles, l’entretien et les usages quotidiens.' },
    'sprayers-units': { name: 'Unités de pulvérisation', description: 'Solutions de pulvérisation pour pesticides, engrais, brumisation et protection des plantes.' },
    'food-processing': { name: 'Transformation alimentaire', description: 'Machines pour couper, nettoyer, transporter, emballer, stocker et traiter après récolte.' },
  },
  pt: {
    'heavy-machinery': { name: 'Máquinas pesadas', description: 'Máquinas agrícolas resistentes para preparo do solo, colheita e operações de grande escala.' },
    'agriculture-tools': { name: 'Ferramentas agrícolas', description: 'Ferramentas duráveis para trabalho diário, cuidado de culturas, preparo do solo e manutenção.' },
    'irrigation-water': { name: 'Irrigação e água', description: 'Produtos eficientes para irrigação econômica, poços e fornecimento de água às culturas.' },
    'workshop-mro': { name: 'Oficina e manutenção', description: 'Ferramentas confiáveis para reparos agrícolas, serviço e uso diário.' },
    'sprayers-units': { name: 'Unidades de pulverização', description: 'Soluções para pesticidas, fertilizantes, nebulização e proteção de plantas.' },
    'food-processing': { name: 'Processamento de alimentos', description: 'Máquinas para limpeza, corte, transporte, embalagem, armazenamento e pós-colheita.' },
  },
  ar: {
    'heavy-machinery': { name: 'معدات ثقيلة', description: 'آلات زراعية قوية لتحضير الأرض والحصاد والعمليات الزراعية الكبيرة.' },
    'agriculture-tools': { name: 'أدوات زراعية', description: 'أدوات متينة للعمل الزراعي اليومي ورعاية المحاصيل وتحضير التربة والصيانة.' },
    'irrigation-water': { name: 'الري والمياه', description: 'منتجات فعالة للري الموفر للمياه والآبار وسقي المحاصيل.' },
    'workshop-mro': { name: 'الورشة والصيانة', description: 'أدوات موثوقة لإصلاحات المزرعة والخدمة والاستخدام اليومي.' },
    'sprayers-units': { name: 'وحدات الرش', description: 'حلول رش للمبيدات والأسمدة والتغشية وحماية النباتات.' },
    'food-processing': { name: 'معالجة الأغذية', description: 'آلات للتنظيف والقطع والنقل والتعبئة والتخزين وما بعد الحصاد.' },
  },
  ko: {
    'heavy-machinery': { name: '중장비', description: '토지 준비, 수확 및 대규모 농장 작업을 위한 견고한 농업 기계입니다.' },
    'agriculture-tools': { name: '농업 도구', description: '일상 농작업, 작물 관리, 토양 준비 및 현장 유지보수를 위한 내구성 있는 도구입니다.' },
    'irrigation-water': { name: '관개 및 물', description: '절수 관개, 우물 및 작물 급수를 위한 효율적인 제품입니다.' },
    'workshop-mro': { name: '작업장 및 유지보수', description: '농장 수리, 서비스 및 일상 작업을 위한 신뢰성 높은 도구입니다.' },
    'sprayers-units': { name: '분무기 장치', description: '농약, 비료, 미스트 및 식물 보호용 분무 솔루션입니다.' },
    'food-processing': { name: '식품 가공', description: '세척, 절단, 운반, 포장, 보관 및 수확 후 작업용 기계입니다.' },
  },
});

const productTermTranslations = {
  te: {
    'MODERN TRACTOR': 'ఆధునిక ట్రాక్టర్',
    'CULTIVATOR': 'కల్టివేటర్',
    'POWER TILLER': 'పవర్ టిల్లర్',
    'FIELD CULTIVATOR': 'ఫీల్డ్ కల్టివేటర్',
    'IRRIGATION SYSTEM': 'నీటిపారుదల వ్యవస్థ',
    'DRIP PIPES': 'డ్రిప్ పైపులు',
    'PRECISION CUTTER': 'ప్రెసిషన్ కట్టర్',
    'INDUSTRIAL WRENCH': 'ఇండస్ట్రియల్ రెంచ్',
    'POWER DRILLER': 'పవర్ డ్రిల్లర్',
    'HARVEST HOOK KNIFE': 'హార్వెస్ట్ హుక్ కత్తి',
    'SPRINKLER SYSTEM': 'స్ప్రింక్లర్ వ్యవస్థ',
    'RAIN GUN': 'రెయిన్ గన్',
    'PVC PIPES': 'పీవీసీ పైపులు',
    'SPRINKLER PIPES': 'స్ప్రింక్లర్ పైపులు',
    'BOREWELL PUMPS': 'బోర్‌వెల్ పంపులు',
    '1HP WATER PUMP': '1HP నీటి పంపు',
    'DRIP LATERAL PIPE': 'డ్రిప్ లాటరల్ పైప్',
    'DRONE SPRAYER': 'డ్రోన్ స్ప్రేయర్',
    'MANUAL SPRAYER': 'మాన్యువల్ స్ప్రేయర్',
    'BATTERY SPRAYER': 'బ్యాటరీ స్ప్రేయర్',
    'POWER SPRAYER': 'పవర్ స్ప్రేయర్',
    'HIGH PRESSURE PUMP': 'హై ప్రెషర్ పంపు',
    'TRACTOR SPRAYER': 'ట్రాక్టర్ స్ప్రేయర్',
    'FOGGER SPRAYER': 'ఫాగర్ స్ప్రేయర్',
    'ELECTRIC FENCE': 'ఎలక్ట్రిక్ ఫెన్స్',
    'BRUSH CUTTER': 'బ్రష్ కట్టర్',
    'TROLLEY BRUSH CUTTER': 'ట్రాలీ బ్రష్ కట్టర్',
    'GRASS CUTTER': 'గ్రాస్ కట్టర్',
    'HEDGE TRIMMER': 'హెడ్జ్ ట్రిమ్మర్',
    'EARTH AUGER': 'ఎర్త్ ఆగర్',
    'POWER WEEDER': 'పవర్ వీడర్',
    'MINI TILLER': 'మినీ టిల్లర్',
    'SEED DRILL': 'సీడ్ డ్రిల్',
    'MANUAL SEEDER': 'మాన్యువల్ సీడర్',
    'CYCLE WEEDER': 'సైకిల్ వీడర్',
    'TARPAULIN': 'టార్పాలిన్',
    'GREEN SHADE NET': 'గ్రీన్ షేడ్ నెట్',
    'SOLAR FENCE ENERGIZER': 'సోలార్ ఫెన్స్ ఎనర్జైజర్',
    'SOLAR INSECT TRAP': 'సోలార్ ఇన్సెక్ట్ ట్రాప్',
    'SOLAR STREET LIGHT': 'సోలార్ స్ట్రీట్ లైట్',
    'HARVESTER': 'హార్వెస్టర్',
    'MINI REAPER': 'మినీ రీపర్',
    'REAPER BINDER': 'రీపర్ బైండర్',
    'CHAFF CUTTER': 'చాఫ్ కట్టర్',
    'HEAVY DUTY SCYTHE WITH 23 INCH BLADE FOR HARVESTING GRASS OR CROPS': 'గ్రాస్ మరియు పంటల కోతకు 23 ఇంచుల బ్లేడ్‌తో కూడిన హెవీ డ్యూటీ కొడవలి',
    'HEAVY-DUTY 6 TOOTH SPIKE HARROW - DURABLE AGRICULTURAL TOOL FOR SOIL TILLING, LEVELING & WEEDING': 'నేల దున్నడం మరియు కలుపు తీయడానికి హెవీ-డ్యూటీ 6 టూత్ స్పైక్ హారో',
    'GRAMSTRONG REPUBLIC SOLAR STREET LIGHT 120W, 140 LEDS WATERPROOF STREET LIGHT WITH SOLAR PANEL FOR FARM USE WITH REMOTE': 'రిమోట్‌తో కూడిన గ్రామ్‌స్ట్రాంగ్ రిపబ్లిక్ సోలార్ స్ట్రీట్ లైట్ 120W',
    'HEAVY-DUTY AGRICULTURAL IMPACT SPRINKLER FOR IRRIGATION': 'సాగు నీటిపారుదల కోసం హెవీ-డ్యూటీ అగ్రికల్చరల్ ఇంపాక్ట్ స్ప్రింక్లర్',
    'FARMIO GRASS CUTTING MACHINE (WITHOUT BATTERY) / CORDED BRUSH CUTTER MACHINE': 'ఫార్మియో గ్రాస్ కటింగ్ మెషిన్ (బ్యాటరీ లేకుండా) / కార్డెడ్ బ్రష్ కట్టర్ మెషిన్',
    'PREMIUM QUALITY THERMAL FOGGING MACHINE, 16 LITRE': 'ప్రీమియం నాణ్యత థర్మల్ ఫాగింగ్ మెషిన్, 16 లీటర్లు',
    'KISANKRAFT KK-STB-050 GRASS STUBBLE MOWER 4-STROKE PETROL 6.7 HP USED FOR GRASS/TWIGS & LOW VEGETATION/SCRUB & STUBBLE': 'కిసాన్‌క్రాఫ్ట్ KK-STB-050 గ్రాస్ స్టబుల్ మోవర్ 6.7 HP',
    '1 INCH PORTABLE PETROL WATER PUMP': '1 ఇంచ్ పోర్టబుల్ పెట్రోల్ వాటర్ పంప్',
    '15 L MIST BLOWER 2 STROKE PETROL ENGINE, 21 TO 25 FEET SPRAYING': '15 లీటర్ల మిస్ట్ బ్లోయర్ 2 స్ట్రోక్ పెట్రోల్ ఇంజిన్',
    '360 DEGREE ROTATABLE DOUBLE FACED ELECTRIC LIGHT': '360 డిగ్రీల తిరిగే డబుల్ ఫేస్డ్ ఎలక్ట్రిక్ లైట్',
    'RESUN ACO-001 AIR PUMP ELECTROMAGNETIC AIR AQUARIUM PUMP (136 CM) FOR TANK WATER AERATION & ANY AQUARIUM FILTRATION': 'రెసన్ ACO-001 ఎయిర్ పంప్ అక్వేరియం ఎయిరేషన్ పంప్',
    'MINI CORDLESS CHAINSAW DUAL BATTERY (21V X 2) WITH 6 INCH GUIDE BAR | COMES WITH FREE ACCESSORIES': 'మినీ కార్డ్‌లెస్ చైన్సా డ్యూయల్ బ్యాటరీ (21V x 2) 6 ఇంచ్ గైడ్ బార్‌తో',
    'HEAVY DUTY 63 CC 2 STROKE EARTH AUGER / POST HOLE DIGGER / EARTH DRILL MACHINE WITH 12 INCH BIT': 'హెవీ డ్యూటీ 63 cc 2 స్ట్రోక్ ఎర్త్ ఆగర్ డ్రిల్ మెషిన్',
    'BRUSH CUTTER WITH HEDGE TRIMMER ATTACHMENT': 'హెడ్జ్ ట్రిమ్మర్ అటాచ్‌మెంట్‌తో కూడిన బ్రష్ కట్టర్',
    'HEAVY DUTY 18 INCH STEEL FRAME WHEEL TYPE PUSH MOWER | IDEAL FOR GRASS CUTTING, TRIMMING': 'హెవీ డ్యూటీ 18 ఇంచుల పుష్ మోవర్ (పచ్చిక కోతకు అనువైనది)',
    'HEAVY DUTY 63-CC POWERFUL PETROL OPERATED LEAF BLOWER | 300 LITER / SECOND AIR VOLUME': 'హెవీ డ్యూటీ 63-cc పవర్ఫుల్ పెట్రోల్ లీఫ్ బ్లోయర్',
    'KORAMANDAL': 'కోరమాండల్',
    'TF 545P 7.2 HP PETROL OPERATED POWER TILLER': 'TF 545P 7.2 HP పెట్రోల్ పవర్ టిల్లర్',
    'DRIP IRRIGATION KIT (1 ACRE)': 'డ్రిప్ ఇరిగేషన్ కిట్ (1 ఎకరం)',
    'SPRINKLER NOZZLE SET (10 PCS)': 'స్ప్రింక్లర్ నాజిల్ సెట్ (10 పీసులు)',
    'MANUAL REAPER BINDER': 'మాన్యువల్ రీపర్ బైండర్',
    'NPK FERTILIZER SPREADER': 'NPK ఎరువుల స్ప్రెడర్',
  },
  hi: {
    'MODERN TRACTOR': 'आधुनिक ट्रैक्टर',
    'CULTIVATOR': 'कल्टीवेटर',
    'POWER TILLER': 'पावर टिलर',
    'FIELD CULTIVATOR': 'फील्ड कल्टीवेटर',
    'IRRIGATION SYSTEM': 'सिंचाई सिस्टम',
    'DRIP PIPES': 'ड्रिप पाइप',
    'PRECISION CUTTER': 'प्रिसिजन कटर',
    'INDUSTRIAL WRENCH': 'इंडस्ट्रियल रेंच',
    'POWER DRILLER': 'पावर ड्रिलर',
    'HARVEST HOOK KNIFE': 'हार्वेस्ट हुक चाकू',
    'SPRINKLER SYSTEM': 'स्प्रिंकलर सिस्टम',
    'RAIN GUN': 'रेन गन',
    'PVC PIPES': 'पीवीसी पाइप',
    'SPRINKLER PIPES': 'स्प्रिंकलर पाइप',
    'BOREWELL PUMPS': 'बोरवेल पंप',
    '1HP WATER PUMP': '1HP पानी पंप',
    'DRIP LATERAL PIPE': 'ड्रिप लैटरल पाइप',
    'DRONE SPRAYER': 'ड्रोन स्प्रेयर',
    'MANUAL SPRAYER': 'मैनुअल स्प्रेयर',
    'BATTERY SPRAYER': 'बैटरी स्प्रेयर',
    'POWER SPRAYER': 'पावर स्प्रेयर',
    'HIGH PRESSURE PUMP': 'हाई प्रेशर पंप',
    'TRACTOR SPRAYER': 'ट्रैक्टर स्प्रेयर',
    'FOGGER SPRAYER': 'फॉगर स्प्रेयर',
    'ELECTRIC FENCE': 'इलेक्ट्रिक फेंस',
    'BRUSH CUTTER': 'ब्रश कटर',
    'TROLLEY BRUSH CUTTER': 'ट्रॉली ब्रश कटर',
    'GRASS CUTTER': 'ग्रास कटर',
    'HEDGE TRIMMER': 'हेज ट्रिमर',
    'EARTH AUGER': 'अर्थ ऑगर',
    'POWER WEEDER': 'पावर वीडर',
    'MINI TILLER': 'मिनी टिलर',
    'SEED DRILL': 'सीड ड्रिल',
    'MANUAL SEEDER': 'मैनुअल सीडर',
    'CYCLE WEEDER': 'साइकिल वीडर',
    'TARPAULIN': 'तिरपाल',
    'GREEN SHADE NET': 'ग्रीन शेड नेट',
    'SOLAR FENCE ENERGIZER': 'सोलर फेंस एनर्जाइजर',
    'SOLAR INSECT TRAP': 'सोलर कीट ट्रैप',
    'SOLAR STREET LIGHT': 'सोलर स्ट्रीट लाइट',
    'HARVESTER': 'हार्वेस्टर',
    'MINI REAPER': 'मिनी रीपर',
    'REAPER BINDER': 'रीपर बाइंडर',
    'CHAFF CUTTER': 'चाफ कटर',
    'HEAVY DUTY SCYTHE WITH 23 INCH BLADE FOR HARVESTING GRASS OR CROPS': 'घास और फसलों की कटाई के लिए 23 इंच ब्लेड वाला हेवी ड्यूटी हंसिया (सिकल)',
    'HEAVY-DUTY 6 TOOTH SPIKE HARROW - DURABLE AGRICULTURAL TOOL FOR SOIL TILLING, LEVELING & WEEDING': 'मिट्टी जोतने और खरपतवार निकालने के लिए हेवी-ड्यूटी 6 टूथ स्पाइक हैरो',
    'GRAMSTRONG REPUBLIC SOLAR STREET LIGHT 120W, 140 LEDS WATERPROOF STREET LIGHT WITH SOLAR PANEL FOR FARM USE WITH REMOTE': 'रिमोट के साथ ग्रामस्ट्रॉन्ग रिपब्लिक सोलर स्ट्रीट लाइट 120W',
    'HEAVY-DUTY AGRICULTURAL IMPACT SPRINKLER FOR IRRIGATION': 'सिंचाई के लिए हेवी-ड्यूटी कृषि प्रभाव स्प्रिंकलर',
    'FARMIO GRASS CUTTING MACHINE (WITHOUT BATTERY) / CORDED BRUSH CUTTER MACHINE': 'फार्मियो ग्रास कटिंग मशीन (बिना बैटरी) / कॉर्डेड ब्रश कटर मशीन',
    'PREMIUM QUALITY THERMAL FOGGING MACHINE, 16 LITRE': 'प्रीमियम गुणवत्ता वाली थर्मल फॉगिंग मशीन, 16 लीटर',
    'KISANKRAFT KK-STB-050 GRASS STUBBLE MOWER 4-STROKE PETROL 6.7 HP USED FOR GRASS/TWIGS & LOW VEGETATION/SCRUB & STUBBLE': 'किसानक्राफ्ट KK-STB-050 ग्रास स्टबल मूवर 6.7 HP',
    '1 INCH PORTABLE PETROL WATER PUMP': '1 इंच पोर्टेबल पेट्रोल वॉटर पंप',
    '15 L MIST BLOWER 2 STROKE PETROL ENGINE, 21 TO 25 FEET SPRAYING': '15 लीटर मिस्ट ब्लोअर 2 स्ट्रोक पेट्रोल इंजन',
    '360 DEGREE ROTATABLE DOUBLE FACED ELECTRIC LIGHT': '360 डिग्री घूमने वाली डबल फेस वाली इलेक्ट्रिक लाइट',
    'RESUN ACO-001 AIR PUMP ELECTROMAGNETIC AIR AQUARIUM PUMP (136 CM) FOR TANK WATER AERATION & ANY AQUARIUM FILTRATION': 'रेसुन ACO-001 एयर पंप एक्वेरियम वातन पंप',
    'MINI CORDLESS CHAINSAW DUAL BATTERY (21V X 2) WITH 6 INCH GUIDE BAR | COMES WITH FREE ACCESSORIES': 'मिनी कॉर्डलेस चेनसॉ ड्यूल बैटरी (21V x 2) 6 इंच गाइड बार के साथ',
    'HEAVY DUTY 63 CC 2 STROKE EARTH AUGER / POST HOLE DIGGER / EARTH DRILL MACHINE WITH 12 INCH BIT': 'हेवी ड्यूटी 63 सीसी 2 स्ट्रोक अर्थ ऑगर ड्रिल मशीन',
    'BRUSH CUTTER WITH HEDGE TRIMMER ATTACHMENT': 'हेज ट्रिमर अटैचमेंट के साथ ब्रश कटर',
    'HEAVY DUTY 18 INCH STEEL FRAME WHEEL TYPE PUSH MOWER | IDEAL FOR GRASS CUTTING, TRIMMING': 'हेवी ड्यूटी 18 इंच पुश मूवर (घास काटने के लिए आदर्श)',
    'HEAVY DUTY 63-CC POWERFUL PETROL OPERATED LEAF BLOWER | 300 LITER / SECOND AIR VOLUME': 'हेवी ड्यूटी 63-सीसी शक्तिशाली पेट्रोल संचालित लीफ ब्लोअर',
    'KORAMANDAL': 'कोरोमंडल',
    'TF 545P 7.2 HP PETROL OPERATED POWER TILLER': 'TF 545P 7.2 एचपी पेट्रोल संचालित पावर टिलर',
    'DRIP IRRIGATION KIT (1 ACRE)': 'ड्रिप सिंचाई किट (1 एकड़)',
    'SPRINKLER NOZZLE SET (10 PCS)': 'स्प्रिंकलर नोजल सेट (10 नग)',
    'MANUAL REAPER BINDER': 'मैनुअल रीपर बाइंडर',
    'NPK FERTILIZER SPREADER': 'एनपीके उर्वरक स्प्रेडर',
  },
};

Object.assign(productTermTranslations, {
  ta: {
    'MODERN TRACTOR': 'நவீன டிராக்டர்',
    'CULTIVATOR': 'கல்டிவேட்டர்',
    'POWER TILLER': 'பவர் டில்லர்',
    'FIELD CULTIVATOR': 'ஃபீல்டு கல்டிவேட்டர்',
    'IRRIGATION SYSTEM': 'பாசன அமைப்பு',
    'DRIP PIPES': 'டிரிப் குழாய்கள்',
    'PRECISION CUTTER': 'துல்லிய வெட்டான்',
    'INDUSTRIAL WRENCH': 'தொழில்துறை ரெஞ்ச்',
    'POWER DRILLER': 'பவர் டிரில்லர்',
    'HARVEST HOOK KNIFE': 'அறுவடை ஹூக் கத்தி',
    'SPRINKLER SYSTEM': 'ஸ்பிரிங்கிளர் அமைப்பு',
    'RAIN GUN': 'ரெயின் கன்',
    'PVC PIPES': 'PVC குழாய்கள்',
    'SPRINKLER PIPES': 'ஸ்பிரிங்கிளர் குழாய்கள்',
    'BOREWELL PUMPS': 'போர்வெல் பம்புகள்',
    '1HP WATER PUMP': '1HP நீர் பம்ப்',
    'DRIP LATERAL PIPE': 'டிரிப் லேட்டரல் குழாய்',
    'DRONE SPRAYER': 'ட்ரோன் ஸ்ப்ரேயர்',
    'MANUAL SPRAYER': 'கைமுறை ஸ்ப்ரேயர்',
    'BATTERY SPRAYER': 'பேட்டரி ஸ்ப்ரேயர்',
    'POWER SPRAYER': 'பவர் ஸ்ப்ரேயர்',
    'HIGH PRESSURE PUMP': 'உயர் அழுத்த பம்ப்',
    'TRACTOR SPRAYER': 'டிராக்டர் ஸ்ப்ரேயர்',
    'FOGGER SPRAYER': 'ஃபோகர் ஸ்ப்ரேயர்',
    'ELECTRIC FENCE': 'மின்சார வேலி',
    'BRUSH CUTTER': 'பிரஷ் கட்டர்',
    'TROLLEY BRUSH CUTTER': 'ட்ராலி பிரஷ் கட்டர்',
    'GRASS CUTTER': 'புல் வெட்டான்',
    'HEDGE TRIMMER': 'ஹெட்ஜ் டிரிம்மர்',
    'EARTH AUGER': 'எர்த் ஆகர்',
    'POWER WEEDER': 'பவர் வீடர்',
    'MINI TILLER': 'மினி டில்லர்',
    'SEED DRILL': 'சீட் டிரில்',
    'MANUAL SEEDER': 'கைமுறை விதைப்பான்',
    'CYCLE WEEDER': 'சைக்கிள் வீடர்',
    'TARPAULIN': 'தார்பாலின்',
    'GREEN SHADE NET': 'கிரீன் ஷேட் நெட்',
    'SOLAR FENCE ENERGIZER': 'சோலார் வேலி எனர்ஜைசர்',
    'SOLAR INSECT TRAP': 'சோலார் பூச்சி வலை',
    'SOLAR STREET LIGHT': 'சோலார் தெரு விளக்கு',
    'HARVESTER': 'ஹார்வெஸ்டர்',
    'MINI REAPER': 'மினி ரீப்பர்',
    'REAPER BINDER': 'ரீப்பர் பைண்டர்',
    'CHAFF CUTTER': 'சாஃப் கட்டர்',
  },
  ja: {
    'MODERN TRACTOR': '最新トラクター',
    'CULTIVATOR': 'カルチベーター',
    'POWER TILLER': 'パワーティラー',
    'FIELD CULTIVATOR': 'フィールドカルチベーター',
    'IRRIGATION SYSTEM': '灌漑システム',
    'DRIP PIPES': '点滴灌漑パイプ',
    'PRECISION CUTTER': '精密カッター',
    'INDUSTRIAL WRENCH': '産業用レンチ',
    'POWER DRILLER': 'パワードリル',
    'HARVEST HOOK KNIFE': '収穫用フックナイフ',
    'SPRINKLER SYSTEM': 'スプリンクラーシステム',
    'RAIN GUN': 'レインガン',
    'PVC PIPES': 'PVCパイプ',
    'SPRINKLER PIPES': 'スプリンクラーパイプ',
    'BOREWELL PUMPS': 'ボアウェルポンプ',
    '1HP WATER PUMP': '1HPウォーターポンプ',
    'DRIP LATERAL PIPE': '点滴ラテラルパイプ',
    'DRONE SPRAYER': 'ドローン噴霧器',
    'MANUAL SPRAYER': '手動噴霧器',
    'BATTERY SPRAYER': 'バッテリー噴霧器',
    'POWER SPRAYER': 'パワー噴霧器',
    'HIGH PRESSURE PUMP': '高圧ポンプ',
    'TRACTOR SPRAYER': 'トラクター噴霧器',
    'FOGGER SPRAYER': 'フォガー噴霧器',
    'ELECTRIC FENCE': '電気柵',
    'BRUSH CUTTER': '刈払機',
    'TROLLEY BRUSH CUTTER': '台車式刈払機',
    'GRASS CUTTER': '草刈機',
    'HEDGE TRIMMER': 'ヘッジトリマー',
    'EARTH AUGER': 'アースオーガー',
    'POWER WEEDER': 'パワー除草機',
    'MINI TILLER': 'ミニティラー',
    'SEED DRILL': '播種機',
    'MANUAL SEEDER': '手動播種機',
    'CYCLE WEEDER': 'サイクル除草機',
    'TARPAULIN': '防水シート',
    'GREEN SHADE NET': 'グリーン遮光ネット',
    'SOLAR FENCE ENERGIZER': 'ソーラー電気柵電源',
    'SOLAR INSECT TRAP': 'ソーラー害虫トラップ',
    'SOLAR STREET LIGHT': 'ソーラー街灯',
    'HARVESTER': 'ハーベスター',
    'MINI REAPER': 'ミニリーパー',
    'REAPER BINDER': 'リーパーバインダー',
    'CHAFF CUTTER': '飼料カッター',
  },
  zh: {
    'MODERN TRACTOR': '现代拖拉机',
    'CULTIVATOR': '中耕机',
    'POWER TILLER': '动力耕整机',
    'FIELD CULTIVATOR': '田间中耕机',
    'IRRIGATION SYSTEM': '灌溉系统',
    'DRIP PIPES': '滴灌管',
    'PRECISION CUTTER': '精密切割机',
    'INDUSTRIAL WRENCH': '工业扳手',
    'POWER DRILLER': '电动钻机',
    'HARVEST HOOK KNIFE': '收割钩刀',
    'SPRINKLER SYSTEM': '喷灌系统',
    'RAIN GUN': '雨枪喷头',
    'PVC PIPES': 'PVC管',
    'SPRINKLER PIPES': '喷灌管',
    'BOREWELL PUMPS': '深井泵',
    '1HP WATER PUMP': '1HP水泵',
    'DRIP LATERAL PIPE': '滴灌支管',
    'DRONE SPRAYER': '无人机喷雾器',
    'MANUAL SPRAYER': '手动喷雾器',
    'BATTERY SPRAYER': '电池喷雾器',
    'POWER SPRAYER': '动力喷雾器',
    'HIGH PRESSURE PUMP': '高压泵',
    'TRACTOR SPRAYER': '拖拉机喷雾器',
    'FOGGER SPRAYER': '雾化喷雾器',
    'ELECTRIC FENCE': '电围栏',
    'BRUSH CUTTER': '割灌机',
    'TROLLEY BRUSH CUTTER': '推车割灌机',
    'GRASS CUTTER': '割草机',
    'HEDGE TRIMMER': '绿篱修剪机',
    'EARTH AUGER': '地钻',
    'POWER WEEDER': '动力除草机',
    'MINI TILLER': '迷你耕整机',
    'SEED DRILL': '播种机',
    'MANUAL SEEDER': '手动播种器',
    'CYCLE WEEDER': '自行车式除草机',
    'TARPAULIN': '防水篷布',
    'GREEN SHADE NET': '绿色遮阳网',
    'SOLAR FENCE ENERGIZER': '太阳能电围栏电源',
    'SOLAR INSECT TRAP': '太阳能害虫诱捕器',
    'SOLAR STREET LIGHT': '太阳能路灯',
    'HARVESTER': '收割机',
    'MINI REAPER': '小型割晒机',
    'REAPER BINDER': '割捆机',
    'CHAFF CUTTER': '铡草机',
  },
  kn: {
    'MODERN TRACTOR': 'ಆಧುನಿಕ ಟ್ರಾಕ್ಟರ್',
    'CULTIVATOR': 'ಕಲ್ಟಿವೇಟರ್',
    'POWER TILLER': 'ಪವರ್ ಟಿಲ್ಲರ್',
    'FIELD CULTIVATOR': 'ಫೀಲ್ಡ್ ಕಲ್ಟಿವೇಟರ್',
    'IRRIGATION SYSTEM': 'ನೀರಾವರಿ ವ್ಯವಸ್ಥೆ',
    'DRIP PIPES': 'ಡ್ರಿಪ್ ಪೈಪ್‌ಗಳು',
    'PRECISION CUTTER': 'ಪ್ರೆಸಿಷನ್ ಕಟ್ಟರ್',
    'INDUSTRIAL WRENCH': 'ಇಂಡಸ್ಟ್ರಿಯಲ್ ರೆಂಚ್',
    'POWER DRILLER': 'ಪವರ್ ಡ್ರಿಲ್ಲರ್',
    'HARVEST HOOK KNIFE': 'ಕೊಯ್ಲು ಹುಕ್ ಚಾಕು',
    'SPRINKLER SYSTEM': 'ಸ್ಪ್ರಿಂಕ್ಲರ್ ವ್ಯವಸ್ಥೆ',
    'RAIN GUN': 'ರೇನ್ ಗನ್',
    'PVC PIPES': 'PVC ಪೈಪ್‌ಗಳು',
    'SPRINKLER PIPES': 'ಸ್ಪ್ರಿಂಕ್ಲರ್ ಪೈಪ್‌ಗಳು',
    'BOREWELL PUMPS': 'ಬೋರ್‌ವೆಲ್ ಪಂಪ್‌ಗಳು',
    '1HP WATER PUMP': '1HP ನೀರಿನ ಪಂಪ್',
    'DRIP LATERAL PIPE': 'ಡ್ರಿಪ್ ಲ್ಯಾಟರಲ್ ಪೈಪ್',
    'DRONE SPRAYER': 'ಡ್ರೋನ್ ಸ್ಪ್ರೇಯರ್',
    'MANUAL SPRAYER': 'ಮ್ಯಾನುಯಲ್ ಸ್ಪ್ರೇಯರ್',
    'BATTERY SPRAYER': 'ಬ್ಯಾಟರಿ ಸ್ಪ್ರೇಯರ್',
    'POWER SPRAYER': 'ಪವರ್ ಸ್ಪ್ರೇಯರ್',
    'HIGH PRESSURE PUMP': 'ಹೈ ಪ್ರೆಶರ್ ಪಂಪ್',
    'TRACTOR SPRAYER': 'ಟ್ರಾಕ್ಟರ್ ಸ್ಪ್ರೇಯರ್',
    'FOGGER SPRAYER': 'ಫಾಗರ್ ಸ್ಪ್ರೇಯರ್',
    'ELECTRIC FENCE': 'ಎಲೆಕ್ಟ್ರಿಕ್ ಫೆನ್ಸ್',
    'BRUSH CUTTER': 'ಬ್ರಷ್ ಕಟ್ಟರ್',
    'TROLLEY BRUSH CUTTER': 'ಟ್ರಾಲಿ ಬ್ರಷ್ ಕಟ್ಟರ್',
    'GRASS CUTTER': 'ಗ್ರಾಸ್ ಕಟ್ಟರ್',
    'HEDGE TRIMMER': 'ಹೆಡ್ಜ್ ಟ್ರಿಮ್ಮರ್',
    'EARTH AUGER': 'ಅರ್ಥ್ ಆಗರ್',
    'POWER WEEDER': 'ಪವರ್ ವೀಡರ್',
    'MINI TILLER': 'ಮಿನಿ ಟಿಲ್ಲರ್',
    'SEED DRILL': 'ಸೀಡ್ ಡ್ರಿಲ್',
    'MANUAL SEEDER': 'ಮ್ಯಾನುಯಲ್ ಸೀಡರ್',
    'CYCLE WEEDER': 'ಸೈಕಲ್ ವೀಡರ್',
    'TARPAULIN': 'ತಾರ್ಪಾಲಿನ್',
    'GREEN SHADE NET': 'ಗ್ರೀನ್ ಶೇಡ್ ನೆಟ್',
    'SOLAR FENCE ENERGIZER': 'ಸೋಲಾರ್ ಫೆನ್ಸ್ ಎನರ್ಜೈಸರ್',
    'SOLAR INSECT TRAP': 'ಸೋಲಾರ್ ಕೀಟ ಟ್ರ್ಯಾಪ್',
    'SOLAR STREET LIGHT': 'ಸೋಲಾರ್ ಸ್ಟ್ರೀಟ್ ಲೈಟ್',
    'HARVESTER': 'ಹಾರ್ವೆಸ್ಟರ್',
    'MINI REAPER': 'ಮಿನಿ ರೀಪರ್',
    'REAPER BINDER': 'ರೀಪರ್ ಬೈಂಡರ್',
    'CHAFF CUTTER': 'ಚಾಫ್ ಕಟ್ಟರ್',
  },
});

const extraProductTerms = {
  fr: {
    'MODERN TRACTOR': 'Tracteur moderne',
    'CULTIVATOR': 'Cultivateur',
    'POWER TILLER': 'Motoculteur',
    'FIELD CULTIVATOR': 'Cultivateur de champ',
    'IRRIGATION SYSTEM': 'Système d’irrigation',
    'DRIP PIPES': 'Tuyaux goutte-à-goutte',
    'PRECISION CUTTER': 'Coupeuse de précision',
    'INDUSTRIAL WRENCH': 'Clé industrielle',
    'POWER DRILLER': 'Perceuse électrique',
    'HARVEST HOOK KNIFE': 'Couteau crochet de récolte',
    'SPRINKLER SYSTEM': 'Système d’arrosage',
    'RAIN GUN': 'Canon à pluie',
    'PVC PIPES': 'Tuyaux PVC',
    'SPRINKLER PIPES': 'Tuyaux d’arrosage',
    'BOREWELL PUMPS': 'Pompes de forage',
    '1HP WATER PUMP': 'Pompe à eau 1HP',
    'DRIP LATERAL PIPE': 'Tuyau latéral goutte-à-goutte',
    'DRONE SPRAYER': 'Pulvérisateur drone',
    'MANUAL SPRAYER': 'Pulvérisateur manuel',
    'BATTERY SPRAYER': 'Pulvérisateur à batterie',
    'POWER SPRAYER': 'Pulvérisateur motorisé',
    'HIGH PRESSURE PUMP': 'Pompe haute pression',
    'TRACTOR SPRAYER': 'Pulvérisateur pour tracteur',
    'FOGGER SPRAYER': 'Brumisateur',
    'ELECTRIC FENCE': 'Clôture électrique',
    'BRUSH CUTTER': 'Débroussailleuse',
    'TROLLEY BRUSH CUTTER': 'Débroussailleuse sur chariot',
    'GRASS CUTTER': 'Coupe-herbe',
    'HEDGE TRIMMER': 'Taille-haie',
    'EARTH AUGER': 'Tarière',
    'POWER WEEDER': 'Désherbeur motorisé',
    'MINI TILLER': 'Mini motoculteur',
    'SEED DRILL': 'Semoir',
    'MANUAL SEEDER': 'Semoir manuel',
    'CYCLE WEEDER': 'Désherbeur à roue',
    'TARPAULIN': 'Bâche',
    'GREEN SHADE NET': 'Filet d’ombrage vert',
    'SOLAR FENCE ENERGIZER': 'Électrificateur solaire',
    'SOLAR INSECT TRAP': 'Piège solaire à insectes',
    'SOLAR STREET LIGHT': 'Lampadaire solaire',
    'HARVESTER': 'Moissonneuse',
    'MINI REAPER': 'Mini faucheuse',
    'REAPER BINDER': 'Faucheuse-lieuse',
    'CHAFF CUTTER': 'Hache-paille',
  },
  pt: {
    'MODERN TRACTOR': 'Trator moderno',
    'CULTIVATOR': 'Cultivador',
    'POWER TILLER': 'Motocultivador',
    'FIELD CULTIVATOR': 'Cultivador de campo',
    'IRRIGATION SYSTEM': 'Sistema de irrigação',
    'DRIP PIPES': 'Tubos de gotejamento',
    'PRECISION CUTTER': 'Cortador de precisão',
    'INDUSTRIAL WRENCH': 'Chave industrial',
    'POWER DRILLER': 'Furadeira elétrica',
    'HARVEST HOOK KNIFE': 'Faca de colheita',
    'SPRINKLER SYSTEM': 'Sistema de aspersão',
    'RAIN GUN': 'Canhão de chuva',
    'PVC PIPES': 'Tubos PVC',
    'SPRINKLER PIPES': 'Tubos de aspersão',
    'BOREWELL PUMPS': 'Bombas de poço',
    '1HP WATER PUMP': 'Bomba de água 1HP',
    'DRIP LATERAL PIPE': 'Tubo lateral de gotejamento',
    'DRONE SPRAYER': 'Pulverizador drone',
    'MANUAL SPRAYER': 'Pulverizador manual',
    'BATTERY SPRAYER': 'Pulverizador a bateria',
    'POWER SPRAYER': 'Pulverizador motorizado',
    'HIGH PRESSURE PUMP': 'Bomba de alta pressão',
    'TRACTOR SPRAYER': 'Pulverizador de trator',
    'FOGGER SPRAYER': 'Nebulizador',
    'ELECTRIC FENCE': 'Cerca elétrica',
    'BRUSH CUTTER': 'Roçadeira',
    'TROLLEY BRUSH CUTTER': 'Roçadeira com carrinho',
    'GRASS CUTTER': 'Cortador de grama',
    'HEDGE TRIMMER': 'Aparador de cerca viva',
    'EARTH AUGER': 'Perfurador de solo',
    'POWER WEEDER': 'Capinadeira motorizada',
    'MINI TILLER': 'Mini motocultivador',
    'SEED DRILL': 'Semeadora',
    'MANUAL SEEDER': 'Semeadora manual',
    'CYCLE WEEDER': 'Capinadeira de roda',
    'TARPAULIN': 'Lona',
    'GREEN SHADE NET': 'Tela de sombreamento verde',
    'SOLAR FENCE ENERGIZER': 'Energizador solar de cerca',
    'SOLAR INSECT TRAP': 'Armadilha solar para insetos',
    'SOLAR STREET LIGHT': 'Luz solar de rua',
    'HARVESTER': 'Colheitadeira',
    'MINI REAPER': 'Mini ceifadeira',
    'REAPER BINDER': 'Ceifadeira amarradora',
    'CHAFF CUTTER': 'Picador de forragem',
  },
  ar: {
    'MODERN TRACTOR': 'جرار حديث',
    'CULTIVATOR': 'مزارع',
    'POWER TILLER': 'عزاقة آلية',
    'FIELD CULTIVATOR': 'مزارع حقلي',
    'IRRIGATION SYSTEM': 'نظام ري',
    'DRIP PIPES': 'أنابيب تنقيط',
    'PRECISION CUTTER': 'قاطع دقيق',
    'INDUSTRIAL WRENCH': 'مفتاح صناعي',
    'POWER DRILLER': 'مثقاب كهربائي',
    'HARVEST HOOK KNIFE': 'سكين حصاد',
    'SPRINKLER SYSTEM': 'نظام رش',
    'RAIN GUN': 'مدفع مطر',
    'PVC PIPES': 'أنابيب PVC',
    'SPRINKLER PIPES': 'أنابيب رش',
    'BOREWELL PUMPS': 'مضخات آبار',
    '1HP WATER PUMP': 'مضخة مياه 1 حصان',
    'DRIP LATERAL PIPE': 'أنبوب تنقيط جانبي',
    'DRONE SPRAYER': 'رشاش درون',
    'MANUAL SPRAYER': 'رشاش يدوي',
    'BATTERY SPRAYER': 'رشاش بطارية',
    'POWER SPRAYER': 'رشاش آلي',
    'HIGH PRESSURE PUMP': 'مضخة ضغط عالي',
    'TRACTOR SPRAYER': 'رشاش جرار',
    'FOGGER SPRAYER': 'رشاش ضباب',
    'ELECTRIC FENCE': 'سياج كهربائي',
    'BRUSH CUTTER': 'قاطع أعشاب',
    'TROLLEY BRUSH CUTTER': 'قاطع أعشاب بعربة',
    'GRASS CUTTER': 'جزاز عشب',
    'HEDGE TRIMMER': 'مقص سياج',
    'EARTH AUGER': 'مثقاب أرضي',
    'POWER WEEDER': 'مزيل أعشاب آلي',
    'MINI TILLER': 'عزاقة صغيرة',
    'SEED DRILL': 'بذارة',
    'MANUAL SEEDER': 'بذارة يدوية',
    'CYCLE WEEDER': 'مزيل أعشاب بعجلة',
    'TARPAULIN': 'مشمع',
    'GREEN SHADE NET': 'شبكة ظل خضراء',
    'SOLAR FENCE ENERGIZER': 'مشغل سياج شمسي',
    'SOLAR INSECT TRAP': 'مصيدة حشرات شمسية',
    'SOLAR STREET LIGHT': 'مصباح شارع شمسي',
    'HARVESTER': 'حصادة',
    'MINI REAPER': 'حاصدة صغيرة',
    'REAPER BINDER': 'حاصدة رابطة',
    'CHAFF CUTTER': 'قاطعة علف',
  },
  ko: {
    'MODERN TRACTOR': '현대식 트랙터',
    'CULTIVATOR': '경운기',
    'POWER TILLER': '동력 경운기',
    'FIELD CULTIVATOR': '밭 경운기',
    'IRRIGATION SYSTEM': '관개 시스템',
    'DRIP PIPES': '점적 파이프',
    'PRECISION CUTTER': '정밀 절단기',
    'INDUSTRIAL WRENCH': '산업용 렌치',
    'POWER DRILLER': '전동 드릴',
    'HARVEST HOOK KNIFE': '수확 갈고리 칼',
    'SPRINKLER SYSTEM': '스프링클러 시스템',
    'RAIN GUN': '레인건',
    'PVC PIPES': 'PVC 파이프',
    'SPRINKLER PIPES': '스프링클러 파이프',
    'BOREWELL PUMPS': '관정 펌프',
    '1HP WATER PUMP': '1HP 물 펌프',
    'DRIP LATERAL PIPE': '점적 측관 파이프',
    'DRONE SPRAYER': '드론 분무기',
    'MANUAL SPRAYER': '수동 분무기',
    'BATTERY SPRAYER': '배터리 분무기',
    'POWER SPRAYER': '동력 분무기',
    'HIGH PRESSURE PUMP': '고압 펌프',
    'TRACTOR SPRAYER': '트랙터 분무기',
    'FOGGER SPRAYER': '포거 분무기',
    'ELECTRIC FENCE': '전기 울타리',
    'BRUSH CUTTER': '예초기',
    'TROLLEY BRUSH CUTTER': '트롤리 예초기',
    'GRASS CUTTER': '잔디 절단기',
    'HEDGE TRIMMER': '울타리 전정기',
    'EARTH AUGER': '땅굴착기',
    'POWER WEEDER': '동력 제초기',
    'MINI TILLER': '미니 경운기',
    'SEED DRILL': '파종기',
    'MANUAL SEEDER': '수동 파종기',
    'CYCLE WEEDER': '휠 제초기',
    'TARPAULIN': '방수포',
    'GREEN SHADE NET': '녹색 차광망',
    'SOLAR FENCE ENERGIZER': '태양광 울타리 전원기',
    'SOLAR INSECT TRAP': '태양광 해충 트랩',
    'SOLAR STREET LIGHT': '태양광 가로등',
    'HARVESTER': '수확기',
    'MINI REAPER': '미니 예취기',
    'REAPER BINDER': '예취 결속기',
    'CHAFF CUTTER': '사료 절단기',
  },
};

Object.entries(extraProductTerms).forEach(([locale, terms]) => {
  productTermTranslations[locale] = terms;
});

const productFieldTemplates = {
  te: {
    displayName: (name, category) => `${name} - ${category} కోసం నాణ్యమైన వ్యవసాయ పరికరం`,
    shortDesc: (name) => `${name} రైతుల రోజువారీ పనులకు ఉపయోగపడే బలమైన మరియు నమ్మదగిన ఉత్పత్తి.`,
    longDesc: (name, category) => `${name} ${category} పనులకు రూపొందించబడింది. ఇది వ్యవసాయ పనిలో సమయం ఆదా చేసి పనితీరును మెరుగుపరచడానికి సహాయపడుతుంది.`,
    aboutProduct: (name) => `${name} వ్యవసాయ అవసరాలకు ఉపయోగపడే ప్రొఫెషనల్ ఉత్పత్తి.`,
    productWork: (name) => `${name} పంట సంరక్షణ, నిర్వహణ లేదా ఫీల్డ్ పనులను సులభంగా చేయడంలో సహాయపడుతుంది.`,
  },
  hi: {
    displayName: (name, category) => `${name} - ${category} के लिए गुणवत्तापूर्ण कृषि उपकरण`,
    shortDesc: (name) => `${name} किसानों के दैनिक कामों के लिए मजबूत और भरोसेमंद उत्पाद है.`,
    longDesc: (name, category) => `${name} ${category} कार्यों के लिए बनाया गया है. यह खेती में समय बचाने और प्रदर्शन सुधारने में मदद करता है.`,
    aboutProduct: (name) => `${name} कृषि जरूरतों के लिए उपयोगी पेशेवर उत्पाद है.`,
    productWork: (name) => `${name} फसल देखभाल, रखरखाव या फील्ड कार्य को आसान बनाने में मदद करता है.`,
  },
  ta: {
    displayName: (name, category) => `${name} - ${category}க்கான தரமான வேளாண் கருவி`,
    shortDesc: (name) => `${name} விவசாயிகளின் தினசரி பணிகளுக்கு வலுவான மற்றும் நம்பகமான தயாரிப்பு.`,
    longDesc: (name, category) => `${name} ${category} பணிகளுக்காக வடிவமைக்கப்பட்டது. இது வேளாண் பணிகளில் நேரத்தை சேமித்து செயல்திறனை மேம்படுத்த உதவுகிறது.`,
    aboutProduct: (name) => `${name} வேளாண் தேவைகளுக்கான தொழில்முறை தயாரிப்பு.`,
    productWork: (name) => `${name} பயிர் பராமரிப்பு, பராமரிப்பு அல்லது வயல் பணிகளை எளிதாக்க உதவுகிறது.`,
  },
  ja: {
    displayName: (name, category) => `${name} - ${category}向けの高品質農業ツール`,
    shortDesc: (name) => `${name}は農家の日常作業に役立つ丈夫で信頼できる商品です。`,
    longDesc: (name, category) => `${name}は${category}作業向けに設計されています。農作業の時間短縮と作業効率の向上に役立ちます。`,
    aboutProduct: (name) => `${name}は農業用途に適したプロ向け商品です。`,
    productWork: (name) => `${name}は作物管理、保守、圃場作業を簡単にします。`,
  },
  zh: {
    displayName: (name, category) => `${name} - 适用于${category}的优质农业工具`,
    shortDesc: (name) => `${name}是适合农民日常作业的坚固可靠产品。`,
    longDesc: (name, category) => `${name}专为${category}作业设计，可帮助节省农活时间并提升效率。`,
    aboutProduct: (name) => `${name}是适用于农业需求的专业产品。`,
    productWork: (name) => `${name}可帮助简化作物护理、维护或田间作业。`,
  },
  kn: {
    displayName: (name, category) => `${name} - ${category}ಗಾಗಿ ಗುಣಮಟ್ಟದ ಕೃಷಿ ಸಾಧನ`,
    shortDesc: (name) => `${name} ರೈತರ ದೈನಂದಿನ ಕೆಲಸಗಳಿಗೆ ಬಲವಾದ ಮತ್ತು ನಂಬಿಕಸ್ಥ ಉತ್ಪನ್ನವಾಗಿದೆ.`,
    longDesc: (name, category) => `${name} ${category} ಕೆಲಸಗಳಿಗಾಗಿ ವಿನ್ಯಾಸಗೊಳಿಸಲಾಗಿದೆ. ಇದು ಕೃಷಿ ಕೆಲಸಗಳಲ್ಲಿ ಸಮಯ ಉಳಿಸಿ ಕಾರ್ಯಕ್ಷಮತೆಯನ್ನು ಸುಧಾರಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.`,
    aboutProduct: (name) => `${name} ಕೃಷಿ ಅಗತ್ಯಗಳಿಗೆ ಸೂಕ್ತವಾದ ವೃತ್ತಿಪರ ಉತ್ಪನ್ನವಾಗಿದೆ.`,
    productWork: (name) => `${name} ಬೆಳೆ ಆರೈಕೆ, ನಿರ್ವಹಣೆ ಅಥವಾ ಫೀಲ್ಡ್ ಕೆಲಸಗಳನ್ನು ಸುಲಭಗೊಳಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತದೆ.`,
  },
};

Object.assign(productFieldTemplates, {
  fr: {
    displayName: (name, category) => `${name} - outil agricole de qualité pour ${category}`,
    shortDesc: (name) => `${name} est un produit solide et fiable pour les travaux agricoles quotidiens.`,
    longDesc: (name, category) => `${name} est conçu pour les travaux de ${category}. Il aide à gagner du temps et à améliorer l’efficacité sur le terrain.`,
    aboutProduct: (name) => `${name} est un produit professionnel adapté aux besoins agricoles.`,
    productWork: (name) => `${name} simplifie l’entretien des cultures, la maintenance et les travaux de champ.`,
  },
  pt: {
    displayName: (name, category) => `${name} - ferramenta agrícola de qualidade para ${category}`,
    shortDesc: (name) => `${name} é um produto forte e confiável para trabalhos agrícolas diários.`,
    longDesc: (name, category) => `${name} foi desenvolvido para tarefas de ${category}. Ajuda a economizar tempo e melhorar a eficiência no campo.`,
    aboutProduct: (name) => `${name} é um produto profissional adequado para necessidades agrícolas.`,
    productWork: (name) => `${name} facilita o cuidado das culturas, a manutenção e as tarefas no campo.`,
  },
  ar: {
    displayName: (name, category) => `${name} - أداة زراعية عالية الجودة لفئة ${category}`,
    shortDesc: (name) => `${name} منتج قوي وموثوق للأعمال الزراعية اليومية.`,
    longDesc: (name, category) => `تم تصميم ${name} لأعمال ${category}. يساعد على توفير الوقت وتحسين الكفاءة في الحقل.`,
    aboutProduct: (name) => `${name} منتج احترافي مناسب للاحتياجات الزراعية.`,
    productWork: (name) => `${name} يساعد في تبسيط رعاية المحاصيل والصيانة وأعمال الحقل.`,
  },
  ko: {
    displayName: (name, category) => `${name} - ${category}용 고품질 농업 도구`,
    shortDesc: (name) => `${name}은 일상 농작업에 적합한 튼튼하고 신뢰할 수 있는 제품입니다.`,
    longDesc: (name, category) => `${name}은 ${category} 작업을 위해 설계되었습니다. 현장에서 시간을 절약하고 효율을 높이는 데 도움을 줍니다.`,
    aboutProduct: (name) => `${name}은 농업 요구에 적합한 전문 제품입니다.`,
    productWork: (name) => `${name}은 작물 관리, 유지보수 및 현장 작업을 쉽게 해줍니다.`,
  },
  es: {
    displayName: (name, category) => `${name} - herramienta agrícola de calidad para ${category}`,
    shortDesc: (name) => `${name} es un producto fuerte y confiable para el trabajo agrícola diario.`,
    longDesc: (name, category) => `${name} está diseñado para tareas de ${category}. Ayuda a ahorrar tiempo y mejorar la eficiencia en el campo.`,
    aboutProduct: (name) => `${name} es un producto profesional adecuado para las necesidades agrícolas.`,
    productWork: (name) => `${name} ayuda a simplificar el cuidado de los cultivos, el mantenimiento y el trabajo de campo.`,
  },
});

const translateProductFallback = (product, field, language, categoryName) => {
  if (language === 'en') return product[field] || '';
  const nameKey = String(product.name || '').trim().toUpperCase();
  const translatedName = productTermTranslations[language]?.[nameKey] || product.name;
  if (field === 'name') return translatedName;
  const template = productFieldTemplates[language]?.[field];
  return template ? template(translatedName, categoryName) : product[field] || translatedName;
};

const isCorruptTranslation = (value) =>
  typeof value === 'string' && (/\?{2,}/.test(value) || value.startsWith('?'));

const cleanTranslation = (value, fallback = '') =>
  value && !isCorruptTranslation(value) ? value : fallback;

const getByPath = (source, path) => {
  if (!source || !path) return undefined;
  return path.split('.').reduce((current, part) => current?.[part], source);
};

const getProductKeyForField = (field) => {
  const normalizedField = field === 'shortDescription' ? 'shortDesc' : field;
  return `${normalizedField}Key`;
};

const getLocalizedFieldValue = (source, field, language) => {
  if (!source || !field || language === 'en') return '';
  const raw = source.raw || {};
  const aliases = field === 'shortDesc'
    ? ['shortDesc', 'shortDescription']
    : field === 'longDesc'
      ? ['longDesc', 'description', 'productDetails']
      : field === 'description'
        ? ['description', 'productDetails', 'longDesc']
        : [field];

  for (const alias of aliases) {
    const candidates = [
      `${alias}_${language}`,
      `${alias}${language.toUpperCase()}`,
      `${alias}${language.charAt(0).toUpperCase()}${language.slice(1)}`,
      `${alias}_${language.toUpperCase()}`,
    ];

    for (const key of candidates) {
      const value = source[key] ?? raw[key];
      if (value && !isCorruptTranslation(value)) return value;
    }
  }

  const translationsBlock = source.translations || raw.translations || source.i18n || raw.i18n;
  for (const alias of aliases) {
    const value = translationsBlock?.[language]?.[alias] || translationsBlock?.[alias]?.[language];
    if (value && !isCorruptTranslation(value)) return value;
  }

  return '';
};

const getLocalizedListValue = (source, field, index, language) => {
  const value = getLocalizedFieldValue(source, field, language);
  if (Array.isArray(value)) return value[index] || '';
  if (typeof value === 'string') {
    return value
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)[index] || '';
  }
  return '';
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const savedLanguage = localStorage.getItem(STORAGE_KEY);
    return languages.some((item) => item.code === savedLanguage) ? savedLanguage : 'en';
  });

  const setLanguage = (code) => {
    const nextLanguage = languages.some((item) => item.code === code) ? code : 'en';
    localStorage.setItem(STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
  };

  useEffect(() => {
    const direction = RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', direction);
    document.body.setAttribute('dir', direction);

    return () => {
      document.body.removeAttribute('dir');
    };
  }, [language]);

  const value = useMemo(() => {
    const activeLanguage = languages.find((item) => item.code === language) || languages[0];

    const t = (key) =>
      cleanTranslation(
        getByPath(translations[language], key) ?? translations[language]?.[key],
        (getByPath(translations.en, key) ?? translations.en[key]) || key
      );

    const getLocalizedCategoryName = (categoryName) => {
      if (!categoryName) return '';
      let categoryId = CATEGORY_ID_BY_ENGLISH_NAME[categoryName];
      if (!categoryId) {
        // Fallback: generate slug from name
        categoryId = String(categoryName).toLowerCase()
          .replace(/ & /g, '-and-')
          .replace(/&/g, '-and-')
          .replace(/ /g, '-')
          .replace(/\//g, '-')
          .replace(/\\/g, '-')
          .trim('-');
      }
      return categoryTranslations[language]?.[categoryId]?.name || categoryName;
    };

    const productText = (product, field = 'name') => {
      if (!product) return '';
      const keyField = getProductKeyForField(field);
      const localeProductText = product[keyField] ? getByPath(translations[language], product[keyField]) : '';
      if (localeProductText && !isCorruptTranslation(localeProductText)) return localeProductText;
      const englishProductText = product[keyField] ? getByPath(translations.en, product[keyField]) : '';
      if (language === 'en' && englishProductText) return englishProductText;
      const localizedFieldValue = getLocalizedFieldValue(product, field, language);
      if (localizedFieldValue) return localizedFieldValue;
      const manualTranslation = productTranslations[language]?.[product.id]?.[field];
      if (manualTranslation && !isCorruptTranslation(manualTranslation)) return manualTranslation;
      const knownTranslation = translateKnownDynamicText(product[field], language);
      if (knownTranslation) return knownTranslation;
      if (['description', 'longDesc', 'shortDesc', 'shortDescription', 'aboutProduct', 'productWork'].includes(field)) {
        return product[field] || '';
      }
      const categoryName = getLocalizedCategoryName(product.category);
      return cleanTranslation(
        translateProductFallback(product, field, language, categoryName),
        product[field] || product.name || ''
      );
    };

    const categoryText = (category, field = 'name') => {
      if (!category) return '';
      const keyField = getProductKeyForField(field);
      const keyedTranslation = category[keyField] ? t(category[keyField]) : '';
      if (keyedTranslation && keyedTranslation !== category[keyField]) return keyedTranslation;
      return cleanTranslation(
        categoryTranslations[language]?.[category.id]?.[field] ||
        categoryTranslations[language]?.[category.slug]?.[field],
        category[field] || ''
      );
    };

    const subcategoryText = (subcategory) => {
      if (!subcategory) return '';
      const key = subcategory.nameKey || subcategory.subCategoryKey;
      const keyedTranslation = key ? t(key) : '';
      if (keyedTranslation && keyedTranslation !== key) return keyedTranslation;
      return subcategoryTranslations[language]?.[subcategory.name] || subcategory.name || '';
    };

    const dynamicText = (source, field, fallback = '') => {
      const value = source?.[field] ?? fallback ?? '';
      if (!value) return '';
      if (language === 'en') return String(value);

      const fieldRoot = field
        .replace(/Name$/, '')
        .replace(/Text$/, '')
        .replace(/Description$/, 'Description');
      const fieldPascal = field.charAt(0).toUpperCase() + field.slice(1);
      const fieldRootPascal = fieldRoot.charAt(0).toUpperCase() + fieldRoot.slice(1);
      const localeKeys = [
        `${field}_${language}`,
        `${field}${language.toUpperCase()}`,
        `${field}${language.charAt(0).toUpperCase()}${language.slice(1)}`,
        `${fieldPascal}_${language}`,
        `${fieldPascal}${language.toUpperCase()}`,
        `${fieldPascal}${language.charAt(0).toUpperCase()}${language.slice(1)}`,
        `${fieldRoot}_${language}`,
        `${fieldRoot}${language.toUpperCase()}`,
        `${fieldRoot}${language.charAt(0).toUpperCase()}${language.slice(1)}`,
        `${fieldRootPascal}_${language}`,
        `${fieldRootPascal}${language.toUpperCase()}`,
        `${fieldRootPascal}${language.charAt(0).toUpperCase()}${language.slice(1)}`,
      ];

      const raw = source?.raw || {};
      for (const key of localeKeys) {
        const localized = source?.[key] ?? raw?.[key];
        if (localized && !isCorruptTranslation(localized)) return String(localized);
      }

      const translationsBlock = source?.translations || raw?.translations || source?.i18n || raw?.i18n;
      const localizedFromBlock = translationsBlock?.[language]?.[field] || translationsBlock?.[field]?.[language];
      if (localizedFromBlock && !isCorruptTranslation(localizedFromBlock)) return String(localizedFromBlock);

      const mapped = getDynamicTextTranslation(value, language);
      return mapped || String(value);
    };

    const productGenericText = (templateKey, values = {}, fallback = '') => {
      const template = t(`productGeneric.${templateKey}`);
      if (!template || template === `productGeneric.${templateKey}`) return fallback;
      return interpolate(template, values);
    };

    const productListText = (product, field, text, index = 0) => {
      if (!product) return text || '';
      const exactKey = `${product.translationBaseKey || `products.${product.id}`}.${field}.${index}`;
      const exactTranslation = getByPath(translations[language], exactKey);
      if (exactTranslation && !isCorruptTranslation(exactTranslation)) return exactTranslation;
      if (language === 'en') return text || '';
      const localizedListValue = getLocalizedListValue(product, field, index, language);
      if (localizedListValue && !isCorruptTranslation(localizedListValue)) return localizedListValue;
      const knownTranslation = translateKnownDynamicText(text, language);
      if (knownTranslation) return knownTranslation;

      if (field === 'productDetails' && text === product.aboutProduct) return productText(product, 'aboutProduct');
      if (field === 'productDetails' && text === product.productWork) return productText(product, 'productWork');
      return text || '';
    };

    const productSpecLabel = (key) => {
      const translated = t(`specLabels.${key}`);
      return translated && translated !== `specLabels.${key}` ? translated : formatReadableKey(key);
    };

    const productSpecValue = (product, key, value) => {
      if (language === 'en') return value || '';
      const exactKey = `${product?.specificationsKey || `products.${product?.id}.specifications`}.${key}`;
      const exactTranslation = getByPath(translations[language], exactKey);
      if (exactTranslation && !isCorruptTranslation(exactTranslation)) return exactTranslation;
      const localizedSpec = getLocalizedFieldValue(product?.specifications || product, key, language);
      const knownTranslation = translateKnownDynamicText(value, language);
      return localizedSpec || knownTranslation || value || '';
    };

    const reviewText = (product, review, field = 'comment') => {
      if (!review) return '';
      if (field === 'userName') return review.userName || '';
      if (language === 'en') return review[field] || '';
      return productGenericText(
        'reviewComment',
        {
          productName: productText(product, 'name'),
          rating: review.rating || 5,
        },
        review[field] || ''
      );
    };

    return {
      language,
      activeLanguage,
      languages,
      setLanguage,
      t,
      productText,
      productListText,
      productSpecLabel,
      productSpecValue,
      reviewText,
      categoryText,
      subcategoryText,
      dynamicText,
    };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
